import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import config from '../../config';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useCity } from '../../contexts/CityContext';
import { toast } from 'react-toastify';
import DriverOutreachModal from '../../components/DriverOutreachModal';
import BookingOutreachModal from '../../components/BookingOutreachModal';
import OutreachCallStatus from '../../components/OutreachCallStatus';
import { outreachForVehicle, summarizeOutreach } from '../../utils/driverOutreachDisplay';
import LinkedCallLogs from '../../components/LinkedCallLogs';
import CustomerDetailsModal from '../customers/CustomerDetailsModal';
import s from './Bookings.module.scss';
import { buildGoogleMapsRouteUrl, getMapsRouteLabel } from '../../utils/googleMapsRoute';
import BookingBotCalls from './BookingBotCalls';
import BookingActivity from './BookingActivity';

const EMPTY_INVOICE_EDIT_ALL_FIELDS = {
  invoice_number: '',
  invoice_date: '',
  total_amount: '',
  discount_amount: '',
  sub_total: '',
  sgst: '',
  cgst: '',
  total_value: '',
  consigner_name: '',
  gstin: '',
  company_name: '',
  company_address: '',
  pickup_address: '',
  drop_off_address: '',
  job_site_address: '',
};

const invoiceEditAllFieldDefs = (isHourly) => ([
  { key: 'invoice_number', label: 'Invoice Number' },
  { key: 'invoice_date', label: 'Date' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'discount_amount', label: 'Discount Amount' },
  { key: 'sub_total', label: 'Sub Total' },
  { key: 'sgst', label: 'SGST' },
  { key: 'cgst', label: 'CGST' },
  { key: 'total_value', label: 'Total Value' },
  ...(isHourly
    ? [{ key: 'job_site_address', label: 'Job Site Address' }]
    : [
        { key: 'pickup_address', label: 'Pickup Address' },
        { key: 'drop_off_address', label: 'Drop Off Address' },
      ]),
  { key: 'consigner_name', label: 'Consigner Name' },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'company_address', label: 'Company Address' },
]);

const fieldsFromInvoiceData = (data) => {
  const next = { ...EMPTY_INVOICE_EDIT_ALL_FIELDS };
  if (!data) return next;
  Object.keys(next).forEach((key) => {
    next[key] = data[key] != null ? String(data[key]) : '';
  });
  return next;
};

// Equipment (hourly) vehicle class names
const EQUIPMENT_CLASS_MAP = {
  51: 'JCB - Backhoe Loader',
  52: 'Chota JCB',
  53: 'Mini Excavator',
  54: 'Excavator',
  55: 'Crane - 12 Ton',
  56: 'Crane - 14 Ton',
  57: 'Crane - 15 Ton',
};

// Standard vehicle class names
const VEHICLE_CLASS_MAP = {
  1: 'Piaggio Ape',
  2: 'Tata Ace',
  3: 'Ashok Leyland Dost',
  4: 'Force Tempo',
  5: 'Tata 407',
  6: 'Eicher Pro 1049',
  7: 'Eicher Pro 1059',
  8: 'Eicher Pro 2080',
  9: 'Partner',
  10: 'Tata LPT 1109',
  11: 'Furio 18',
  12: 'Eicher Pro 6032',
  13: 'Tata LPT 1818',
  14: 'Ashok Leyland 2525',
  15: 'Tata Signa 3523',
};

const isHourlyBooking = (booking) => {
  return booking.booking_type === 'hourly' || (booking.vehicle_class_id >= 51 && booking.vehicle_class_id <= 57);
};

const isPartBooking = (booking) => {
  const bookingType = String(booking?.booking_type || '').trim().toLowerCase();
  return bookingType === 'part' || bookingType === 'apsrt';
};

const isFlexBooking = (booking) => {
  return String(booking?.booking_type || '').trim().toLowerCase() === 'flex';
};

const BOOKING_KIND_OPTIONS = [
  { key: 'normal', label: 'Normal' },
  { key: 'flex', label: 'Flex' },
  { key: 'part', label: 'Part load' },
];
const ALL_BOOKING_KINDS = BOOKING_KIND_OPTIONS.map((option) => option.key);

const getBookingStopCounts = (booking) => {
  const pickupStops = Array.isArray(booking?.pickup_stops) ? booking.pickup_stops : [];
  const pickupAddressIds = Array.isArray(booking?.pickup_address_ids) ? booking.pickup_address_ids : [];

  const dropStops = Array.isArray(booking?.drop_stops) ? booking.drop_stops : [];
  const dropAddresses = Array.isArray(booking?.drop_addresses) ? booking.drop_addresses : [];
  const dropAddressIds = Array.isArray(booking?.drop_address_ids) ? booking.drop_address_ids : [];

  const pickupCount = pickupStops.length > 0
    ? pickupStops.length
    : pickupAddressIds.length > 0
      ? pickupAddressIds.length
      : booking?.pickup_address_id || booking?.pickup_address
        ? 1
        : 0;

  const dropCount = dropStops.length > 0
    ? dropStops.length
    : dropAddresses.length > 0
      ? dropAddresses.length
      : dropAddressIds.length > 0
        ? dropAddressIds.length
        : booking?.drop_address
          ? 1
          : 0;

  return { pickupCount, dropCount };
};

const shouldUsePartAssignment = (booking) => {
  // Only bookings explicitly marked as "part" use the part-assignment workflow.
  // A normal booking with multiple drops is handled by the standard workflow
  // (single pickup + first drop); the remaining drops are stored separately.
  return isPartBooking(booking);
};

const getBookingDropAddresses = (booking) => {
  const dropAddresses = Array.isArray(booking?.drop_addresses)
    ? booking.drop_addresses.filter(Boolean)
    : [];
  if (dropAddresses.length > 0) return dropAddresses;

  const dropStops = Array.isArray(booking?.drop_stops)
    ? booking.drop_stops
      .map((stop) => stop?.location_name)
      .filter(Boolean)
    : [];
  if (dropStops.length > 0) return dropStops;

  if (booking?.drop_address) return [booking.drop_address];
  return [];
};

const getBookingPickupAddresses = (booking) => {
  const pickupStops = Array.isArray(booking?.pickup_stops)
    ? booking.pickup_stops
      .map((stop) => stop?.location_name || stop?.address)
      .filter(Boolean)
    : [];
  if (pickupStops.length > 0) return pickupStops;

  const addr = String(booking?.pickup_address || '').trim();
  if (addr && addr !== 'N/A') return [addr];
  return [];
};

const getBookingRouteStops = (booking) => {
  const pickups = getBookingPickupAddresses(booking);
  if (isHourlyBooking(booking)) return pickups;
  return [...pickups, ...getBookingDropAddresses(booking)];
};

const getVehicleDisplayName = (booking) => {
  const classId = booking.vehicle_class_id;
  if (EQUIPMENT_CLASS_MAP[classId]) return EQUIPMENT_CLASS_MAP[classId];
  if (VEHICLE_CLASS_MAP[classId]) return VEHICLE_CLASS_MAP[classId];
  return `Class ${classId}`;
};

const getVehicleClassNumber = (vehicle) => {
  const classNum = Number(vehicle?.vehicle_class);
  if (Number.isInteger(classNum) && classNum > 0) return classNum;
  const typeMatch = String(vehicle?.vehicle_type || '').match(/class\s*(\d+)/i);
  if (typeMatch) return Number(typeMatch[1]);
  return null;
};

const formatNearbyVehicleClass = (vehicle) => {
  const classNum = getVehicleClassNumber(vehicle);
  if (classNum) return `Class ${classNum}`;
  return '—';
};

const formatVehicleBodyType = (vehicle) => {
  const raw = String(vehicle?.body_type || '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'open') return 'Open';
  if (raw === 'closed' || raw === 'close') return 'Closed';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const bodyTypeBadgeStyle = (label) => {
  const isOpen = label === 'Open';
  const isClosed = label === 'Closed';
  return {
    display: 'inline-block',
    marginLeft: 6,
    padding: '1px 7px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.2,
    color: isOpen ? '#065f46' : isClosed ? '#9a3412' : '#374151',
    background: isOpen ? '#d1fae5' : isClosed ? '#ffedd5' : '#f3f4f6',
    border: `1px solid ${isOpen ? '#6ee7b7' : isClosed ? '#fdba74' : '#e5e7eb'}`,
    verticalAlign: 'middle',
  };
};

const formatSuggestionVehicleMeta = (vehicle) => {
  const cls = formatNearbyVehicleClass(vehicle);
  const showClass = cls && cls !== '—' && cls !== '-';
  const body = formatVehicleBodyType(vehicle);
  if (!showClass && !body) return null;
  return (
    <>
      {showClass ? ` · ${cls}` : ''}
      {body ? <span style={bodyTypeBadgeStyle(body)}>{body}</span> : null}
    </>
  );
};

const getBookingVehicleClassNumber = (booking) => {
  const classId = Number(booking?.vehicle_class_id);
  if (!Number.isInteger(classId) || classId <= 0) return null;
  return classId;
};

const getAllowedVehicleClassesForBooking = (booking) => {
  const bookingClass = getBookingVehicleClassNumber(booking);
  if (!bookingClass) return null;
  return [bookingClass, bookingClass + 1];
};

const formatDurationHours = (durationMinutes) => {
  const total = Number(durationMinutes);
  if (!Number.isFinite(total) || total <= 0) return null;
  const minutes = Math.round(total);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

const BOOKING_STATE_OPTIONS = ['Pending', 'Assigned', 'Modified', 'Completed', 'Cancelled'];

const formatBookingStateLabel = (state) => {
  if (!state) return 'Pending';
  const lower = String(state).toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

/** Compact facts shown when a booking row is expanded (not full booking details). */
const BookingExpandPanel = ({ booking, onLogDriverCalls, onMaterialUpdated, onRemarksUpdated, onStateUpdated, onBookingTypeUpdated, onOpenCustomerPreview, notifStatus, onFetchNotifStatus, isDraft }) => {
  const hourly = isHourlyBooking(booking);
  const partLoad = isPartBooking(booking);
  const { pickupCount, dropCount } = getBookingStopCounts(booking);
  const showStops = pickupCount > 1 || dropCount > 1;
  const remarks = String(booking?.remarks || '').trim();

  const [isEditingMaterial, setIsEditingMaterial] = React.useState(false);
  const [materialDraft, setMaterialDraft] = React.useState(booking?.material_description || '');
  const [materialSaving, setMaterialSaving] = React.useState(false);
  const [isEditingRemarks, setIsEditingRemarks] = React.useState(false);
  const [remarksDraft, setRemarksDraft] = React.useState(booking?.remarks || '');
  const [remarksSaving, setRemarksSaving] = React.useState(false);
  const [convertOpen, setConvertOpen] = React.useState(false);
  const [convertBoxCount, setConvertBoxCount] = React.useState('1');
  const [convertSaving, setConvertSaving] = React.useState(false);
  const [stateDraft, setStateDraft] = React.useState(formatBookingStateLabel(booking?.state));
  const [stateSaving, setStateSaving] = React.useState(false);
  const [stateUnlocked, setStateUnlocked] = React.useState(false);
  const [statePrompt, setStatePrompt] = React.useState(false);
  const [statePassword, setStatePassword] = React.useState('');
  const [statePasswordInput, setStatePasswordInput] = React.useState('');
  const [stateUnlocking, setStateUnlocking] = React.useState(false);
  const [stateUnlockError, setStateUnlockError] = React.useState('');

  React.useEffect(() => {
    setStateDraft(formatBookingStateLabel(booking?.state));
    setStateUnlocked(false);
    setStatePrompt(false);
    setStatePassword('');
    setStatePasswordInput('');
    setStateUnlockError('');
  }, [booking?.id, booking?.state]);

  React.useEffect(() => {
    if (!isDraft && onFetchNotifStatus && !notifStatus) {
      onFetchNotifStatus(booking?.id ?? booking?.booking_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  const parseStateApiError = async (response) => {
    const errBody = await response.text();
    try {
      const parsed = JSON.parse(errBody);
      return parsed.detail || errBody || `API ${response.status}`;
    } catch {
      return errBody || `API ${response.status}`;
    }
  };

  const handleUnlockState = async (e) => {
    e?.stopPropagation?.();
    if (!statePasswordInput) return;

    setStateUnlocking(true);
    setStateUnlockError('');
    try {
      const response = await fetch(`${config.customerApiBase}/ops/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: statePasswordInput }),
      });
      if (!response.ok) {
        throw new Error(await parseStateApiError(response));
      }
      setStatePassword(statePasswordInput);
      setStatePasswordInput('');
      setStateUnlocked(true);
      setStatePrompt(false);
    } catch (err) {
      setStateUnlockError(err.message || 'Invalid password');
    } finally {
      setStateUnlocking(false);
    }
  };

  const handleLockState = (e) => {
    e?.stopPropagation?.();
    setStateUnlocked(false);
    setStatePrompt(false);
    setStatePassword('');
    setStatePasswordInput('');
    setStateUnlockError('');
    setStateDraft(formatBookingStateLabel(booking?.state));
  };

  const handleSaveState = async () => {
    const bookingId = booking?.id ?? booking?.booking_id;
    const currentState = formatBookingStateLabel(booking?.state);
    if (!stateUnlocked || !statePassword) {
      toast.error('Unlock state change first');
      return;
    }
    if (stateDraft === currentState) {
      toast.info('State unchanged');
      return;
    }
    if (!window.confirm(
      `Update booking #${bookingId} state from "${currentState}" to "${stateDraft}"?\n\nThis updates the database directly — no message will be sent to the customer.`
    )) {
      return;
    }

    setStateSaving(true);
    try {
      const response = await fetch(`${config.customerApiBase}/bookings/${bookingId}/state`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state: stateDraft, password: statePassword }),
      });
      if (!response.ok) {
        throw new Error(await parseStateApiError(response));
      }
      const data = await response.json();
      const updatedState = data.new_state || stateDraft;
      if (onStateUpdated) onStateUpdated(bookingId, updatedState);
      toast.success(`State updated to ${updatedState} (no customer notification)`);
    } catch (err) {
      console.error('Error updating booking state:', err);
      toast.error(err.message || 'Failed to update booking state');
    } finally {
      setStateSaving(false);
    }
  };

  const renderStateUpdate = () => {
    if (isDraft) return null;
    const currentState = formatBookingStateLabel(booking?.state);
    const stateChanged = stateDraft !== currentState;

    if (!stateUnlocked && statePrompt) {
      return (
        <div className={s.bookingExpandStateRow}>
          {customerPreviewButtons ? (
            <div className={s.bookingExpandCustomerActions}>{customerPreviewButtons}</div>
          ) : null}
          <div className={s.statePasswordPanel}>
            <div className={s.statePasswordTitle}>
              <i className="fa fa-lock" /> Enter password to change booking state
            </div>
            <input
              type="password"
              className={s.statePasswordInput}
              value={statePasswordInput}
              onChange={(e) => setStatePasswordInput(e.target.value)}
              placeholder="Password"
              disabled={stateUnlocking}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') handleUnlockState(e);
              }}
            />
            {stateUnlockError ? <div className={s.statePasswordError}>{stateUnlockError}</div> : null}
            <div className={s.statePasswordActions}>
              <button
                type="button"
                className={s.stateUnlockBtn}
                disabled={stateUnlocking || !statePasswordInput}
                onClick={handleUnlockState}
              >
                {stateUnlocking ? (<><i className="fa fa-spinner fa-spin" /> Checking...</>) : 'Unlock'}
              </button>
              <button
                type="button"
                className={s.stateLockBtn}
                disabled={stateUnlocking}
                onClick={(e) => {
                  e.stopPropagation();
                  setStatePrompt(false);
                  setStatePasswordInput('');
                  setStateUnlockError('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (!stateUnlocked) {
      return (
        <div className={s.bookingExpandStateRow}>
          {customerPreviewButtons ? (
            <div className={s.bookingExpandCustomerActions}>{customerPreviewButtons}</div>
          ) : null}
          <div className={s.bookingExpandStateRight}>
            <div className={s.bookingExpandFact}>
              <span className={s.bookingExpandFactLabel}>State (manual)</span>
              <button
                type="button"
                className={s.stateUnlockBtnOutline}
                onClick={(e) => {
                  e.stopPropagation();
                  setStatePrompt(true);
                  setStatePasswordInput('');
                  setStateUnlockError('');
                }}
              >
                <i className="fa fa-lock" /> Unlock to change state
              </button>
            </div>
            <span className={s.stateUpdateHint}>Password required</span>
          </div>
        </div>
      );
    }

    return (
      <div className={s.bookingExpandStateRow}>
        {customerPreviewButtons ? (
          <div className={s.bookingExpandCustomerActions}>{customerPreviewButtons}</div>
        ) : null}
        <div className={s.bookingExpandStateRight}>
          <div className={s.bookingExpandStateRightRow}>
            <div className={s.bookingExpandFact}>
              <span className={s.bookingExpandFactLabel}>State (manual)</span>
              <select
                className={s.stateSelect}
                value={stateDraft}
                disabled={stateSaving}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  e.stopPropagation();
                  setStateDraft(e.target.value);
                }}
              >
                {BOOKING_STATE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className={s.stateUpdateBtn}
              disabled={stateSaving || !stateChanged}
              onClick={(e) => {
                e.stopPropagation();
                handleSaveState();
              }}
            >
              {stateSaving ? 'Updating…' : 'Update state'}
            </button>
            <button
              type="button"
              className={s.stateLockBtn}
              disabled={stateSaving}
              onClick={handleLockState}
            >
              <i className="fa fa-unlock" /> Lock
            </button>
          </div>
          <span className={s.stateUpdateHint}>Direct update - no customer message</span>
        </div>
      </div>
    );
  };

  const handleSaveMaterial = async () => {
    setMaterialSaving(true);
    try {
      const bookingId = booking?.id ?? booking?.booking_id;
      const url = `${config.customerApiBase}/bookings/${bookingId}/material`;
      console.log('[Material] PATCH', url, { material_description: materialDraft.trim() || null });
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ material_description: materialDraft.trim() || null }),
      });
      if (!response.ok) {
        const errBody = await response.text();
        console.error('[Material] API error:', response.status, errBody);
        throw new Error(`API ${response.status}: ${errBody}`);
      }
      setIsEditingMaterial(false);
      if (onMaterialUpdated) onMaterialUpdated(bookingId, materialDraft.trim());
      toast.success('Material description updated');
    } catch (err) {
      console.error('Error updating material:', err);
      toast.error(err.message || 'Failed to update material description');
    } finally {
      setMaterialSaving(false);
    }
  };

  const handleCancelMaterialEdit = () => {
    setMaterialDraft(booking?.material_description || '');
    setIsEditingMaterial(false);
  };

  const handleSaveRemarks = async () => {
    setRemarksSaving(true);
    try {
      const bookingId = booking?.id ?? booking?.booking_id;
      const url = `${config.customerApiBase}/bookings/${bookingId}/remarks`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ remarks: remarksDraft.trim() || null }),
      });
      if (!response.ok) {
        const errBody = await response.text();
        throw new Error(`API ${response.status}: ${errBody}`);
      }
      setIsEditingRemarks(false);
      if (onRemarksUpdated) onRemarksUpdated(bookingId, remarksDraft.trim());
      toast.success('Remarks updated');
    } catch (err) {
      console.error('Error updating remarks:', err);
      toast.error(err.message || 'Failed to update remarks');
    } finally {
      setRemarksSaving(false);
    }
  };

  const stateLower = String(booking?.state || '').toLowerCase();
  const canConvertToPart = !isDraft && !hourly && !partLoad && stateLower !== 'cancelled' && stateLower !== 'completed';

  const handleConvertToPartLoad = async () => {
    const boxes = parseInt(convertBoxCount, 10);
    if (!Number.isFinite(boxes) || boxes < 1) {
      toast.error('Enter a valid box count (minimum 1)');
      return;
    }
    setConvertSaving(true);
    try {
      const bookingId = booking?.id ?? booking?.booking_id;
      const response = await fetch(`${config.customerApiBase}/bookings/${bookingId}/convert-to-part-load`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          box_count: boxes,
          changed_by: localStorage.getItem('user_name') || localStorage.getItem('username') || null,
        }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `API ${response.status}`);
      }
      const data = await response.json();
      setConvertOpen(false);
      if (onBookingTypeUpdated) onBookingTypeUpdated(bookingId, data.booking_type, data.remarks);
      toast.success('Booking converted to part load');
    } catch (err) {
      console.error('Error converting to part load:', err);
      toast.error(err.message || 'Failed to convert booking');
    } finally {
      setConvertSaving(false);
    }
  };

  const handleCancelRemarksEdit = () => {
    setRemarksDraft(booking?.remarks || '');
    setIsEditingRemarks(false);
  };

  const facts = [];
  const durationLabel = formatDurationHours(booking.duration);
  if (durationLabel) {
    facts.push({ label: 'Duration', value: durationLabel });
  }

  if (!hourly && booking.distance != null) {
    facts.push({
      label: 'Distance',
      value: Number(booking.distance) > 0 ? `${Number(booking.distance).toFixed(1)} km` : '—',
    });
  }

  facts.push({ label: 'Vehicle', value: getVehicleDisplayName(booking) });

  if (partLoad) {
    facts.push({ label: 'Type', value: 'Part load' });
  }

  if (Number(booking.toll_charge) > 0) {
    facts.push({ label: 'Toll', value: `₹${Number(booking.toll_charge).toFixed(2)}` });
  }

  if (isFlexBooking(booking) && Number(booking.discount_factor) > 0) {
    facts.push({ label: 'Flex Discount', value: `${Number(booking.discount_factor).toFixed(0)}%` });
  }

  if (booking.actual_bill != null && booking.actual_bill !== '') {
    facts.push({ label: 'Final bill', value: `₹${Number(booking.actual_bill).toFixed(2)}` });
  } else if (Number(booking.payment_estimate) > 0) {
    facts.push({ label: 'Estimate', value: `₹${Number(booking.payment_estimate).toFixed(2)}` });
  }

  if (showStops) {
    facts.push({ label: 'Stops', value: `${pickupCount} pickup · ${dropCount} drop` });
  }

  if (booking.other_party_phone_number) {
    const pays = booking.other_party_pays ? ' · other party pays' : '';
    facts.push({
      label: 'Other party',
      value: `+${booking.other_party_phone_number}${pays}`,
    });
  }

  const extraContacts = Array.isArray(booking.other_party_contacts) ? booking.other_party_contacts : [];
  if (extraContacts.length > 0) {
    const mappedDigits = String(booking.other_party_phone_number || '').replace(/\D/g, '');
    const mappedTail = mappedDigits.slice(-10);
    const extras = extraContacts.filter((contact) => {
      const digits = String(contact.phone || '').replace(/\D/g, '');
      if (!digits) return false;
      if (mappedTail && digits.slice(-10) === mappedTail) return false;
      return true;
    });
    const pickupExtras = extras.filter((contact) => contact.stop_type !== 'drop');
    const dropExtras = extras.filter((contact) => contact.stop_type === 'drop');
    const pushExtra = (contact, label) => {
      const digits = String(contact.phone || '').replace(/\D/g, '');
      const name = (contact.name || '').trim();
      facts.push({
        label,
        value: name ? `${name} · +${digits}` : `+${digits}`,
      });
    };
    pickupExtras.forEach((contact, index) => {
      pushExtra(contact, pickupExtras.length > 1 ? `Pickup contact ${index + 1}` : 'Pickup contact');
    });
    dropExtras.forEach((contact, index) => {
      pushExtra(contact, dropExtras.length > 1 ? `Drop contact ${index + 1}` : 'Drop contact');
    });
  }

  if (booking.payment_link_sent) {
    const status = booking.payment_link_status ? ` (${booking.payment_link_status})` : '';
    const isQr = String(booking.payment_instrument_type || '').toUpperCase() === 'QR_CODE';
    facts.push({
      label: isQr ? 'Payment QR' : 'Payment request',
      value: `Sent${status}`,
    });
  }

  const sourceRaw = String(booking?.source || 'whatsapp').trim().toLowerCase();
  const sourceLabels = { whatsapp: 'WhatsApp', ops: 'Ops', b2b: 'B2B' };
  facts.push({
    label: 'Source',
    value: sourceLabels[sourceRaw] || sourceRaw.toUpperCase(),
  });

  const sentDotClass = (sent) => {
    if (sent === true) return s.customerActionBtnSent;
    if (sent === false) return s.customerActionBtnUnsent;
    return ''; // unknown / still loading - no dot yet
  };

  const customerPreviewButtons = !isDraft && onOpenCustomerPreview ? (
    <>
      <button
        type="button"
        className={`${s.outreachLogBtnStandalone} ${sentDotClass(notifStatus?.confirmationSent ?? null)}`}
        title={notifStatus?.confirmationSent ? 'Already sent to customer' : 'Not sent yet'}
        onClick={(e) => {
          e.stopPropagation();
          onOpenCustomerPreview(booking, 'booking_summary');
        }}
      >
        <i className="fa fa-clipboard" aria-hidden="true" />
        Send Booking Confirmation
      </button>
      <button
        type="button"
        className={`${s.outreachLogBtnStandalone} ${sentDotClass(notifStatus?.driverDetailsSent ?? null)}`}
        title={notifStatus?.driverDetailsSent ? 'Already sent to customer' : 'Not sent yet'}
        onClick={(e) => {
          e.stopPropagation();
          onOpenCustomerPreview(booking, 'driver_details');
        }}
      >
        <i className="fa fa-id-card" aria-hidden="true" />
        Trip Details
      </button>
    </>
  ) : null;

  if (!remarks && facts.length === 0) {
    return (
      <div className={s.bookingExpandPanel}>
        {renderStateUpdate()}
        <span className={s.bookingExpandEmpty}>No remarks or extra details.</span>
        {onLogDriverCalls ? (
          <div className={s.bookingExpandActions}>
            {onLogDriverCalls ? (
              <button
                type="button"
                className={s.outreachLogBtnStandalone}
                onClick={(e) => {
                  e.stopPropagation();
                  onLogDriverCalls(booking);
                }}
              >
                <i className="fa fa-phone" aria-hidden="true" />
                Log driver calls
              </button>
            ) : null}
          </div>
        ) : null}
        {!isDraft ? (
          <BookingActivity
            bookingId={booking?.id ?? booking?.booking_id}
            refreshKey={[booking?.state, booking?.booking_type, booking?.remarks, booking?.material_description, booking?.on_hold, booking?.modified_at].join('|')}
          />
        ) : null}
        <LinkedCallLogs
          entityType={isDraft ? 'draft_booking' : 'booking'}
          entityId={booking?.id ?? booking?.booking_id}
        />
      </div>
    );
  }

  return (
    <div className={s.bookingExpandPanel}>
      {renderStateUpdate()}
      {remarks || isEditingRemarks ? (
        <div className={s.bookingExpandRemarks}>
          <div className={s.bookingExpandRemarksLabel} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>
              <i className="fa fa-commenting-o" aria-hidden="true" />
              Remarks
            </span>
            {!isEditingRemarks && (
              <button
                type="button"
                className={s.remarksActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setRemarksDraft(booking?.remarks || '');
                  setIsEditingRemarks(true);
                }}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}
              >
                <i className="fa fa-pencil" /> Edit
              </button>
            )}
          </div>
          {isEditingRemarks ? (
            <div style={{ marginTop: '0.5rem' }}>
              <textarea
                value={remarksDraft}
                onChange={(e) => setRemarksDraft(e.target.value)}
                placeholder="Add remarks for ops"
                rows={3}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem', resize: 'vertical' }}
                onClick={(e) => e.stopPropagation()}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSaveRemarks(); }}
                  disabled={remarksSaving}
                  style={{ padding: '0.375rem 0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  {remarksSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCancelRemarksEdit(); }}
                  disabled={remarksSaving}
                  style={{ padding: '0.375rem 0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className={s.bookingExpandRemarksText}>{remarks}</p>
          )}
        </div>
      ) : (
        <div className={s.bookingExpandRemarks}>
          <div className={s.bookingExpandRemarksLabel} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>
              <i className="fa fa-commenting-o" aria-hidden="true" />
              Remarks
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRemarksDraft('');
                setIsEditingRemarks(true);
              }}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}
            >
              <i className="fa fa-plus" /> Add
            </button>
          </div>
          <p className={s.bookingExpandRemarksText} style={{ color: '#9ca3af', fontStyle: 'italic' }}>No remarks</p>
        </div>
      )}
      {booking.material_description || isEditingMaterial ? (
        <div className={s.bookingExpandRemarks}>
          <div className={s.bookingExpandRemarksLabel} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              <i className="fa fa-cube" aria-hidden="true" />
              Materials
            </span>
            {!isEditingMaterial && (
              <button
                type="button"
                className={s.remarksActionBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setMaterialDraft(booking?.material_description || '');
                  setIsEditingMaterial(true);
                }}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}
              >
                <i className="fa fa-pencil" /> Edit
              </button>
            )}
          </div>
          {isEditingMaterial ? (
            <div style={{ marginTop: '0.5rem' }}>
              <input
                type="text"
                value={materialDraft}
                onChange={(e) => setMaterialDraft(e.target.value)}
                placeholder="e.g. Cement bags, Furniture items, Steel rods"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                onClick={(e) => e.stopPropagation()}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleSaveMaterial(); }}
                  disabled={materialSaving}
                  style={{ padding: '0.375rem 0.75rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  {materialSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleCancelMaterialEdit(); }}
                  disabled={materialSaving}
                  style={{ padding: '0.375rem 0.75rem', background: '#6b7280', color: 'white', border: 'none', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className={s.bookingExpandRemarksText}>{booking.material_description}</p>
          )}
        </div>
      ) : (
        <div className={s.bookingExpandRemarks}>
          <div className={s.bookingExpandRemarksLabel}>
            <span>
              <i className="fa fa-cube" aria-hidden="true" />
              Materials
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMaterialDraft('');
                setIsEditingMaterial(true);
              }}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem' }}
            >
              <i className="fa fa-plus" /> Add
            </button>
          </div>
          <p className={s.bookingExpandRemarksText} style={{ color: '#9ca3af', fontStyle: 'italic' }}>No materials specified</p>
        </div>
      )}
      {facts.length > 0 ? (
        <div className={`${s.bookingExpandFacts} ${remarks ? s.bookingExpandFactsWithRemarks : ''}`}>
          {facts.map((fact) => (
            <div key={fact.label} className={s.bookingExpandFact}>
              <span className={s.bookingExpandFactLabel}>{fact.label}</span>
              <span className={s.bookingExpandFactValue}>{fact.value}</span>
            </div>
          ))}
        </div>
      ) : null}
      {onLogDriverCalls ? (
        <div className={s.bookingExpandActions}>
          <button
            type="button"
            className={s.outreachLogBtnStandalone}
            onClick={(e) => {
              e.stopPropagation();
              onLogDriverCalls(booking);
            }}
          >
            <i className="fa fa-phone" aria-hidden="true" />
            Log driver calls
          </button>
        </div>
      ) : null}
      {canConvertToPart ? (
        <div className={s.bookingExpandActions}>
          {convertOpen ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ fontSize: '0.85rem' }}>
                Boxes{' '}
                <input
                  type="number"
                  min="1"
                  value={convertBoxCount}
                  onChange={(e) => setConvertBoxCount(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ width: 64 }}
                />
              </label>
              <button
                type="button"
                className={s.outreachLogBtnStandalone}
                disabled={convertSaving}
                onClick={(e) => { e.stopPropagation(); handleConvertToPartLoad(); }}
              >
                {convertSaving ? 'Converting...' : 'Confirm switch to Part Load'}
              </button>
              <button
                type="button"
                disabled={convertSaving}
                onClick={(e) => { e.stopPropagation(); setConvertOpen(false); }}
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={s.outreachLogBtnStandalone}
              onClick={(e) => { e.stopPropagation(); setConvertOpen(true); }}
            >
              <i className="fa fa-cubes" aria-hidden="true" />
              Convert to Part Load
            </button>
          )}
        </div>
      ) : null}
      {!isDraft ? (
        <BookingActivity
          bookingId={booking?.id ?? booking?.booking_id}
          refreshKey={[booking?.state, booking?.booking_type, booking?.remarks, booking?.material_description, booking?.on_hold, booking?.modified_at].join('|')}
        />
      ) : null}
      <LinkedCallLogs
        entityType={isDraft ? 'draft_booking' : 'booking'}
        entityId={booking?.id ?? booking?.booking_id}
      />
    </div>
  );
};

const CONFIG = {
  API_BASE_URL: config.customerApiBase,
  POLLING_INTERVAL: 60000, // 60s refresh keeps data current without hammering the API
  DEBOUNCE_DELAY: 400,
};

const getBookingsPageMeta = (filter) => {
  if (filter === 'draft') {
    return {
      title: 'Drafts',
      subtitle: 'View and manage draft bookings',
      iconClass: 'fa-file-text-o',
    };
  }

  return {
    title: 'Bookings',
    subtitle: 'View and manage all customer bookings',
    iconClass: 'fa-calendar-check-o',
  };
};

// Debounced value hook
const useDebounce = (value, delay = CONFIG.DEBOUNCE_DELAY) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

const normalizeIndianPhone = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const formatPhoneForDisplay = (rawPhone) => {
  const digits = String(rawPhone || '').replace(/\D/g, '');
  return digits || 'N/A';
};

// Numbers this customer used as "other party" on earlier bookings (latest booking per number).
const OtherPartySuggestions = ({ booking, selectedPhone, onPick }) => {
  const [items, setItems] = useState([]);
  const customerId = booking?.customer_id;
  const bookingId = booking?.id;

  useEffect(() => {
    if (!customerId) {
      setItems([]);
      return undefined;
    }
    let cancelled = false;
    const excludeQuery = bookingId ? `?exclude_booking_id=${bookingId}` : '';
    fetch(`${CONFIG.API_BASE_URL}/customers/${customerId}/other-party-recommendations${excludeQuery}`)
      .then((res) => (res.ok ? res.json() : { recommendations: [] }))
      .then((data) => {
        if (!cancelled) setItems(data.recommendations || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, bookingId]);

  if (!items.length) return null;
  const selectedDigits = normalizeIndianPhone(selectedPhone);

  return (
    <div style={{ marginBottom: '10px' }}>
      <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 6px' }}>Previously used:</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {items.map((item) => {
          const active = selectedDigits === item.phone;
          return (
            <button
              key={item.phone}
              type="button"
              onClick={() => onPick(item.phone)}
              title={item.use_count > 1 ? `Used on ${item.use_count} bookings` : 'Used on 1 booking'}
              style={{
                padding: '6px 10px',
                borderRadius: '999px',
                border: `1px solid ${active ? '#7c3aed' : '#d1d5db'}`,
                background: active ? '#ede9fe' : '#fff',
                fontSize: '12px',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              +{item.phone} · #{item.last_booking_id}
              {item.other_party_pays ? ' · paid' : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const toLocalDateTimeInput = (dt = new Date()) => {
  const d = new Date(dt);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const haversineDistanceKm = (lat1, lon1, lat2, lon2) => {
  const a1 = Number(lat1);
  const o1 = Number(lon1);
  const a2 = Number(lat2);
  const o2 = Number(lon2);
  if (![a1, o1, a2, o2].every(Number.isFinite)) return null;
  const R = 6371;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(a2 - a1);
  const dLon = toRad(o2 - o1);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a1)) * Math.cos(toRad(a2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

const getBookingPickupCoords = (booking) => {
  const lat = Number(booking?.pickup_lat ?? booking?.pickup_latitude);
  const lng = Number(booking?.pickup_lon ?? booking?.pickup_lng ?? booking?.pickup_longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};
const Bookings = ({ initialFilter = 'all', hideNewBooking = false, searchPlaceholder }) => {
  const history = useHistory();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(initialFilter || 'all');
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [actionBookingId, setActionBookingId] = useState(null);
  const [assigningBookingId, setAssigningBookingId] = useState(null);
  const [partAssignModal, setPartAssignModal] = useState(null);
  const [partVehicles, setPartVehicles] = useState([]);
  const [partVehiclesLoading, setPartVehiclesLoading] = useState(false);
  const [selectedPartVehicleId, setSelectedPartVehicleId] = useState('');
  const [vehicleAssignModal, setVehicleAssignModal] = useState(null);
  const [bookingCallsOpen, setBookingCallsOpen] = useState(false);
  const [vehicleIdInput, setVehicleIdInput] = useState('');
  const [driverNameInput, setDriverNameInput] = useState('');
  const [driverPayInput, setDriverPayInput] = useState('');
  const [labourPayInput, setLabourPayInput] = useState('');
  const [assignVehicleDrivers, setAssignVehicleDrivers] = useState([]);
  const [assignVehicleDriversLoading, setAssignVehicleDriversLoading] = useState(false);
  const [driverAssignSuggestions, setDriverAssignSuggestions] = useState([]);
  const [nearbyVehiclesModal, setNearbyVehiclesModal] = useState(null);
  const [nearbyRadiusKm, setNearbyRadiusKm] = useState(10);
  const [nearbyCustomRadiusKm, setNearbyCustomRadiusKm] = useState('');
  const [nearbyClassFilter, setNearbyClassFilter] = useState('class');
  const [nearbyVehiclesList, setNearbyVehiclesList] = useState([]);
  const [nearbyVehiclesSource, setNearbyVehiclesSource] = useState([]);
  const [nearbyVehiclesLoading, setNearbyVehiclesLoading] = useState(false);
  const [mlSuggestions, setMlSuggestions] = useState([]);
  const [mlCityWildcards, setMlCityWildcards] = useState([]);
  const [mlSuggestionsLoading, setMlSuggestionsLoading] = useState(false);
  /** On-demand "free drivers within 12 km of pickup, not on a trip" fallback list */
  const [freeNearbySuggestions, setFreeNearbySuggestions] = useState([]);
  const [freeNearbyLoading, setFreeNearbyLoading] = useState(false);
  const [showFreeNearby, setShowFreeNearby] = useState(false);
  /** Drop-location fallback: free ≤5 km from drop + loyalty near drop */
  const [freeNearDropSuggestions, setFreeNearDropSuggestions] = useState([]);
  const [dropLoyaltySuggestions, setDropLoyaltySuggestions] = useState([]);
  const [freeNearDropLoading, setFreeNearDropLoading] = useState(false);
  const [showFreeNearDrop, setShowFreeNearDrop] = useState(false);
  const [mlOutreachHistory, setMlOutreachHistory] = useState([]);
  const [driverOutreachModal, setDriverOutreachModal] = useState(null);
  const [bookingOutreachModal, setBookingOutreachModal] = useState(null);
  const [customerPreviewModal, setCustomerPreviewModal] = useState(null);
  // bookingId -> { confirmationSent: bool|null, driverDetailsSent: bool|null }
  // null = not fetched / unknown yet (renders as a neutral dot, never red by default)
  const [notifStatusByBooking, setNotifStatusByBooking] = useState({});
  const [outreachRefreshToken, setOutreachRefreshToken] = useState(0);
  const [callingDrivers, setCallingDrivers] = useState({});
  const [voiceCallingDrivers, setVoiceCallingDrivers] = useState({});
  const [voiceCallNotice, setVoiceCallNotice] = useState(null);
  const voiceCallNoticeRef = useRef(null);
  // Call Queue: select multiple drivers then bulk-enqueue AI calls
  const [callQueueMode, setCallQueueMode] = useState(false);
  const [callQueueSelected, setCallQueueSelected] = useState(new Set()); // keys: "vehicleId:phone"
  const [callQueueRunning, setCallQueueRunning] = useState(false);
  /** When vehicle owner ≠ driver, ops picks assignment path before POST manual-assignment */
  const [ownerAssignChoiceModal, setOwnerAssignChoiceModal] = useState(null);
  /** Confirmation before directly assigning a suggested driver (no separate owner) */
  const [confirmAssignModal, setConfirmAssignModal] = useState(null);
  /** Transporter assignment: assign a booking to an agency before vehicle/driver is known */
  const [transporterAssignModal, setTransporterAssignModal] = useState(null); // booking object
  const [transporterList, setTransporterList] = useState([]);
  const [transporterListLoading, setTransporterListLoading] = useState(false);
  const [transporterSearch, setTransporterSearch] = useState('');
  const [selectedTransporter, setSelectedTransporter] = useState(null);
  const [assigningTransporter, setAssigningTransporter] = useState(false);
  const [onHoldUpdatingId, setOnHoldUpdatingId] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);
  const [paymentAmountInput, setPaymentAmountInput] = useState('');
  const [collectNowInput, setCollectNowInput] = useState('');
  const [planSavingId, setPlanSavingId] = useState(null);
  const [planError, setPlanError] = useState('');
  const [amountUpdatingId, setAmountUpdatingId] = useState(null);
  const [amountUpdateError, setAmountUpdateError] = useState('');
  const [manualPaySavingId, setManualPaySavingId] = useState(null);
  const [manualPaymentMode, setManualPaymentMode] = useState('CASH');
  const [manualOtherProvider, setManualOtherProvider] = useState('');
  const [manualTxnTime, setManualTxnTime] = useState(toLocalDateTimeInput());
  const [manualGatewayTxnId, setManualGatewayTxnId] = useState('');
  const [manualPayNotes, setManualPayNotes] = useState('');
  const [manualPayError, setManualPayError] = useState('');
  const [paymentRecipient, setPaymentRecipient] = useState('customer');
  const [paymentOtherPartyPhone, setPaymentOtherPartyPhone] = useState('');
  const [paymentRecipientError, setPaymentRecipientError] = useState('');
  const [paymentRecipientNotice, setPaymentRecipientNotice] = useState('');
  const [paymentRecipientSavingId, setPaymentRecipientSavingId] = useState(null);
  const [otherPartyModal, setOtherPartyModal] = useState(null);
  const [otherPartyPhoneInput, setOtherPartyPhoneInput] = useState('');
  const [otherPartyPaysInput, setOtherPartyPaysInput] = useState(false);
  const [otherPartyUpdatingId, setOtherPartyUpdatingId] = useState(null);
  const [otherPartyUpdateError, setOtherPartyUpdateError] = useState('');
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [draftConfirmModal, setDraftConfirmModal] = useState(null); // booking object for draft confirmation popup
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoiceEditMode, setInvoiceEditMode] = useState(false);
  const [invoiceFinalizing, setInvoiceFinalizing] = useState(false);
  const [invoiceEditAllUnlocked, setInvoiceEditAllUnlocked] = useState(false);
  const [invoiceEditAllPrompt, setInvoiceEditAllPrompt] = useState(false);
  const [invoiceEditAllPassword, setInvoiceEditAllPassword] = useState('');
  const [invoiceEditAllPasswordInput, setInvoiceEditAllPasswordInput] = useState('');
  const [invoiceEditAllDownloading, setInvoiceEditAllDownloading] = useState(false);
  const [invoiceEditAllPreviewUpdating, setInvoiceEditAllPreviewUpdating] = useState(false);
  const [invoiceEditAllFields, setInvoiceEditAllFields] = useState({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });
  const invoiceEditAllPreviewTimer = useRef(null);
  const invoiceEditAllPreviewSeq = useRef(0);
  const invoiceEditAllSkipLive = useRef(true);
  const [invoiceEditFields, setInvoiceEditFields] = useState({ gstin: '', consigner_name: '', company_name: '', company_address: '' });
  const [modifiableBookings, setModifiableBookings] = useState({});
  const [showDeleted, setShowDeleted] = useState(false);
  const [customerDetailsModalOpen, setCustomerDetailsModalOpen] = useState(false);
  const [customerDetailsModalId, setCustomerDetailsModalId] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [draftActionLoading, setDraftActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('');
  const [bookingKinds, setBookingKinds] = useState(ALL_BOOKING_KINDS);
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const refreshIntervalSeconds = CONFIG.POLLING_INTERVAL / 1000;
  const [nextRefreshIn, setNextRefreshIn] = useState(refreshIntervalSeconds);
  const itemsPerPage = 20;
  const { dateRange } = useDateFilter();
  const { selectedCities } = useCity();
  const pageMeta = getBookingsPageMeta(filter);
  const activeCount = filter === 'draft' ? draftCount : totalBookings;

  const debouncedSearchTerm = useDebounce(searchTerm);
  const isRefreshing = loading;
  const searchPlaceholderText = searchPlaceholder || 'Search by Booking ID, Customer, or Address...';

  const handleMaterialUpdated = useCallback((bookingId, newMaterial) => {
    setBookings(prev => prev.map(b => 
      String(b.id) === String(bookingId) 
        ? { ...b, material_description: newMaterial || null }
        : b
    ));
  }, []);

  const handleRemarksUpdated = useCallback((bookingId, newRemarks) => {
    setBookings(prev => prev.map(b =>
      String(b.id) === String(bookingId)
        ? { ...b, remarks: newRemarks || null }
        : b
    ));
  }, []);

  const handleBookingTypeUpdated = useCallback((bookingId, newType, newRemarks) => {
    setBookings(prev => prev.map(b =>
      String(b.id) === String(bookingId)
        ? { ...b, booking_type: newType, remarks: newRemarks ?? b.remarks }
        : b
    ));
  }, []);

  const handleStateUpdated = useCallback((bookingId, newState) => {
    setBookings(prev => prev.map(b =>
      String(b.id) === String(bookingId)
        ? { ...b, state: newState }
        : b
    ));
  }, []);

  const handleCustomerClick = useCallback((booking) => {
    const customerId = booking?.customer_id ?? null;
    if (!customerId) return;
    setCustomerDetailsModalId(customerId);
    setCustomerDetailsModalOpen(true);
  }, []);

  useEffect(() => {
    if (initialFilter && initialFilter !== filter) {
      setFilter(initialFilter);
    }
  }, [initialFilter]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      
      // Drafts now live in a separate table/endpoint
      const isDraftFilter = filter === 'draft';
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('limit', itemsPerPage);
      params.append('offset', (currentPage - 1) * itemsPerPage);
      
      if (isDraftFilter) {
        if (showDeleted) {
          params.append('show_deleted', 'true');
        }
      } else if (filter === 'hourly') {
        params.append('booking_type', 'hourly');
      } else if (filter !== 'all') {
        params.append('state', filter);
      }

      if (filter !== 'hourly' && bookingKinds.length && bookingKinds.length < ALL_BOOKING_KINDS.length) {
        params.append('booking_type', bookingKinds.join(','));
      }
      
      // Add cities filter if any cities are selected
      if (selectedCities.length > 0) {
        params.append('cities', selectedCities.join(','));
      }

      // Add search filter
      if (debouncedSearchTerm) {
        params.append('search', debouncedSearchTerm);
      }

      if (sourceFilter) {
        params.append('source', sourceFilter);
      }

      // Add date filter
      if (dateRange?.startDate) {
        params.append('start_date', dateRange.startDate);
      }
      if (dateRange?.endDate) {
        params.append('end_date', dateRange.endDate);
      }
      
      const queryString = params.toString();
      const endpoint = isDraftFilter ? 'draft-bookings' : 'bookings';
      const url = `${CONFIG.API_BASE_URL}/${endpoint}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (isDraftFilter) {
        const draftsData = data.drafts || [];
        // Normalize draft shape to match bookings shape for the UI
        const normalizedDrafts = draftsData.map(d => ({
          ...d,
          state: showDeleted ? 'Deleted' : 'Draft',
          is_modifiable: !showDeleted,
        }));
        setBookings(normalizedDrafts);
        const total = data.total || 0;
        setTotalPages(Math.ceil(total / itemsPerPage));
        setTotalBookings(total);
        
        const modifiableStatus = {};
        normalizedDrafts.forEach(booking => {
          modifiableStatus[booking.id] = booking.is_modifiable !== false;
        });
        setModifiableBookings(modifiableStatus);
      } else {
        const bookingsData = data.bookings || [];
        setBookings(bookingsData);
        const total = data.total || 0;
        setTotalPages(Math.ceil(total / itemsPerPage));
        setTotalBookings(total);
        
        // Extract is_modifiable from each booking
        const modifiableStatus = {};
        bookingsData.forEach(booking => {
          modifiableStatus[booking.id] = booking.is_modifiable !== false; // Default to true if not specified
        });
        setModifiableBookings(modifiableStatus);
      }
      
      setNextRefreshIn(refreshIntervalSeconds);
      setError(null);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, sourceFilter, bookingKinds, selectedCities, currentPage, debouncedSearchTerm, dateRange, showDeleted]);

  const fetchDraftCount = useCallback(async () => {
    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/draft-bookings/count`);
      if (response.ok) {
        const data = await response.json();
        setDraftCount(data.count || 0);
      }
    } catch (err) {
      // Silently ignore count errors
    }
  }, []);

  // Fetch draft count on mount and periodically
  useEffect(() => {
    fetchDraftCount();
    const interval = setInterval(fetchDraftCount, CONFIG.POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchDraftCount]);

  const handleModifyBooking = (booking) => {
    const normalizedBooking = {
      ...booking,
      id: booking?.id ?? booking?.booking_id ?? null,
    };

    history.push({
      pathname: '/app/customers/modify-booking',
      state: { booking: normalizedBooking, isModifying: true }
    });
  };

  const handleCancelBooking = async () => {
    if (!cancelModal) return;
    try {
      setCancellingId(cancelModal.id);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${cancelModal.id}/cancel`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: cancelReason.trim() || null }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || `HTTP ${response.status}`);
      }
      setCancelModal(null);
      setCancelReason('');
      fetchBookings();
    } catch (err) {
      console.error('Cancel booking error:', err);
      alert(`Failed to cancel booking: ${err.message}`);
    } finally {
      setCancellingId(null);
    }
  };

  const handleConfirmDraft = (booking) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) return;
    const pickupTime = booking?.pickup_time ? new Date(booking.pickup_time) : null;
    if (pickupTime && !isNaN(pickupTime.getTime()) && pickupTime.getTime() <= Date.now()) {
      toast.error('Cannot confirm draft: pickup time has already passed. Please edit the draft and set a future pickup time.');
      return;
    }
    setDraftConfirmModal(booking);
  };

  const executeConfirmDraft = async () => {
    if (!draftConfirmModal) return;
    const bookingId = draftConfirmModal?.id ?? draftConfirmModal?.booking_id;
    if (!bookingId) return;
    try {
      setDraftActionLoading(bookingId);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${bookingId}/confirm-draft`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      toast.success(`Draft #${bookingId} confirmed and is now Pending`);
      setDraftConfirmModal(null);
      fetchBookings();
    } catch (err) {
      console.error('Confirm draft error:', err);
      toast.error(err.message || 'Failed to confirm draft');
    } finally {
      setDraftActionLoading(null);
    }
  };

  const handleDeleteDraft = async (booking) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) return;
    if (!window.confirm(`Delete draft #${bookingId}? It will be moved to Deleted drafts.`)) return;
    try {
      setDraftActionLoading(bookingId);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${bookingId}/delete-draft`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      toast.success(`Draft #${bookingId} deleted`);
      fetchBookings();
    } catch (err) {
      console.error('Delete draft error:', err);
      toast.error(err.message || 'Failed to delete draft');
    } finally {
      setDraftActionLoading(null);
    }
  };

  const handleRestoreDraft = async (booking) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) return;
    try {
      setDraftActionLoading(bookingId);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${bookingId}/restore-draft`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }
      toast.success(`Draft #${bookingId} restored`);
      fetchBookings();
    } catch (err) {
      console.error('Restore draft error:', err);
      toast.error(err.message || 'Failed to restore draft');
    } finally {
      setDraftActionLoading(null);
    }
  };

  const handleEditDraft = (booking) => {
    const normalizedBooking = {
      ...booking,
      id: booking?.id ?? booking?.booking_id ?? null,
    };
    history.push({
      pathname: '/app/customers/modify-booking',
      state: { booking: normalizedBooking, isModifying: true, isDraft: true }
    });
  };

  const fetchVehicleForOwnerCheck = async (vehicleId) => {
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    const response = await fetch(`${config.opsApiBase}/vehicles/${vehicleId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Vehicle #${vehicleId} not found`);
      }
      throw new Error(data.detail || data.message || `HTTP ${response.status}`);
    }
    return data;
  };

  const assignVehicleToBooking = async (booking, vehicleId, assignOptions = {}) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) {
      toast.error('Booking ID not found');
      return false;
    }

    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      toast.error('Please enter a valid numeric Vehicle ID');
      return false;
    }

    try {
      setAssigningBookingId(bookingId);

      // If booking is on-hold, remove hold before manual assignment.
      if (Boolean(booking?.on_hold)) {
        const holdResponse = await fetch(
          `${CONFIG.API_BASE_URL}/bookings/${bookingId}/on-hold`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ on_hold: false }),
          }
        );

        const holdData = await holdResponse.json().catch(() => ({}));
        if (!holdResponse.ok || holdData.success === false) {
          throw new Error(holdData.detail || holdData.error || `HTTP ${holdResponse.status}`);
        }
      }

      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

      const operatorName =
        localStorage.getItem('user_name') ||
        localStorage.getItem('username') ||
        'Unknown Operator';

      const isHourly = isHourlyBooking(booking);
      const isPart = shouldUsePartAssignment(booking);
      const endpoint = isHourly
        ? `${config.opsApiBase}/hourly-unassigned-bookings/assign`
        : isPart
          ? `${config.opsApiBase}/part-bookings/assign`
          : `${config.dispatchApiBase}/manual-assignment`;

      const parsedDriverPay = (assignOptions.driverPay != null && assignOptions.driverPay !== '')
        ? Number(assignOptions.driverPay)
        : (driverPayInput !== '' && !isNaN(Number(driverPayInput)) ? Number(driverPayInput) : null);
      const parsedLabourPay = (assignOptions.labourPay != null && assignOptions.labourPay !== '')
        ? Number(assignOptions.labourPay)
        : (labourPayInput !== '' && !isNaN(Number(labourPayInput)) ? Number(labourPayInput) : null);

      const payload = isHourly
        ? {
            booking_id: bookingId,
            vehicle_id: vehicleId,
            assigned_by: operatorName,
            source: 'bookings_page',
            ...(parsedDriverPay != null ? { driver_pay: parsedDriverPay } : {}),
            ...(parsedLabourPay != null ? { labour_pay: parsedLabourPay } : {}),
          }
        : isPart
          ? {
              vehicle_id: vehicleId,
              booking_ids: [bookingId],
              use_real_distances: false,
              ...(parsedDriverPay != null ? { driver_pay: parsedDriverPay } : {}),
              ...(parsedLabourPay != null ? { labour_pay: parsedLabourPay } : {}),
            }
        : {
            booking_id: bookingId,
            vehicle_id: vehicleId,
            assigned_by: operatorName,
            source: 'bookings_page',
            confirmed: true,
            warnings_acknowledged: true,
            ...(parsedDriverPay != null ? { driver_pay: parsedDriverPay } : {}),
            ...(parsedLabourPay != null ? { labour_pay: parsedLabourPay } : {}),
            ...(assignOptions.throughOwner
              ? {
                  through_owner: true,
                  owner_phone: assignOptions.ownerPhone,
                }
              : {}),
            ...(assignOptions.mlMeta
              ? {
                  was_from_suggestion: Boolean(assignOptions.mlMeta.was_from_suggestion),
                  suggestion_rank: assignOptions.mlMeta.suggestion_rank ?? null,
                  ops_override: Boolean(assignOptions.mlMeta.ops_override),
                }
              : {}),
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${response.status}`);
      }

      toast.success(`Vehicle ${vehicleId} assigned to booking #${bookingId}`);
      await fetchBookings();
      return true;
    } catch (err) {
      console.error('Assign vehicle error:', err);
      toast.error(err.message || 'Failed to assign vehicle');
      return false;
    } finally {
      setAssigningBookingId(null);
    }
  };

  const loadAssignVehicleDrivers = async () => {
    setAssignVehicleDriversLoading(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicle-drivers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
        throw new Error(data?.detail || data?.message || `HTTP ${response.status}`);
      }
      const vehicles = data.data
        .map((vehicle) => ({
          vehicle_id: Number(vehicle.vehicle_id),
          driver_name: (vehicle.driver_name || '').trim(),
          registration_no: vehicle.registration_no || '',
          driver_phone: vehicle.driver_phone || '',
        }))
        .filter((vehicle) => Number.isInteger(vehicle.vehicle_id) && vehicle.vehicle_id > 0);
      setAssignVehicleDrivers(vehicles);
      if (driverNameInput.trim()) {
        const q = driverNameInput.trim().toLowerCase();
        const matches = vehicles
          .filter((v) => {
            const name = (v.driver_name || '').trim().toLowerCase();
            const vehicleLabel = `v-${v.vehicle_id}`.toLowerCase();
            return name.includes(q) || vehicleLabel.includes(q) || String(v.vehicle_id).includes(q);
          })
          .slice(0, 10);
        setDriverAssignSuggestions(matches);
      }
      return vehicles;
    } catch (err) {
      console.error('Load vehicle drivers error:', err);
      toast.error(err.message || 'Failed to load drivers');
      setAssignVehicleDrivers([]);
      return [];
    } finally {
      setAssignVehicleDriversLoading(false);
    }
  };

  const resolveVehicleIdForAssign = async () => {
    const vehicleIdParsed = Number.parseInt((vehicleIdInput || '').trim(), 10);
    if (Number.isInteger(vehicleIdParsed) && vehicleIdParsed > 0) {
      return vehicleIdParsed;
    }

    const nameQuery = (driverNameInput || '').trim().toLowerCase();
    if (!nameQuery) {
      return null;
    }

    let drivers = assignVehicleDrivers;
    if (drivers.length === 0) {
      drivers = await loadAssignVehicleDrivers();
    }

    const exactMatches = drivers.filter(
      (v) => (v.driver_name || '').trim().toLowerCase() === nameQuery
    );
    if (exactMatches.length === 1) {
      return exactMatches[0].vehicle_id;
    }
    if (exactMatches.length > 1) {
      throw new Error(`Multiple vehicles match driver "${driverNameInput}". Enter Vehicle ID instead.`);
    }

    const partialMatches = drivers.filter((v) =>
      (v.driver_name || '').trim().toLowerCase().includes(nameQuery)
    );
    if (partialMatches.length === 1) {
      return partialMatches[0].vehicle_id;
    }
    if (partialMatches.length > 1) {
      throw new Error(`Multiple drivers match "${driverNameInput}". Enter Vehicle ID instead.`);
    }

    throw new Error(`No driver found matching "${driverNameInput}"`);
  };

  const filterDriverAssignSuggestions = (query, drivers = assignVehicleDrivers) => {
    const q = (query || '').trim().toLowerCase();
    if (!q || q.length < 1) {
      setDriverAssignSuggestions([]);
      return;
    }
    const matches = drivers
      .filter((v) => {
        const name = (v.driver_name || '').trim().toLowerCase();
        const vehicleLabel = `v-${v.vehicle_id}`.toLowerCase();
        return name.includes(q) || vehicleLabel.includes(q) || String(v.vehicle_id).includes(q);
      })
      .slice(0, 10);
    setDriverAssignSuggestions(matches);
  };

  const handleDriverNameInputChange = (value) => {
    setDriverNameInput(value);
    filterDriverAssignSuggestions(value);
  };

  const selectDriverAssignSuggestion = (vehicle) => {
    setDriverNameInput(vehicle.driver_name || '');
    setVehicleIdInput(String(vehicle.vehicle_id));
    setDriverAssignSuggestions([]);
  };

  const closeNearbyVehiclesModal = () => {
    setNearbyVehiclesModal(null);
    setNearbyVehiclesList([]);
    setNearbyVehiclesSource([]);
    setNearbyRadiusKm(10);
    setNearbyCustomRadiusKm('');
    setNearbyClassFilter('class');
  };

  const applyNearbyRadiusFilter = (booking, source, radiusKm, classFilterMode = nearbyClassFilter) => {
    const pickup = getBookingPickupCoords(booking);
    if (!pickup || !Array.isArray(source)) {
      setNearbyVehiclesList([]);
      return;
    }
    const radius = Number(radiusKm);
    if (!Number.isFinite(radius) || radius <= 0) {
      setNearbyVehiclesList([]);
      return;
    }

    const allowedClasses =
      classFilterMode === 'class' ? getAllowedVehicleClassesForBooking(booking) : null;
    const allowedClassSet = allowedClasses ? new Set(allowedClasses) : null;

    const matches = source
      .map((vehicle) => {
        const distanceKm = haversineDistanceKm(
          pickup.lat,
          pickup.lng,
          vehicle.base_lat,
          vehicle.base_lng
        );
        if (distanceKm == null) return null;
        return { ...vehicle, distance_km: distanceKm };
      })
      .filter((vehicle) => {
        if (!vehicle || vehicle.distance_km > radius) return false;
        if (!allowedClassSet) return true;
        const vehicleClass = getVehicleClassNumber(vehicle);
        return vehicleClass != null && allowedClassSet.has(vehicleClass);
      })
      .sort((a, b) => a.distance_km - b.distance_km);

    setNearbyVehiclesList(matches);
  };

  const handleNearbyClassFilterChange = (mode) => {
    setNearbyClassFilter(mode);
    if (nearbyVehiclesModal) {
      applyNearbyRadiusFilter(nearbyVehiclesModal, nearbyVehiclesSource, nearbyRadiusKm, mode);
    }
  };

  const loadVehiclesForNearbyCheck = async () => {
    const [vehiclesResponse, driversResponse] = await Promise.all([
      fetch(`${config.dispatchApiBase}/vehicles`),
      fetch(`${config.opsApiBase}/vehicle-drivers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || 'mock-jwt-token-for-testing'}`,
        },
      }),
    ]);

    const vehiclesData = await vehiclesResponse.json().catch(() => ({}));
    if (!vehiclesResponse.ok || !vehiclesData?.success || !Array.isArray(vehiclesData?.data)) {
      throw new Error(vehiclesData?.detail || vehiclesData?.message || `HTTP ${vehiclesResponse.status}`);
    }

    const driversData = await driversResponse.json().catch(() => ({}));
    const driverMap = {};
    if (driversResponse.ok && driversData?.success && Array.isArray(driversData?.data)) {
      driversData.data.forEach((item) => {
        driverMap[item.vehicle_id] = item;
      });
    }

    return vehiclesData.data
      .map((vehicle) => {
        const driverInfo = driverMap[vehicle.vehicle_id] || {};
        const baseLat = Number(vehicle.base_lat);
        const baseLng = Number(vehicle.base_lng);
        return {
          vehicle_id: Number(vehicle.vehicle_id),
          driver_name: driverInfo.driver_name || '',
          driver_phone: driverInfo.driver_phone || '',
          registration_no: driverInfo.registration_no || '',
          base_location: vehicle.base_location || '',
          base_lat: baseLat,
          base_lng: baseLng,
          vehicle_type: vehicle.vehicle_type || '',
          vehicle_class: vehicle.vehicle_class,
          is_active: driverInfo.is_active !== false,
        };
      })
      .filter(
        (vehicle) =>
          Number.isInteger(vehicle.vehicle_id) &&
          vehicle.vehicle_id > 0 &&
          Number.isFinite(vehicle.base_lat) &&
          Number.isFinite(vehicle.base_lng)
      );
  };

  const handleNearbyRadiusChange = (radiusKm) => {
    const radius = Number(radiusKm);
    if (!Number.isFinite(radius) || radius <= 0) return;
    setNearbyRadiusKm(radius);
    setNearbyCustomRadiusKm(String(radius));
    if (nearbyVehiclesModal) {
      applyNearbyRadiusFilter(nearbyVehiclesModal, nearbyVehiclesSource, radius, nearbyClassFilter);
    }
  };

  const resolveBookingPickupCoords = async (booking) => {
    const direct = getBookingPickupCoords(booking);
    if (direct) return direct;

    const addressId = booking?.pickup_address_id;
    if (!addressId) return null;

    try {
      const response = await fetch(`${CONFIG.API_BASE_URL}/addresses/${addressId}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return null;

      const lat = Number(data.lat ?? data.latitude);
      const lng = Number(data.lng ?? data.longitude ?? data.lon);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    } catch (err) {
      console.error('Resolve pickup address error:', err);
    }
    return null;
  };

  const handleOpenNearbyVehicles = async (e) => {
    e?.stopPropagation?.();
    if (!vehicleAssignModal) return;

    setNearbyVehiclesModal(vehicleAssignModal);
    setNearbyRadiusKm(10);
    setNearbyCustomRadiusKm('10');
    setNearbyClassFilter('class');
    setNearbyVehiclesLoading(true);
    setNearbyVehiclesList([]);
    setNearbyVehiclesSource([]);

    try {
      const pickup = await resolveBookingPickupCoords(vehicleAssignModal);
      if (!pickup) {
        toast.error('Pickup coordinates not found for this booking');
        closeNearbyVehiclesModal();
        return;
      }

      const bookingWithPickup = {
        ...vehicleAssignModal,
        pickup_lat: pickup.lat,
        pickup_lon: pickup.lng,
      };
      setNearbyVehiclesModal(bookingWithPickup);

      const source = await loadVehiclesForNearbyCheck();
      setNearbyVehiclesSource(source);
      applyNearbyRadiusFilter(bookingWithPickup, source, 10);
    } catch (err) {
      console.error('Nearby vehicles error:', err);
      toast.error(err.message || 'Failed to load vehicles');
      closeNearbyVehiclesModal();
    } finally {
      setNearbyVehiclesLoading(false);
    }
  };

  const selectNearbyVehicle = (vehicle) => {
    setVehicleIdInput(String(vehicle.vehicle_id));
    setDriverNameInput(vehicle.driver_name || '');
    setDriverAssignSuggestions([]);
    closeNearbyVehiclesModal();
    toast.success(`Selected V-${vehicle.vehicle_id} (${vehicle.distance_km.toFixed(1)} km away)`);
  };

  const closeVehicleAssignModal = () => {
    closeNearbyVehiclesModal();
    setVehicleAssignModal(null);
    setBookingCallsOpen(false);
    setVoiceCallNotice(null);
    setVehicleIdInput('');
    setDriverNameInput('');
    setDriverPayInput('');
    setLabourPayInput('');
    setDriverAssignSuggestions([]);
    setMlSuggestions([]);
    setMlCityWildcards([]);
    setMlOutreachHistory([]);
    setMlSuggestionsLoading(false);
    setFreeNearbySuggestions([]);
    setFreeNearbyLoading(false);
    setShowFreeNearby(false);
    setFreeNearDropSuggestions([]);
    setDropLoyaltySuggestions([]);
    setFreeNearDropLoading(false);
    setShowFreeNearDrop(false);
    // reset transporter assign state too
    setTransporterAssignModal(null);
    setSelectedTransporter(null);
    setTransporterSearch('');
  };

  /** Load transporter list from ops API */
  const loadTransporters = async () => {
    if (transporterList.length > 0) return; // already loaded
    setTransporterListLoading(true);
    try {
      const res = await fetch(`${config.opsApiBase}/transporters`);
      const data = await res.json().catch(() => ({}));
      if (data.success && Array.isArray(data.transporters)) {
        setTransporterList(data.transporters);
      } else {
        toast.error('Could not load transporter list');
      }
    } catch (err) {
      toast.error('Failed to fetch transporters');
    } finally {
      setTransporterListLoading(false);
    }
  };

  /** Open the transporter assign modal for a booking */
  const openTransporterAssignModal = (booking) => {
    setSelectedTransporter(null);
    setTransporterSearch('');
    setTransporterAssignModal(booking);
    loadTransporters();
  };

  /** POST to assign-transporter endpoint */
  const handleAssignTransporter = async () => {
    if (!transporterAssignModal || !selectedTransporter) return;
    const bookingId = transporterAssignModal?.id ?? transporterAssignModal?.booking_id;
    const operatorName =
      localStorage.getItem('user_name') ||
      localStorage.getItem('username') ||
      'Unknown Operator';
    setAssigningTransporter(true);
    try {
      const res = await fetch(`${config.dispatchApiBase}/assign-transporter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId,
          transporter_id: selectedTransporter.id,
          assigned_by: operatorName,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      }
      toast.success(
        `Booking #${bookingId} assigned to ${selectedTransporter.agency_name || 'Transporter'} (awaiting vehicle)`,
      );
      setTransporterAssignModal(null);
      setSelectedTransporter(null);
      setTransporterSearch('');
      await fetchBookings();
    } catch (err) {
      toast.error(err.message || 'Failed to assign transporter');
    } finally {
      setAssigningTransporter(false);
    }
  };

  const isDraftBooking = (booking) => String(booking?.state || '').toLowerCase() === 'draft';

  const canLogDriverCalls = (booking) => {
    const state = String(booking?.state || 'pending').toLowerCase();
    return !['draft', 'deleted', 'cancelled'].includes(state);
  };

  const handleOpenBookingOutreach = (booking) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) return;
    setBookingOutreachModal({ bookingId });
  };

  // "Send Booking Confirmation" / "Trip Details" — ops-triggered preview +
  // confirm flow, same endpoints as the Trip Details page's "Booking
  // Summary" / "Driver Details" buttons.
  const CUSTOMER_PREVIEW_CONFIG = {
    booking_summary: {
      previewPath: 'preview-booking-summary',
      sendPath: 'send-booking-summary',
      title: 'Send Booking Confirmation',
      successMessage: 'Booking confirmation sent to customer',
    },
    driver_details: {
      previewPath: 'preview-driver-details',
      sendPath: 'send-driver-details',
      title: 'Send Trip Details',
      successMessage: 'Trip details sent to customer',
    },
  };

  const renderWhatsAppText = (text) => {
    if (!text) return 'No message content.';
    const parts = String(text).split(/(\*[^*\n]+\*)/g);
    return parts.map((part, index) => {
      if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  };

  const handleOpenCustomerPreview = async (booking, type) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) return;
    const { previewPath, title } = CUSTOMER_PREVIEW_CONFIG[type];
    setCustomerPreviewModal({
      bookingId, type, title, loading: true, sending: false,
      message: '', photoUrl: null, channel: null, imageKind: null,
    });
    try {
      const res = await fetch(`${config.customerApiBase}/bookings/${bookingId}/${previewPath}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to load preview');
      setCustomerPreviewModal((prev) => (prev && prev.bookingId === bookingId && prev.type === type ? {
        ...prev,
        loading: false,
        message: data.message || '',
        photoUrl: data.photo_url || null,
        channel: data.channel || null,
        imageKind: data.image_kind || null,
      } : prev));
    } catch (err) {
      toast.error(err.message || 'Failed to load preview');
      setCustomerPreviewModal(null);
    }
  };

  const handleCloseCustomerPreview = () => setCustomerPreviewModal(null);

  const fetchNotifStatus = useCallback(async (bookingId) => {
    if (!bookingId) return;
    try {
      const res = await fetch(`${config.customerApiBase}/bookings/${bookingId}/customer-notification-status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setNotifStatusByBooking((prev) => ({
        ...prev,
        [bookingId]: {
          confirmationSent: !!data.booking_confirmation_sent,
          driverDetailsSent: !!data.driver_details_sent,
        },
      }));
    } catch (err) {
      console.error(`Failed to load notification status for booking ${bookingId}:`, err);
    }
  }, []);

  const handleConfirmCustomerSend = async () => {
    const modal = customerPreviewModal;
    if (!modal) return;
    const { sendPath, successMessage } = CUSTOMER_PREVIEW_CONFIG[modal.type];
    setCustomerPreviewModal((prev) => (prev ? { ...prev, sending: true } : prev));
    try {
      const res = await fetch(`${config.customerApiBase}/bookings/${modal.bookingId}/${sendPath}`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to send');
      toast.success(successMessage);
      setNotifStatusByBooking((prev) => ({
        ...prev,
        [modal.bookingId]: {
          confirmationSent: modal.type === 'booking_summary' ? true : (prev[modal.bookingId]?.confirmationSent ?? false),
          driverDetailsSent: modal.type === 'driver_details' ? true : (prev[modal.bookingId]?.driverDetailsSent ?? false),
        },
      }));
      setCustomerPreviewModal(null);
    } catch (err) {
      toast.error(err.message || 'Failed to send');
      setCustomerPreviewModal((prev) => (prev ? { ...prev, sending: false } : prev));
    }
  };

  const handleOutreachLogged = async () => {
    setOutreachRefreshToken((token) => token + 1);
    await refreshMlOutreachHistory();
  };

  const loadMlAssignmentData = async (bookingId, isDraft = false) => {
    setMlSuggestionsLoading(true);
    setMlSuggestions([]);
    setMlCityWildcards([]);
    setMlOutreachHistory([]);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const headers = { Authorization: `Bearer ${token}` };
      const draftQuery = isDraft ? '&is_draft=true' : '';
      const [suggestionsRes, historyRes] = await Promise.all([
        fetch(`${config.dispatchApiBase}/ml/driver-suggestions/${bookingId}?force_refresh=true${draftQuery}`, { headers }),
        fetch(`${config.dispatchApiBase}/ml/outreach-history/${bookingId}?${isDraft ? 'is_draft=true' : ''}`, { headers }),
      ]);
      const suggestionsData = await suggestionsRes.json().catch(() => ({}));
      const historyData = await historyRes.json().catch(() => ({}));
      if (suggestionsRes.ok && suggestionsData.success) {
        setMlSuggestions(suggestionsData.suggestions || []);
        setMlCityWildcards(suggestionsData.city_wildcards || []);
      } else if (!suggestionsRes.ok) {
        toast.error(suggestionsData.detail || suggestionsData.error || 'Failed to load suggestions');
      }
      if (historyRes.ok && historyData.success) {
        setMlOutreachHistory(historyData.history || []);
      }
    } catch (err) {
      console.error('ML assignment data load error:', err);
    } finally {
      setMlSuggestionsLoading(false);
    }
  };

  const refreshMlOutreachHistory = async () => {
    const bookingId = vehicleAssignModal?.id ?? vehicleAssignModal?.booking_id;
    if (!bookingId) return;
    const isDraft = isDraftBooking(vehicleAssignModal);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(
        `${config.dispatchApiBase}/ml/outreach-history/${bookingId}${isDraft ? '?is_draft=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setMlOutreachHistory(data.history || []);
      }
    } catch (err) {
      console.error('ML outreach history refresh error:', err);
    }
  };

  const getMlAssignMeta = (vehicleId) => {
    const allSuggestions = [...mlSuggestions, ...mlCityWildcards];
    if (!allSuggestions.length) {
      return { was_from_suggestion: false, suggestion_rank: null, ops_override: false };
    }
    const match = allSuggestions.find((item) => Number(item.vehicle_id) === Number(vehicleId));
    if (match) {
      const notFeasible = match.is_feasible_at_suggest_time === false;
      return {
        was_from_suggestion: true,
        suggestion_rank: match.suggestion_rank,
        ops_override: notFeasible,
      };
    }
    return { was_from_suggestion: false, suggestion_rank: null, ops_override: true };
  };

  const openDriverOutreachModal = (vehicleId, driverName = '') => {
    const bookingId = vehicleAssignModal?.id ?? vehicleAssignModal?.booking_id;
    if (!bookingId || !vehicleId) {
      toast.error('Select a vehicle first');
      return;
    }
    setDriverOutreachModal({
      bookingId,
      vehicleId,
      driverName,
      isDraft: isDraftBooking(vehicleAssignModal),
    });
  };

  const handlePlaceCall = async (driverPhone) => {
    if (!driverPhone) return;
    const cleanPhone = String(driverPhone).replace(/\D/g, '');
    if (!cleanPhone) return;

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      let chosenSim = null;
      try {
        const simRes = await fetch(`${config.opsApiBase}/call-server/active-sims?role=driver`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const simData = await simRes.json();
        const onlineSims = (simData?.success && simData?.sims) ? simData.sims : [];

        if (onlineSims.length === 0) {
          if (!window.confirm(`⚠️ No Driver SIM phone is currently detected online.\nAttempt to dial ${driverPhone} anyway?`)) {
            return;
          }
        } else if (onlineSims.length === 1) {
          if (!window.confirm(`Initiate call to driver ${driverPhone} via Phone 1 (${onlineSims[0].sim_number})?`)) {
            return;
          }
          chosenSim = onlineSims[0].sim_number;
        } else {
          // Multiple SIMs online - let operator pick
          const simListStr = onlineSims.map((s, i) => `${i + 1}: ${s.sim_number}`).join('\n');
          const selection = window.prompt(
            `Multiple Driver Phones Online!\nChoose which phone to dial from:\n${simListStr}\n\nEnter option (1 to ${onlineSims.length}):`,
            "1"
          );
          if (!selection) return;
          const idx = parseInt(selection.trim(), 10) - 1;
          if (idx >= 0 && idx < onlineSims.length) {
            chosenSim = onlineSims[idx].sim_number;
          } else {
            chosenSim = onlineSims[0].sim_number;
          }
        }
      } catch (err) {
        if (!window.confirm(`Initiate call to driver ${driverPhone} via office Call Server?`)) {
          return;
        }
      }

      setCallingDrivers((prev) => ({ ...prev, [cleanPhone]: true }));

      const response = await fetch(`${config.opsApiBase}/call-driver`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driver_phone: cleanPhone,
          sim_number: chosenSim,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert(`📞 Call initiated successfully to ${driverPhone}!`);
      } else {
        alert(`Failed to place call: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error placing call:', error);
      alert(`Error placing call: ${error.message}`);
    } finally {
      setCallingDrivers((prev) => ({ ...prev, [cleanPhone]: false }));
    }
  };

  // ---------------------------------------------------------------------------
  // AI Voice — offer a booking to a driver via Shreya (LiveKit + Gemini)
  // ---------------------------------------------------------------------------
  const handleVoiceOfferBooking = async (driverPhone, driverName, booking, vehicleId = null) => {
    const bookingId  = booking?.id ?? booking?.booking_id;
    const isDraft    = isDraftBooking(booking);
    const label      = isDraft ? `Draft #${bookingId}` : `Booking #${bookingId}`;

    const vehicleLabel = vehicleId != null && vehicleId !== ''
      ? (String(vehicleId).toUpperCase().startsWith('V-') ? String(vehicleId).toUpperCase() : `V-${vehicleId}`)
      : null;
    const who = vehicleLabel
      ? `${vehicleLabel}${driverName ? ` · ${driverName}` : ''}`
      : (driverName || driverPhone);

    if (!driverPhone) {
      setVoiceCallNotice({ tone: 'error', title: 'No phone number for this driver.' });
      return;
    }
    if (!bookingId) {
      setVoiceCallNotice({ tone: 'error', title: 'Missing booking id — cannot start AI call.' });
      return;
    }

    const clean = String(driverPhone).replace(/\D/g, '');
    setVoiceCallingDrivers((prev) => ({ ...prev, [clean]: true }));
    setVoiceCallNotice({ tone: 'info', title: `Starting AI call to ${who}…` });

    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/voice/offer-booking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          driver_phone: clean,
          driver_name:  driverName || null,
          booking_id:   isDraft ? null : (bookingId || null),
          draft_id:     isDraft ? (bookingId || null) : null,
          vehicle_id:   vehicleLabel,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setVoiceCallNotice({
          tone: 'error',
          title: 'AI voice call failed',
          detail: data.detail || 'Unknown error',
        });
        return;
      }

      // Message depends on whether this driver is dialing now, already on the call,
      // already waiting, or newly added behind the active call.
      const notices = {
        dialing: {
          tone: 'success',
          title: `Call initiated to ${who}`,
          detail: `Shreya is calling now for ${label}.`,
        },
        already_active: {
          tone: 'warn',
          title: `Call in progress with ${who}`,
          detail: 'This driver is already on an AI call for this booking.',
        },
        already_queued: {
          tone: 'warn',
          title: `${who} is already in the queue`,
          detail: `Waiting at position #${data.position}. Not added twice.`,
        },
        queued: {
          tone: 'info',
          title: `Call added to queue for ${who}`,
          detail: `Position #${data.position}. Dials automatically when the current call ends.`,
        },
      };
      setVoiceCallNotice(notices[data.status] || notices.dialing);
    } catch (err) {
      console.error('[VoiceOffer] error:', err);
      setVoiceCallNotice({
        tone: 'error',
        title: 'Error placing AI call',
        detail: err.message,
      });
    } finally {
      setVoiceCallingDrivers((prev) => ({ ...prev, [clean]: false }));
    }
  };

  // ── Call Queue ─────────────────────────────────────────────────────────────
  const toggleCallQueueMode = () => {
    setCallQueueMode((m) => !m);
    setCallQueueSelected(new Set());
  };

  const toggleCallQueueDriver = (key) => {
    setCallQueueSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleCallQueue = async (selectedDrivers) => {
    if (callQueueRunning || selectedDrivers.length === 0) return;
    setCallQueueRunning(true);
    setVoiceCallNotice({ tone: 'info', title: `Queuing ${selectedDrivers.length} AI calls…` });
    for (const d of selectedDrivers) {
      await handleVoiceOfferBooking(d.driver_phone, d.driver_name, vehicleAssignModal, d.vehicle_id);
      // Brief pause between requests so the server can accept each one
      await new Promise((r) => setTimeout(r, 400));
    }
    setCallQueueRunning(false);
    setCallQueueMode(false);
    setCallQueueSelected(new Set());
    setVoiceCallNotice({ tone: 'success', title: `${selectedDrivers.length} drivers queued for AI call.`, detail: 'Shreya will call them one after another.' });
  };
  // ───────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!voiceCallNotice || !voiceCallNoticeRef.current) return;
    voiceCallNoticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [voiceCallNotice]);

  const goToVoiceBotLogs = () => {
    closeVehicleAssignModal();
    history.push('/app/dispatch/vehicles?tab=voice-bot-logs');
  };

  const beginAssignFlow = async ({ vehicleId, driverFallback, suggestion, mlMeta }) => {
    if (!vehicleAssignModal || !Number.isInteger(vehicleId) || vehicleId <= 0) return;
    const booking = vehicleAssignModal;
    try {
      const vehicleData = await fetchVehicleForOwnerCheck(vehicleId);
      if (vehicleData.owner) {
        closeVehicleAssignModal();
        setOwnerAssignChoiceModal({
          booking,
          vehicleId,
          owner: vehicleData.owner,
          driver: vehicleData.driver,
          mlMeta,
        });
        return;
      }
      setConfirmAssignModal({
        booking,
        vehicleId,
        driver: vehicleData.driver || driverFallback || null,
        suggestion,
        mlMeta,
      });
    } catch (err) {
      console.error('Begin assign flow error:', err);
      toast.error(err.message || 'Failed to assign');
    }
  };

  const handleAssignFromMlSuggestion = (suggestion) => {
    if (!suggestion?.vehicle_id) return;
    beginAssignFlow({
      vehicleId: Number(suggestion.vehicle_id),
      driverFallback: { name: suggestion.driver_name, phone: suggestion.driver_phone },
      suggestion,
      mlMeta: {
        was_from_suggestion: true,
        suggestion_rank: suggestion.suggestion_rank,
        ops_override: false,
      },
    });
  };

  const handleAssignFromFreeNearby = (driver) => {
    if (!driver?.vehicle_id) return;
    beginAssignFlow({
      vehicleId: Number(driver.vehicle_id),
      driverFallback: { name: driver.driver_name, phone: driver.driver_phone },
      suggestion: driver,
      mlMeta: {
        was_from_suggestion: false,
        suggestion_rank: null,
        ops_override: true,
      },
    });
  };

  const loadFreeNearbyDrivers = async (bookingId, radiusKm = 12, isDraft = false) => {
    setFreeNearbyLoading(true);
    setFreeNearbySuggestions([]);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const draftQuery = isDraft ? '&is_draft=true' : '';
      const response = await fetch(
        `${config.dispatchApiBase}/ml/nearby-available-drivers/${bookingId}?radius_km=${radiusKm}${draftQuery}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) {
        setFreeNearbySuggestions(data.drivers || []);
      } else {
        toast.error(data.detail || data.error || 'Failed to load nearby drivers');
      }
    } catch (err) {
      console.error('Free nearby drivers load error:', err);
      toast.error(err.message || 'Failed to load nearby drivers');
    } finally {
      setFreeNearbyLoading(false);
    }
  };

  const loadFreeNearDropDrivers = async (bookingId, isDraft = false) => {
    setFreeNearDropLoading(true);
    setFreeNearDropSuggestions([]);
    setDropLoyaltySuggestions([]);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const headers = { Authorization: `Bearer ${token}` };
      const draftQuery = isDraft ? '&is_draft=true' : '';
      const [freeRes, loyaltyRes] = await Promise.all([
        fetch(
          `${config.dispatchApiBase}/ml/nearby-available-drivers/${bookingId}?near=drop&radius_km=5${draftQuery}`,
          { headers }
        ),
        fetch(
          `${config.dispatchApiBase}/ml/driver-suggestions/${bookingId}?near=drop&limit=3&force_refresh=true${draftQuery}`,
          { headers }
        ),
      ]);
      const freeData = await freeRes.json().catch(() => ({}));
      const loyaltyData = await loyaltyRes.json().catch(() => ({}));
      if (freeRes.ok && freeData.success) {
        setFreeNearDropSuggestions(freeData.drivers || []);
      } else if (!freeRes.ok) {
        toast.error(freeData.detail || freeData.error || 'Failed to load free drivers near drop');
      }
      if (loyaltyRes.ok && loyaltyData.success) {
        setDropLoyaltySuggestions(loyaltyData.suggestions || []);
      } else if (!loyaltyRes.ok) {
        toast.error(loyaltyData.detail || loyaltyData.error || 'Failed to load drop loyalty suggestions');
      }
    } catch (err) {
      console.error('Free near drop drivers load error:', err);
      toast.error(err.message || 'Failed to load drivers near drop');
    } finally {
      setFreeNearDropLoading(false);
    }
  };

  const handleToggleFreeNearby = () => {
    const bookingId = vehicleAssignModal?.id ?? vehicleAssignModal?.booking_id;
    if (!bookingId) return;
    const isDraft = isDraftBooking(vehicleAssignModal);
    const next = !showFreeNearby;
    setShowFreeNearby(next);
    if (next && freeNearbySuggestions.length === 0 && !freeNearbyLoading) {
      loadFreeNearbyDrivers(bookingId, 12, isDraft);
    }
  };

  const handleToggleFreeNearDrop = () => {
    const bookingId = vehicleAssignModal?.id ?? vehicleAssignModal?.booking_id;
    if (!bookingId) return;
    const isDraft = isDraftBooking(vehicleAssignModal);
    const next = !showFreeNearDrop;
    setShowFreeNearDrop(next);
    if (next && freeNearDropSuggestions.length === 0 && dropLoyaltySuggestions.length === 0 && !freeNearDropLoading) {
      loadFreeNearDropDrivers(bookingId, isDraft);
    }
  };

  const handleConfirmDirectAssign = async () => {
    if (!confirmAssignModal) return;
    const { booking, vehicleId, mlMeta } = confirmAssignModal;
    const modalSnapshot = { ...confirmAssignModal };
    setConfirmAssignModal(null);
    try {
      const success = await assignVehicleToBooking(booking, vehicleId, {
        mlMeta,
      });
      if (success) closeVehicleAssignModal();
    } catch (err) {
      console.error('Confirm direct assign error:', err);
      toast.error(err.message || 'Failed to assign');
      setConfirmAssignModal(modalSnapshot);
    }
  };

  const loadPartVehicles = async () => {
    setPartVehiclesLoading(true);
    try {
      const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
      const response = await fetch(`${config.opsApiBase}/vehicle-drivers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.success || !Array.isArray(data?.data)) {
        throw new Error(data?.detail || data?.message || `HTTP ${response.status}`);
      }

      const eligibleVehicles = data.data
        .filter((vehicle) => Boolean(vehicle?.is_part))
        .map((vehicle) => ({
          vehicle_id: Number(vehicle.vehicle_id),
          registration_no: vehicle.registration_no || '',
          driver_name: vehicle.driver_name || '',
          is_active: Boolean(vehicle.is_active),
        }))
        .filter((vehicle) => Number.isInteger(vehicle.vehicle_id) && vehicle.vehicle_id > 0)
        .sort((a, b) => a.vehicle_id - b.vehicle_id);

      setPartVehicles(eligibleVehicles);
      return eligibleVehicles;
    } catch (err) {
      console.error('Load part vehicles error:', err);
      toast.error(err.message || 'Failed to load part-booking vehicles');
      setPartVehicles([]);
      return [];
    } finally {
      setPartVehiclesLoading(false);
    }
  };

  const handleOpenDraftSuggestions = (booking) => {
    const draftId = booking?.id ?? booking?.booking_id;
    if (!draftId) {
      toast.error('Draft ID not found');
      return;
    }
    setVehicleIdInput('');
    setDriverNameInput('');
    setVehicleAssignModal(booking);
    loadMlAssignmentData(draftId, true);
  };

  const handleAssignVehicle = async (booking) => {
    const bookingId = booking?.id ?? booking?.booking_id;
    if (!bookingId) {
      toast.error('Booking ID not found');
      return;
    }

    if (shouldUsePartAssignment(booking)) {
      const eligibleVehicles = await loadPartVehicles();
      if (eligibleVehicles.length === 0) {
        toast.warning('No part-booking enabled vehicles found');
        return;
      }

      setPartAssignModal(booking);
      setSelectedPartVehicleId(String(eligibleVehicles[0].vehicle_id));
      return;
    }

    setVehicleIdInput('');
    setDriverNameInput('');
    setVehicleAssignModal(booking);
    loadAssignVehicleDrivers();
    loadMlAssignmentData(bookingId);
  };

  const handleConfirmVehicleAssign = async () => {
    if (!vehicleAssignModal) return;
    const booking = vehicleAssignModal;

    let vehicleId;
    try {
      vehicleId = await resolveVehicleIdForAssign();
    } catch (err) {
      toast.error(err.message || 'Could not resolve vehicle');
      return;
    }

    if (!Number.isInteger(vehicleId) || vehicleId <= 0) {
      toast.error('Enter a Vehicle ID or Driver name');
      return;
    }

    try {
      const vehicleData = await fetchVehicleForOwnerCheck(vehicleId);

      if (vehicleData.owner) {
        closeVehicleAssignModal();
        setOwnerAssignChoiceModal({
          booking,
          vehicleId,
          owner: vehicleData.owner,
          driver: vehicleData.driver,
          mlMeta: getMlAssignMeta(vehicleId),
          driverPay: driverPayInput,
          labourPay: labourPayInput,
        });
        return;
      }

      const success = await assignVehicleToBooking(booking, vehicleId, {
        mlMeta: getMlAssignMeta(vehicleId),
      });
      if (success) {
        closeVehicleAssignModal();
      }
    } catch (err) {
      console.error('Vehicle owner check error:', err);
      // Owner lookup is optional — do not block manual assignment if vehicle details fail.
      const success = await assignVehicleToBooking(booking, vehicleId);
      if (success) {
        closeVehicleAssignModal();
      }
    }
  };

  const handleAssignDirectToDriver = async () => {
    if (!ownerAssignChoiceModal) return;
    const { booking, vehicleId, mlMeta, driverPay, labourPay } = ownerAssignChoiceModal;
    setOwnerAssignChoiceModal(null);
    await assignVehicleToBooking(booking, vehicleId, {
      ...(mlMeta ? { mlMeta } : {}),
      ...(driverPay != null ? { driverPay } : {}),
      ...(labourPay != null ? { labourPay } : {}),
    });
  };

  const handleAssignThroughOwner = async () => {
    if (!ownerAssignChoiceModal) return;
    const { booking, vehicleId, owner, mlMeta, driverPay, labourPay } = ownerAssignChoiceModal;
    const ownerPhone = owner?.phone;
    if (!ownerPhone) {
      toast.error('Owner phone not found for this vehicle');
      return;
    }
    const modalSnapshot = { ...ownerAssignChoiceModal };
    setOwnerAssignChoiceModal(null);
    const success = await assignVehicleToBooking(booking, vehicleId, {
      throughOwner: true,
      ownerPhone,
      ...(mlMeta ? { mlMeta } : {}),
      ...(driverPay != null ? { driverPay } : {}),
      ...(labourPay != null ? { labourPay } : {}),
    });
    if (!success) {
      setOwnerAssignChoiceModal(modalSnapshot);
    }
  };

  const handleConfirmPartVehicleAssign = async () => {
    if (!partAssignModal) return;
    const vehicleId = Number.parseInt((selectedPartVehicleId || '').trim(), 10);
    const success = await assignVehicleToBooking(partAssignModal, vehicleId);
    if (success) {
      setPartAssignModal(null);
      setSelectedPartVehicleId('');
    }
  };

  const canToggleOnHold = (booking) => {
    const bookingState = normalizeState(booking?.state);
    return !booking?.trip_state && bookingState !== 'cancelled' && bookingState !== 'completed' && bookingState !== 'draft' && bookingState !== 'deleted';
  };

  const handleToggleOnHold = async (booking) => {
    if (!booking?.id) return;

    const nextOnHold = !Boolean(booking.on_hold);
    try {
      setOnHoldUpdatingId(booking.id);

      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${booking.id}/on-hold`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ on_hold: nextOnHold }),
        }
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      toast.success(nextOnHold ? `Booking #${booking.id} put on hold` : `Booking #${booking.id} removed from hold`);
      await fetchBookings();
    } catch (err) {
      console.error('On-hold update error:', err);
      toast.error(err.message || 'Failed to update on-hold status');
    } finally {
      setOnHoldUpdatingId(null);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Auto-refresh data on an interval to keep data fresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchBookings();
    }, CONFIG.POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchBookings]);

  // Tick down countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefreshIn((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sourceFilter, bookingKinds, debouncedSearchTerm, selectedCities, dateRange, showDeleted]);

  const normalizeState = (state) => {
    if (!state) return 'pending';
    return state.toLowerCase();
  };

  const formatStateDisplay = (state) => {
    const normalized = normalizeState(state);
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Pagination logic
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    setPageInput(currentPage);
  }, [currentPage]);

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      const num = parseInt(pageInput, 10);
      if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
        handlePageChange(num);
      } else {
        setPageInput(currentPage);
      }
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage);
  };

  const savePaymentOtherParty = async (booking) => {
    const normalizedPhone = normalizeIndianPhone(paymentOtherPartyPhone);
    if (!normalizedPhone || normalizedPhone.length < 10) {
      throw new Error('Please enter a valid other-party phone number.');
    }

    setPaymentOtherPartyPhone(normalizedPhone);
    const existingPhone = normalizeIndianPhone(booking.other_party_phone_number || '');
    if (existingPhone === normalizedPhone && booking.other_party_pays) {
      return { ...booking, other_party_phone_number: normalizedPhone, other_party_pays: true };
    }

    const payload = {
      other_party_pays: true,
      send_notification: false,
    };
    if (existingPhone !== normalizedPhone) {
      payload.other_party_phone_number = normalizedPhone;
    }

    const response = await fetch(
      `${CONFIG.API_BASE_URL}/bookings/${booking.id}/other-party`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }
    );
    const data = await response.json();
    if (!response.ok || data.success === false) {
      throw new Error(data.detail || data.error || `HTTP ${response.status}`);
    }

    const updatedBooking = {
      ...booking,
      other_party_phone_number: data.other_party_phone_number || normalizedPhone,
      other_party_pays: true,
    };
    setPaymentModal((prev) => (
      prev ? { ...prev, booking: { ...prev.booking, ...updatedBooking } } : prev
    ));
    return updatedBooking;
  };

  const handleSavePaymentOtherParty = async (booking) => {
    try {
      setPaymentRecipientError('');
      setPaymentRecipientNotice('');
      setPaymentRecipientSavingId(booking.id);
      const updatedBooking = await savePaymentOtherParty(booking);
      setPaymentRecipient('other_party');
      setPaymentRecipientNotice(
        `Other party ${formatPhoneForDisplay(updatedBooking.other_party_phone_number)} saved as the paying party.`
      );
      await fetchBookings();
    } catch (err) {
      console.error('Save payment other party error:', err);
      setPaymentRecipientError(err.message || 'Failed to save other-party details.');
    } finally {
      setPaymentRecipientSavingId(null);
    }
  };

  const handleGeneratePaymentLink = async (booking, target = 'customer', deliveryMode = 'link') => {
    try {
      setActionBookingId(booking.id);
      setPaymentRecipientError('');
      setPaymentRecipientNotice('');
      let paymentBooking = booking;
      if (target === 'other_party') {
        paymentBooking = await savePaymentOtherParty(booking);
      }

      const parsedAmount = resolvePaymentSendAmount(paymentBooking);
      const amountToSend = parsedAmount;

      const response = await fetch(
        `${config.paymentApiBase}/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_id: paymentBooking.id,
            amount: amountToSend,
            customer_phone: target === 'other_party'
              ? paymentBooking.other_party_phone_number
              : paymentBooking.customer_phone,
            customer_name: paymentBooking.customer_name,
            delivery_mode: deliveryMode || 'link',
            send_notification: false,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      const modeLabel = data.instrument_type === 'QR_CODE' ? 'QR' : 'payment link';

      if (target === 'other_party' && !paymentBooking.other_party_phone_number) {
        throw new Error('Other party phone number is not available for this booking.');
      }

      const sendResp = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${paymentBooking.id}/payment-link/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient: target,
            amount: amountToSend,
            payment_link: data.payment_url,
            instrument_type: data.instrument_type || (deliveryMode === 'qr' ? 'QR_CODE' : 'PAYMENT_LINK'),
          }),
        }
      );
      const sendData = await sendResp.json();
      if (!sendResp.ok || sendData.success === false) {
        throw new Error(sendData.detail || sendData.error || `HTTP ${sendResp.status}`);
      }

      alert(`${modeLabel} sent to ${target === 'other_party' ? 'other party' : 'customer'}.`);
      setPaymentModal(null);
      fetchBookings();
    } catch (err) {
      console.error('Payment generate error:', err);
      if (target === 'other_party') {
        setPaymentRecipientError(err.message || 'Failed to send payment request.');
      } else {
        alert(`Failed to generate payment: ${err.message}`);
      }
    } finally {
      setActionBookingId(null);
    }
  };

  const handleCancelActivePayment = async (booking) => {
    if (!window.confirm(`Cancel all active payment links/QRs for booking #${booking.id}?`)) {
      return;
    }
    try {
      setActionBookingId(booking.id);
      const response = await fetch(`${config.paymentApiBase}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ booking_id: booking.id }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }
      const count = (data.cancelled || []).length;
      alert(count ? `Cancelled ${count} active payment instrument(s).` : 'No active instruments cancelled.');
      setPaymentModal(null);
      fetchBookings();
    } catch (err) {
      console.error('Payment cancel error:', err);
      alert(`Failed to cancel payment: ${err.message}`);
    } finally {
      setActionBookingId(null);
    }
  };

  const getBookingTotal = (booking) => Number(
    booking.actual_bill ?? booking.payment_estimate ?? booking.amount ?? 0
  );

  const getBookingOutstanding = (booking) => {
    if (booking.outstanding != null) return Number(booking.outstanding);
    return Math.max(0, getBookingTotal(booking) - Number(booking.amount_paid || 0));
  };

  const getAmountStatus = (booking) => {
    if (booking.state?.toLowerCase() === 'cancelled') return 'cancelled';
    if (booking.payment_id) return 'paid';
    if (Number(booking.amount_paid || 0) > 0) return 'partial';
    if (booking.actual_bill !== null && booking.actual_bill !== undefined) {
      return 'unpaid';
    }
    if (Number(booking.collect_now_amount || 0) > 0) {
      return 'unpaid';
    }
    return 'estimate';
  };

  const resolvePaymentSendAmount = (booking) => {
    const outstanding = getBookingOutstanding(booking);
    const amountPaid = Number(booking.amount_paid || 0);
    if (amountPaid > 0) {
      return outstanding;
    }
    const parsedCollect = Number(collectNowInput);
    if (Number.isFinite(parsedCollect) && parsedCollect > 0) {
      return parsedCollect;
    }
    const parsedAmount = Number(paymentAmountInput);
    if (Number.isFinite(parsedAmount) && parsedAmount > 0 && parsedAmount <= outstanding) {
      return parsedAmount;
    }
    return outstanding;
  };

  const handleAmountClick = (booking) => {
    const status = getAmountStatus(booking);
    const bookingState = normalizeState(booking.state);
    if (status === 'cancelled' || status === 'estimate' || bookingState === 'draft' || bookingState === 'deleted') return;

    const total = getBookingTotal(booking);
    setPaymentAmountInput(String(total.toFixed(2)));
    setCollectNowInput(
      booking.collect_now_amount != null
        ? String(Number(booking.collect_now_amount).toFixed(2))
        : ''
    );
    setPlanError('');
    setAmountUpdateError('');
    setManualPaySavingId(null);
    setManualPayError('');
    setManualPaymentMode('CASH');
    setManualOtherProvider('');
    setManualTxnTime(toLocalDateTimeInput());
    setManualGatewayTxnId('');
    setManualPayNotes('');
    const hasPayingOtherParty = Boolean(booking.other_party_pays && booking.other_party_phone_number);
    setPaymentRecipient(hasPayingOtherParty ? 'other_party' : 'customer');
    setPaymentOtherPartyPhone(
      booking.other_party_phone_number ? String(booking.other_party_phone_number) : ''
    );
    setPaymentRecipientError('');
    setPaymentRecipientNotice('');
    setPaymentRecipientSavingId(null);

    if (status === 'paid') {
      setPaymentModal({ type: 'paid', booking });
    } else if (status === 'unpaid' || status === 'partial') {
      setPaymentModal({ type: 'unpaid', booking });
    }
  };

  const handleSavePaymentPlan = async (booking) => {
    try {
      const total = getBookingTotal(booking);
      const trimmed = collectNowInput.trim();
      let payload = { collect_now_amount: null };
      if (trimmed) {
        const parsedCollect = Number(trimmed);
        if (!Number.isFinite(parsedCollect) || parsedCollect <= 0) {
          setPlanError('Enter a valid collect-now amount greater than 0, or leave blank for full payment.');
          return;
        }
        if (parsedCollect >= total) {
          setPlanError('Collect-now amount must be less than the booking total.');
          return;
        }
        payload = { collect_now_amount: parsedCollect };
      }

      setPlanError('');
      setPlanSavingId(booking.id);
      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${booking.id}/payment-plan`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      await fetchBookings();
      setPaymentModal((prev) => (
        prev
          ? {
              ...prev,
              booking: {
                ...prev.booking,
                collect_now_amount: data.collect_now_amount,
                outstanding: data.outstanding,
              },
            }
          : prev
      ));
    } catch (err) {
      console.error('Save payment plan error:', err);
      setPlanError(err.message || 'Failed to save payment plan');
    } finally {
      setPlanSavingId(null);
    }
  };

  const handleUpdateAmount = async (booking) => {
    try {
      const parsedAmount = Number(paymentAmountInput);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setAmountUpdateError('Please enter a valid amount greater than 0.');
        return;
      }

      setAmountUpdateError('');
      setAmountUpdatingId(booking.id);

      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${booking.id}/amount`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: parsedAmount }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      setPaymentModal((prev) => (
        prev ? { ...prev, booking: { ...prev.booking, payment_estimate: parsedAmount } } : prev
      ));

      await fetchBookings();
      alert(`Amount updated to ₹${parsedAmount.toFixed(2)}`);
    } catch (err) {
      console.error('Update amount error:', err);
      setAmountUpdateError(err.message || 'Failed to update amount');
    } finally {
      setAmountUpdatingId(null);
    }
  };

  const handleRecordManualPayment = async (booking) => {
    try {
      const parsedAmount = resolvePaymentSendAmount(booking);
      if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
        setManualPayError('Please enter a valid amount greater than 0.');
        return;
      }

      if (manualPaymentMode === 'OTHER' && !manualOtherProvider.trim()) {
        setManualPayError('Please enter provider name for Other payment mode.');
        return;
      }

      setManualPayError('');
      setManualPaySavingId(booking.id);

      const response = await fetch(
        `${config.paymentApiBase}/manual-payment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            booking_id: booking.id,
            amount: parsedAmount,
            mode: manualPaymentMode,
            other_provider: manualPaymentMode === 'OTHER' ? manualOtherProvider.trim() : null,
            transaction_time: manualTxnTime ? new Date(manualTxnTime).toISOString() : null,
            gateway_transaction_id: manualGatewayTxnId.trim() || null,
            notes: manualPayNotes.trim() || null,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      setPaymentModal(null);
      await fetchBookings();
      const warn = data.superseded_instruments?.failed?.length ? `\n\n${data.message}` : '';
      alert(`Manual payment recorded for booking #${booking.id}${warn}`);
    } catch (err) {
      console.error('Manual payment error:', err);
      setManualPayError(err.message || 'Failed to record manual payment');
    } finally {
      setManualPaySavingId(null);
    }
  };

  const handleNewBooking = useCallback(() => {
    history.push('/app/customers/new-booking');
  }, [history]);

  const openOtherPartyModal = (booking) => {
    setOtherPartyModal(booking);
    setOtherPartyPhoneInput(booking.other_party_phone_number ? String(booking.other_party_phone_number) : '');
    setOtherPartyPaysInput(Boolean(booking.other_party_pays));
    setOtherPartyUpdateError('');
  };

  const handleUpdateOtherParty = async () => {
    if (!otherPartyModal) return;
    try {
      const normalizedPhone = normalizeIndianPhone(otherPartyPhoneInput);
      if (!normalizedPhone) {
        setOtherPartyUpdateError('Please enter other party phone number.');
        return;
      }
      if (normalizedPhone.length < 10) {
        setOtherPartyUpdateError('Phone number should have at least 10 digits.');
        return;
      }

      // Keep UI aligned with backend-normalized value.
      setOtherPartyPhoneInput(normalizedPhone);

      setOtherPartyUpdateError('');
      setOtherPartyUpdatingId(otherPartyModal.id);

      const response = await fetch(
        `${CONFIG.API_BASE_URL}/bookings/${otherPartyModal.id}/other-party`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            other_party_phone_number: normalizedPhone,
            other_party_pays: Boolean(otherPartyPaysInput),
          }),
        }
      );

      const data = await response.json();
      if (!response.ok || data.success === false) {
        throw new Error(data.detail || data.error || `HTTP ${response.status}`);
      }

      setOtherPartyModal(null);
      await fetchBookings();
      alert('Other party details updated.');
    } catch (err) {
      console.error('Update other party error:', err);
      setOtherPartyUpdateError(err.message || 'Failed to update other party details');
    } finally {
      setOtherPartyUpdatingId(null);
    }
  };

  const openInvoiceModal = async (booking) => {
    setInvoiceModal(booking);
    setInvoiceLoading(true);
    setInvoiceError(null);
    setInvoiceData(null);
    setInvoiceSent(false);
    setInvoiceEditMode(false);
    setInvoiceFinalizing(false);
    setInvoiceEditAllUnlocked(false);
    setInvoiceEditAllPrompt(false);
    setInvoiceEditAllPassword('');
    setInvoiceEditAllPasswordInput('');
    setInvoiceEditAllDownloading(false);
    setInvoiceEditAllFields({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });
    setInvoiceEditFields({ gstin: '', consigner_name: '', company_name: '', company_address: '' });

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${booking.id}/invoice?preview=1`, {
        method: 'POST',
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to load invoice');
      }
      setInvoiceData(data);
      // Pre-populate edit fields from invoice data (includes customer GSTIN)
      if (data.invoice_data) {
        setInvoiceEditFields({
          gstin: data.invoice_data.gstin && data.invoice_data.gstin !== '—' ? data.invoice_data.gstin : '',
          consigner_name: data.invoice_data.consigner_name || '',
          company_name: data.invoice_data.company_name || '',
          company_address: data.invoice_data.company_address || '',
        });
      }
    } catch (err) {
      setInvoiceError(err.message || 'Error loading invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceDownload = async (e) => {
    e.stopPropagation();
    if (invoiceData?.invoice_url) {
      window.open(invoiceData.invoice_url, '_blank');
      return;
    }
    if (!invoiceModal || invoiceFinalizing) return;
    setInvoiceFinalizing(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gstin: invoiceEditFields.gstin,
          consigner_name: invoiceEditFields.consigner_name,
          company_name: invoiceEditFields.company_name,
          company_address: invoiceEditFields.company_address,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to generate invoice');
      }
      setInvoiceData({ ...data, cached: true });
      window.open(data.invoice_url, '_blank');
    } catch (err) {
      setInvoiceError(err.message || 'Error generating invoice');
    } finally {
      setInvoiceFinalizing(false);
    }
  };

  const handleInvoiceEditAllClick = () => {
    if (invoiceEditAllUnlocked) {
      setInvoiceEditAllUnlocked(false);
      setInvoiceEditAllPrompt(false);
      return;
    }
    setInvoiceEditAllPrompt(true);
    setInvoiceEditAllPasswordInput('');
    setInvoiceError(null);
  };

  const handleInvoiceEditAllUnlock = async (e) => {
    e.stopPropagation();
    if (!invoiceModal || !invoiceEditAllPasswordInput) return;
    setInvoiceLoading(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice/local-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPasswordInput,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to unlock Edit All');
      }
      setInvoiceEditAllPassword(invoiceEditAllPasswordInput);
      setInvoiceEditAllPasswordInput('');
      setInvoiceEditAllUnlocked(true);
      setInvoiceEditAllPrompt(false);
      setInvoiceEditMode(false);
      invoiceEditAllSkipLive.current = true;
      setInvoiceEditAllFields(fieldsFromInvoiceData(data.invoice_data));
      setInvoiceData(prev => ({ ...prev, invoice_html: data.invoice_html, invoice_data: { ...(prev?.invoice_data || {}), ...data.invoice_data } }));
    } catch (err) {
      setInvoiceError(err.message || 'Invalid password');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleInvoiceEditAllField = (field, value) => {
    setInvoiceEditAllFields(prev => ({ ...prev, [field]: value }));
  };

  const refreshInvoiceEditAllPreview = useCallback(async (fields, { silent = true } = {}) => {
    if (!invoiceModal || !invoiceEditAllPassword) return;
    const seq = ++invoiceEditAllPreviewSeq.current;
    if (!silent) {
      setInvoiceLoading(true);
      setInvoiceError(null);
    } else {
      setInvoiceEditAllPreviewUpdating(true);
    }

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice/local-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPassword,
          ...fields,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to update preview');
      }
      if (seq !== invoiceEditAllPreviewSeq.current) return;
      setInvoiceData(prev => ({ ...prev, invoice_html: data.invoice_html, invoice_data: { ...(prev?.invoice_data || {}), ...data.invoice_data } }));
    } catch (err) {
      if (seq !== invoiceEditAllPreviewSeq.current) return;
      if (!silent) setInvoiceError(err.message || 'Error updating preview');
    } finally {
      if (seq === invoiceEditAllPreviewSeq.current) {
        if (!silent) setInvoiceLoading(false);
        setInvoiceEditAllPreviewUpdating(false);
      }
    }
  }, [invoiceModal, invoiceEditAllPassword]);

  const handleInvoiceEditAllApplyPreview = async (e) => {
    e.stopPropagation();
    await refreshInvoiceEditAllPreview(invoiceEditAllFields, { silent: false });
  };

  useEffect(() => {
    if (!invoiceEditAllUnlocked || !invoiceEditAllPassword || !invoiceModal) return;
    if (invoiceEditAllSkipLive.current) {
      invoiceEditAllSkipLive.current = false;
      return;
    }
    if (invoiceEditAllPreviewTimer.current) clearTimeout(invoiceEditAllPreviewTimer.current);
    invoiceEditAllPreviewTimer.current = setTimeout(() => {
      refreshInvoiceEditAllPreview(invoiceEditAllFields, { silent: true });
    }, 400);
    return () => {
      if (invoiceEditAllPreviewTimer.current) clearTimeout(invoiceEditAllPreviewTimer.current);
    };
  }, [invoiceEditAllFields, invoiceEditAllUnlocked, invoiceEditAllPassword, invoiceModal, refreshInvoiceEditAllPreview]);

  const handleInvoiceEditAllDownload = async (e) => {
    e.stopPropagation();
    if (!invoiceModal || invoiceEditAllDownloading || !invoiceEditAllPassword) return;
    setInvoiceEditAllDownloading(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice/local-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPassword,
          ...invoiceEditAllFields,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to download local PDF');
      }
      const blob = await resp.blob();
      const header = resp.headers.get('Content-Disposition');
      const match = header && header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const filename = match ? decodeURIComponent(match[1]) : `Invoice_INV-${invoiceModal.id}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setInvoiceError(err.message || 'Error downloading local PDF');
    } finally {
      setInvoiceEditAllDownloading(false);
    }
  };

  const handleInvoiceSend = async (e) => {
    e.stopPropagation();
    setInvoiceSending(true);
    setInvoiceSent(false);
    setInvoiceError(null);

    try {
      const options = invoiceData?.cached
        ? { method: 'POST' }
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              gstin: invoiceEditFields.gstin,
              consigner_name: invoiceEditFields.consigner_name,
              company_name: invoiceEditFields.company_name,
              company_address: invoiceEditFields.company_address,
            }),
          };
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice/send`, options);
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to send invoice');
      }
      setInvoiceSent(true);
      setInvoiceData(prev => ({ ...prev, ...data, cached: true }));
    } catch (err) {
      setInvoiceError(err.message || 'Error sending invoice');
    } finally {
      setInvoiceSending(false);
    }
  };

  const closeInvoiceModal = () => {
    setInvoiceModal(null);
    setInvoiceData(null);
    setInvoiceError(null);
    setInvoiceSent(false);
    setInvoiceEditMode(false);
    setInvoiceFinalizing(false);
    setInvoiceEditAllUnlocked(false);
    setInvoiceEditAllPrompt(false);
    setInvoiceEditAllPassword('');
    setInvoiceEditAllPasswordInput('');
    setInvoiceEditAllDownloading(false);
    setInvoiceEditAllFields({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });
  };

  const handleInvoiceEditField = (field, value) => {
    setInvoiceEditFields(prev => ({ ...prev, [field]: value }));
  };

  const handleInvoiceToggleEdit = () => {
    if (!invoiceEditMode && invoiceData?.invoice_data) {
      setInvoiceEditFields({
        gstin: invoiceData.invoice_data.gstin && invoiceData.invoice_data.gstin !== '—' ? invoiceData.invoice_data.gstin : '',
        consigner_name: invoiceData.invoice_data.consigner_name || '',
        company_name: invoiceData.invoice_data.company_name || '',
        company_address: invoiceData.invoice_data.company_address || '',
      });
    }
    setInvoiceEditMode(prev => !prev);
  };

  const handleInvoiceApplyPreview = async (e) => {
    e.stopPropagation();
    if (!invoiceModal) return;
    setInvoiceLoading(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${CONFIG.API_BASE_URL}/bookings/${invoiceModal.id}/invoice?preview=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gstin: invoiceEditFields.gstin,
          consigner_name: invoiceEditFields.consigner_name,
          company_name: invoiceEditFields.company_name,
          company_address: invoiceEditFields.company_address,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to update preview');
      }
      setInvoiceData(data);
      setInvoiceEditMode(false);
    } catch (err) {
      setInvoiceError(err.message || 'Error updating preview');
    } finally {
      setInvoiceLoading(false);
    }
  };

  return (
    <div className={s.root}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerLeft}>
          <h1 className={s.title}>
            <div className={s.titleIcon}>
              <i className={`fa ${pageMeta.iconClass}`} />
            </div>
            {pageMeta.title}
            <span className={s.countBadge}>{activeCount}</span>
          </h1>
          <p className={s.subtitle}>{pageMeta.subtitle}</p>
        </div>
        <div className={s.headerRight}>
          {!hideNewBooking && (
            <button className={s.btnNewBooking} onClick={handleNewBooking}>
              <i className="fa fa-plus" />
              New Booking
            </button>
          )}
          <button 
            className={s.refreshBtn} 
            onClick={fetchBookings}
            disabled={isRefreshing}
            title={`Auto-refreshing in ${Math.ceil(nextRefreshIn)}s`}
          >
            <i className={`fa ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
            {!isRefreshing && (
              <div className={s.refreshProgressTrack}>
                <div 
                  className={s.refreshProgressBar}
                  style={{ width: `${Math.max(0, Math.min(100, (nextRefreshIn / refreshIntervalSeconds) * 100))}%` }}
                />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Filter & Search Section */}
      <div className={s.filterSection}>
        <div className={s.filterWrapper}>
          <div className={s.filterTabs}>
            <button
              className={`${s.filterTab} ${s.all} ${filter === 'all' ? s.active : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`${s.filterTab} ${s.pending} ${filter === 'pending' ? s.active : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button
              className={`${s.filterTab} ${s.assigned} ${filter === 'assigned' ? s.active : ''}`}
              onClick={() => setFilter('assigned')}
            >
              Assigned
            </button>
            <button
              className={`${s.filterTab} ${s.modified} ${filter === 'modified' ? s.active : ''}`}
              onClick={() => setFilter('modified')}
            >
              Modified
            </button>
            <button
              className={`${s.filterTab} ${s.completed} ${filter === 'completed' ? s.active : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
            <button
              className={`${s.filterTab} ${s.cancelled} ${filter === 'cancelled' ? s.active : ''}`}
              onClick={() => setFilter('cancelled')}
            >
              Cancelled
            </button>
            <button
              className={`${s.filterTab} ${s.hourly} ${filter === 'hourly' ? s.active : ''}`}
              onClick={() => setFilter('hourly')}
            >
              <i className="fa fa-clock-o" />
              Hourly
            </button>
            <button
              className={`${s.filterTab} ${s.draft} ${filter === 'draft' ? s.active : ''}`}
              onClick={() => setFilter('draft')}
            >
              <i className="fa fa-file-text-o" />
              Drafts
              {draftCount > 0 && (
                <span className={s.draftCountBadge}>{draftCount}</span>
              )}
            </button>
          </div>
          {filter === 'draft' && (
            <div className={s.draftToggleWrap}>
              <button
                className={`${s.showDeletedBtn} ${showDeleted ? s.active : ''}`}
                onClick={() => setShowDeleted((v) => !v)}
              >
                <i className={`fa ${showDeleted ? 'fa-check-square-o' : 'fa-square-o'}`} />
                Show Deleted
              </button>
            </div>
          )}

          {filter !== 'hourly' && (
            <div className={s.typeFilterGroup} role="group" aria-label="Booking types">
              {BOOKING_KIND_OPTIONS.map((option) => {
                const checked = bookingKinds.includes(option.key);
                return (
                  <button
                    type="button"
                    key={option.key}
                    className={`${s.typeFilterChip} ${checked ? s.typeFilterChipActive : ''}`}
                    aria-pressed={checked}
                    onClick={() => {
                      const next = checked
                        ? bookingKinds.filter((key) => key !== option.key)
                        : [...bookingKinds, option.key];
                      if (next.length === 0) return;
                      setBookingKinds(next);
                    }}
                  >
                    <span className={`${s.typeFilterBox} ${checked ? s.typeFilterBoxOn : ''}`} aria-hidden />
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className={s.sourceFilterGroup}>
            <select
              id="source-filter"
              className={s.sourceSelect}
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <option value="">All Sources</option>
              <option value="b2b">B2B Portal</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="ops">Manual Entry</option>
            </select>
          </div>

          <div className={s.searchInputGroup}>
            <span className={s.searchIcon}>
              <i className="fa fa-search" />
            </span>
            <input
              type="text"
              className={s.searchInput}
              placeholder={searchPlaceholderText}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className={s.clearBtn} onClick={() => setSearchTerm('')}>
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className={s.tableCard}>
        {error ? (
          <div className={s.errorState}>
            <div className={s.errorIcon}>
              <i className="fa fa-exclamation-circle" />
            </div>
            <h3>Error Loading Bookings</h3>
            <p>{error}</p>
            <button className={s.retryBtn} onClick={fetchBookings}>
              <i className="fa fa-refresh" />
              Retry
            </button>
          </div>
        ) : loading && bookings.length === 0 ? (
          <div className={s.loadingState}>
            <div className={s.spinner} />
            <p>Loading bookings...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className={s.emptyState}>
            <div className={s.emptyIcon}>
              <i className="fa fa-calendar-times-o" />
            </div>
            <h3>No bookings found</h3>
            <p>
              {searchTerm
                ? `No bookings match "${searchTerm}"`
                : 'There are no bookings to display'}
            </p>
          </div>
        ) : (
          <div className={s.tableWrapper}>
            <table className={s.dataTable}>
              <thead>
                <tr>
                  <th><i className="fa fa-hashtag" />Booking ID</th>
                  <th><i className="fa fa-user" />Customer</th>
                  <th><i className="fa fa-map-signs" />Route</th>
                  <th><i className="fa fa-clock-o" />Timing</th>
                  <th><i className="fa fa-truck" />Vehicle</th>
                  <th><i className="fa fa-rupee" />Amount</th>
                  <th><i className="fa fa-info-circle" />State</th>
                  <th><i className="fa fa-cog" />Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => {
                  const isOnHoldActive = Boolean(booking.on_hold) && !booking.trip_state;
                  const { pickupCount, dropCount } = getBookingStopCounts(booking);
                  const showMultiStopSummary = pickupCount > 1 || dropCount > 1;
                  const dropAddresses = getBookingDropAddresses(booking);
                  const routeStops = getBookingRouteStops(booking);
                  const mapsUrl = buildGoogleMapsRouteUrl(routeStops);
                  const mapsLabel = getMapsRouteLabel(routeStops, dropAddresses.length);

                  const isExpanded = expandedBookingId === booking.id;

                  return (
                  <React.Fragment key={booking.id}>
                  <tr
                    onClick={() => {
                      setExpandedBookingId(isExpanded ? null : booking.id);
                    }}
                    style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                    className={`${isExpanded ? s.expandedRow : ''} ${isFlexBooking(booking) ? s.flexRow : ''}`}
                    aria-expanded={isExpanded}
                  >
                    <td>
                      <div 
                        className={s.refCell} 
                        style={{ flexDirection: 'column', alignItems: 'flex-start' }}
                        title="Click row to show remarks and key details"
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i
                            className={`fa fa-chevron-right ${s.rowExpandChevron} ${isExpanded ? s.rowExpandChevronOpen : ''}`}
                            aria-hidden="true"
                          />
                          <span className={s.idBadge}>{booking.id}</span>
                          {booking.source === 'b2b' && (
                            <span className={s.b2bBadge} title="B2B Booking">
                              <i className="fa fa-star" /> B2B
                            </span>
                          )}
                        </div>
                        {isHourlyBooking(booking) && (
                          <span className={s.hourlyBadge}>
                            <i className="fa fa-clock-o" />
                            Hourly
                          </span>
                        )}
                        {isPartBooking(booking) && (
                          <span className={s.partLoadBadge}>
                            <i className="fa fa-cubes" />
                            Part Load
                          </span>
                        )}
                        {isFlexBooking(booking) && (
                          <span className={s.flexBadge}>
                            <i className="fa fa-clock-o" />
                            FLEX
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={s.customerCell}>
                        <span
                          className={s.customerName}
                          onClick={(e) => { e.stopPropagation(); handleCustomerClick(booking); }}
                          style={
                            booking.customer_id
                              ? { cursor: 'pointer', color: '#2563eb', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '2px' }
                              : undefined
                          }
                          title={
                            booking.customer_id
                              ? `View details for ${booking.customer_name || 'customer'}`
                              : undefined
                          }
                        >
                          {booking.customer_name}
                        </span>
                        {booking.customer_phone && (
                          <span className={s.customerPhone}>
                            <i className="fa fa-phone" />
                            {booking.customer_phone}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={s.routeCell}>
                      {showMultiStopSummary && (
                        <div className={s.routeStopSummary}>
                          P{pickupCount} / D{dropCount}
                        </div>
                      )}
                      {isHourlyBooking(booking) ? (
                        <div className={s.routeItem}>
                          <div className={s.routeIconPickup}>
                            <i className="fa fa-map-pin" />
                          </div>
                          <div className={s.routeText} title={booking.pickup_address}>
                            <span className={s.jobSiteLabel}>Job Site:</span> {booking.pickup_address || 'N/A'}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className={s.routeItem}>
                            <div className={s.routeIconPickup}>
                              <i className="fa fa-circle" />
                            </div>
                            <div className={s.routeText} title={booking.pickup_address}>
                              {booking.pickup_address || 'N/A'}
                            </div>
                          </div>
                          {dropAddresses.map((addr, idx) => (
                            <div key={idx} className={s.routeItem}>
                              <div className={s.routeIconDrop}>
                                <i className="fa fa-map-marker" />
                              </div>
                              <div className={s.routeText} title={addr}>
                                {addr}
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={s.routeMapsLink}
                          title={mapsLabel}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fa fa-external-link" aria-hidden="true" />
                          {mapsLabel}
                        </a>
                      )}
                    </td>
                    <td>
                      <div className={s.timeCell}>
                        {isFlexBooking(booking) ? (
                          <>
                            <div className={s.mainTime}>
                              <i className="fa fa-clock-o" />
                              {booking.pickup_time ? new Date(booking.pickup_time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                              {booking.delivery_deadline && (
                                <> - {new Date(booking.delivery_deadline).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                              )}
                            </div>
                            <div className={s.subTime}>
                              Created: {formatDateTime(booking.created_at)}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={s.mainTime}>
                              <i className="fa fa-clock-o" />
                              {formatDateTime(booking.pickup_time)}
                            </div>
                            <div className={s.subTime}>
                              Created: {formatDateTime(booking.created_at)}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {isHourlyBooking(booking) ? (
                        <div className={s.equipmentBadgeWrap}>
                          <span className={s.equipmentBadge}>
                            <i className="fa fa-cogs" />
                            Class {booking.vehicle_class_id}
                          </span>
                          {booking.duration > 0 && (
                            <span className={s.durationBadge}>
                              <i className="fa fa-clock-o" />
                              {formatDurationHours(booking.duration)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={s.vehicleBadge}>
                          <i className="fa fa-truck" />
                          Class {booking.vehicle_class_id}
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div
                        className={`${s.amountCell} ${s[getAmountStatus(booking)]}`}
                        onClick={(e) => {
                          if (['draft', 'deleted'].includes(normalizeState(booking.state))) return;
                          e.stopPropagation();
                          handleAmountClick(booking);
                        }}
                        style={{
                          cursor: ['cancelled', 'estimate', 'draft', 'deleted'].includes(getAmountStatus(booking)) || ['draft', 'deleted'].includes(normalizeState(booking.state)) ? 'default' : 'pointer'
                        }}
                        title={getAmountStatus(booking) === 'estimate' ? 'Estimate only - waiting for final amount' : ''}
                      >
                        <div className={s.mainAmount}>
                          {getAmountStatus(booking) === 'paid' && <i className="fa fa-check-circle" style={{ marginRight: '4px' }} />}
                          {getAmountStatus(booking) === 'partial' && <i className="fa fa-adjust" style={{ marginRight: '4px' }} />}
                          {getAmountStatus(booking) === 'unpaid' && <i className="fa fa-exclamation-circle" style={{ marginRight: '4px' }} />}
                          {getAmountStatus(booking) === 'estimate' && <i className="fa fa-info-circle" style={{ marginRight: '4px' }} />}
                          ₹{getBookingTotal(booking).toFixed(2)}
                        </div>
                        {getAmountStatus(booking) === 'partial' && (
                          <div className={s.tollInfo}>
                            Paid ₹{Number(booking.amount_paid || 0).toFixed(2)} · Due ₹{getBookingOutstanding(booking).toFixed(2)}
                          </div>
                        )}
                        {booking.toll_charge > 0 && (
                          <div className={s.tollInfo}>
                            (incl. ₹{Number(booking.toll_charge).toFixed(2)} toll)
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className={s.stateCell}>
                        <span className={`${s.statusBadge} ${s[normalizeState(booking.state)]}`}>
                          {formatStateDisplay(booking.state)}
                        </span>
                        {isOnHoldActive && (
                          <span className={s.onHoldBadge}>
                            <i className="fa fa-pause" />
                            On Hold
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={s.actionsCell}>
                      <div className={s.actionButtons}>
                        {normalizeState(booking.state) === 'draft' && (
                          <>
                            <button
                              className={s.modifyBtn}
                              onClick={() => handleEditDraft(booking)}
                              title="Edit Draft"
                              aria-label="Edit Draft"
                              disabled={draftActionLoading === booking.id}
                            >
                              <i className="fa fa-edit" />
                              <span className={s.actionLabel}>Edit</span>
                            </button>
                            <button
                              className={s.assignBtn}
                              onClick={() => handleOpenDraftSuggestions(booking)}
                              title="Driver Suggestions"
                              aria-label="Driver Suggestions"
                              disabled={draftActionLoading === booking.id}
                            >
                              <i className="fa fa-truck" />
                              <span className={s.actionLabel}>Suggest</span>
                            </button>
                            <button
                              className={s.assignBtn}
                              onClick={() => handleConfirmDraft(booking)}
                              title="Confirm Booking"
                              aria-label="Confirm Booking"
                              disabled={draftActionLoading === booking.id}
                            >
                              {draftActionLoading === booking.id ? (
                                <i className="fa fa-spinner fa-spin" />
                              ) : (
                                <i className="fa fa-check" />
                              )}
                              <span className={s.actionLabel}>Confirm</span>
                            </button>
                            <button
                              className={s.cancelBtn}
                              onClick={() => handleDeleteDraft(booking)}
                              title="Delete Draft"
                              aria-label="Delete Draft"
                              disabled={draftActionLoading === booking.id}
                            >
                              <i className="fa fa-trash" />
                              <span className={s.actionLabel}>Delete</span>
                            </button>
                          </>
                        )}
                        {normalizeState(booking.state) === 'deleted' && (
                          <button
                            className={s.modifyBtn}
                            onClick={(e) => { e.stopPropagation(); handleRestoreDraft(booking); }}
                            title="Restore Draft"
                            aria-label="Restore Draft"
                            disabled={draftActionLoading === booking.id}
                          >
                            {draftActionLoading === booking.id ? (
                              <i className="fa fa-spinner fa-spin" />
                            ) : (
                              <i className="fa fa-undo" />
                            )}
                            <span className={s.actionLabel}>Restore</span>
                          </button>
                        )}
                        {normalizeState(booking.state) !== 'draft' && normalizeState(booking.state) !== 'deleted' && (
                          <>
                            {modifiableBookings[booking.id] &&
                             normalizeState(booking.state) !== 'cancelled' &&
                             normalizeState(booking.state) !== 'completed' && (
                              <button
                                className={s.modifyBtn}
                                onClick={() => handleModifyBooking(booking)}
                                title="Modify Booking"
                                aria-label="Modify Booking"
                              >
                                <i className="fa fa-edit" />
                                <span className={s.actionLabel}>Modify</span>
                              </button>
                            )}
                            {modifiableBookings[booking.id] &&
                             normalizeState(booking.state) !== 'cancelled' &&
                             normalizeState(booking.state) !== 'completed' && (
                              <button
                                className={s.assignBtn}
                                onClick={() => handleAssignVehicle(booking)}
                                title={assigningBookingId === booking.id ? 'Assigning Vehicle' : 'Assign Vehicle'}
                                aria-label="Assign Vehicle"
                                disabled={assigningBookingId === booking.id}
                              >
                                {assigningBookingId === booking.id ? (
                                  <i className="fa fa-spinner fa-spin" />
                                ) : (
                                  <i className="fa fa-car" />
                                )}
                                <span className={s.actionLabel}>{assigningBookingId === booking.id ? 'Assigning' : 'Assign'}</span>
                              </button>
                            )}
                            {canToggleOnHold(booking) && (
                              <button
                                className={`${s.onHoldBtn} ${isOnHoldActive ? s.activeHold : ''}`}
                                onClick={() => handleToggleOnHold(booking)}
                                title={onHoldUpdatingId === booking.id ? 'Updating On Hold' : (isOnHoldActive ? 'Remove On Hold' : 'Put On Hold')}
                                aria-label={isOnHoldActive ? 'Remove On Hold' : 'Put On Hold'}
                                disabled={onHoldUpdatingId === booking.id}
                              >
                                {onHoldUpdatingId === booking.id ? (
                                  <i className="fa fa-spinner fa-spin" />
                                ) : isOnHoldActive ? (
                                  <i className="fa fa-play" />
                                ) : (
                                  <i className="fa fa-pause" />
                                )}
                                <span className={s.actionLabel}>{isOnHoldActive ? 'Unhold' : 'Hold'}</span>
                              </button>
                            )}
                            {!modifiableBookings[booking.id] &&
                             normalizeState(booking.state) !== 'cancelled' &&
                             normalizeState(booking.state) !== 'completed' && (
                              <span className={s.notModifiable} title="Trip has already started - Booking locked">
                                <i className="fa fa-lock" />
                              </span>
                            )}
                            {normalizeState(booking.state) !== 'cancelled' &&
                             normalizeState(booking.state) !== 'completed' && (
                              <button
                                className={s.otherPartyBtn}
                                onClick={() => openOtherPartyModal(booking)}
                                title="Other Party Details"
                                aria-label="Other Party Details"
                              >
                                <i className="fa fa-phone" />
                                <span className={s.actionLabel}>Other Party</span>
                              </button>
                            )}
                            {booking.payment_id && (
                              <button
                                className={s.invoiceBtn}
                                onClick={(e) => { e.stopPropagation(); openInvoiceModal(booking); }}
                                title="Download / Send Invoice"
                                aria-label="Download / Send Invoice"
                              >
                                <i className="fa fa-file-text-o" />
                                <span className={s.actionLabel}>Invoice</span>
                              </button>
                            )}
                            {modifiableBookings[booking.id] && (
                              <button
                                className={s.cancelBtn}
                                onClick={() => { setCancelModal(booking); setCancelReason(''); }}
                                title="Cancel Booking"
                                aria-label="Cancel Booking"
                              >
                                <i className="fa fa-times" />
                                <span className={s.actionLabel}>Cancel</span>
                              </button>
                            )}
                            {normalizeState(booking.state) === 'completed' && canLogDriverCalls(booking) && (
                              <button
                                className={s.assignBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenBookingOutreach(booking);
                                }}
                                title="Log driver calls"
                                aria-label="Log driver calls"
                              >
                                <i className="fa fa-car" />
                                <span className={s.actionLabel}>Log call</span>
                              </button>
                            )}
                          </>

                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className={s.bookingExpandRow}>
                      <td colSpan={8} className={s.bookingExpandCell}>
                        <BookingExpandPanel
                          booking={booking}
                          isDraft={filter === 'draft'}
                          onLogDriverCalls={
                            canLogDriverCalls(booking) ? handleOpenBookingOutreach : null
                          }
                          onMaterialUpdated={handleMaterialUpdated}
                          onRemarksUpdated={handleRemarksUpdated}
                          onStateUpdated={handleStateUpdated}
                          onBookingTypeUpdated={handleBookingTypeUpdated}
                          onOpenCustomerPreview={handleOpenCustomerPreview}
                          notifStatus={notifStatusByBooking[booking.id]}
                          onFetchNotifStatus={fetchNotifStatus}
                        />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                )})}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination Controls */}
        {!loading && !error && bookings.length > 0 && (
          <div className={s.pagination}>
            <button
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className={s.pageBtn}
            >
              <i className="fa fa-chevron-left" /> Previous
            </button>
            <span className={s.pageInfo}>
              Page
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
                onBlur={handlePageInputBlur}
                className={s.pageInput}
              />
              of {totalPages}
            </span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className={s.pageBtn}
            >
              Next <i className="fa fa-chevron-right" />
            </button>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModal && (
        <div className={s.modalOverlay} onClick={() => setPaymentModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            {paymentModal.type === 'paid' ? (
              <>
                <div className={s.modalHeader}>
                  <i className="fa fa-check-circle" style={{ color: '#10b981', fontSize: '24px' }} />
                  <h3>Payment Received</h3>
                </div>
                <div className={s.modalBody}>
                  <p className={s.modalLabel}>Booking ID:</p>
                  <p className={s.modalValue}>{paymentModal.booking.id}</p>
                  
                  <p className={s.modalLabel}>Amount Paid:</p>
                  <p className={s.modalValue} style={{ color: '#10b981', fontWeight: 'bold', fontSize: '20px' }}>
                    ₹{paymentModal.booking.payment_estimate?.toFixed(2)}
                  </p>
                  
                  <p className={s.modalLabel}>Payment ID:</p>
                  <p className={s.modalValue}>{paymentModal.booking.payment_id}</p>
                  
                  {paymentModal.booking.paid_at && (
                    <>
                      <p className={s.modalLabel}>Paid On:</p>
                      <p className={s.modalValue}>{formatDateTime(paymentModal.booking.paid_at)}</p>
                    </>
                  )}
                </div>
                <div className={s.modalFooter}>
                  <button className={s.modalCloseBtn} onClick={() => setPaymentModal(null)}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={s.modalHeader}>
                  <i className="fa fa-credit-card" style={{ color: '#3b82f6', fontSize: '24px' }} />
                  <h3>Collect Payment</h3>
                </div>
                <div className={s.modalBody}>
                  <p className={s.modalLabel}>Booking ID:</p>
                  <p className={s.modalValue}>{paymentModal.booking.id}</p>
                  
                  <p className={s.modalLabel}>Customer:</p>
                  <p className={s.modalValue}>{paymentModal.booking.customer_name}</p>
                  <p className={s.modalValue} style={{ fontSize: '13px', color: '#6b7280' }}>
                    <i className="fa fa-phone" /> {paymentModal.booking.customer_phone}
                  </p>
                  
                  <p className={s.modalLabel}>Total amount:</p>
                  <p className={s.modalValue} style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '20px' }}>
                    ₹{getBookingTotal(paymentModal.booking).toFixed(2)}
                  </p>
                  {Number(paymentModal.booking.amount_paid || 0) > 0 && (
                    <>
                      <p className={s.modalLabel}>Paid so far:</p>
                      <p className={s.modalValue}>₹{Number(paymentModal.booking.amount_paid || 0).toFixed(2)}</p>
                      <p className={s.modalLabel}>Outstanding:</p>
                      <p className={s.modalValue} style={{ fontWeight: 'bold' }}>
                        ₹{getBookingOutstanding(paymentModal.booking).toFixed(2)}
                      </p>
                    </>
                  )}
                  <div style={{ marginTop: '12px' }}>
                    <p className={s.modalLabel}>Update total amount:</p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={paymentAmountInput}
                        onChange={(e) => setPaymentAmountInput(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '14px'
                        }}
                      />
                      <button
                        className={s.modalConfirmBtn}
                        onClick={() => handleUpdateAmount(paymentModal.booking)}
                        disabled={amountUpdatingId === paymentModal.booking.id}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {amountUpdatingId === paymentModal.booking.id ? (
                          <>
                            <i className="fa fa-spinner fa-spin" />
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-pencil" />
                            Update
                          </>
                        )}
                      </button>
                    </div>
                    {amountUpdateError && (
                      <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{amountUpdateError}</p>
                    )}
                  </div>

                  {Number(paymentModal.booking.amount_paid || 0) <= 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <p className={s.modalLabel}>Collect now (first installment):</p>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          value={collectNowInput}
                          onChange={(e) => setCollectNowInput(e.target.value)}
                          placeholder={`Full amount (₹${getBookingTotal(paymentModal.booking).toFixed(2)})`}
                          style={{
                            flex: 1,
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            fontSize: '14px'
                          }}
                        />
                        <button
                          className={s.modalConfirmBtn}
                          onClick={() => handleSavePaymentPlan(paymentModal.booking)}
                          disabled={planSavingId === paymentModal.booking.id}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {planSavingId === paymentModal.booking.id ? (
                            <>
                              <i className="fa fa-spinner fa-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save plan'
                          )}
                        </button>
                      </div>
                      <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '6px' }}>
                        Remaining after first: ₹{(
                          collectNowInput.trim()
                            ? Math.max(0, getBookingTotal(paymentModal.booking) - Number(collectNowInput || 0))
                            : getBookingTotal(paymentModal.booking)
                        ).toFixed(2)}
                      </p>
                      {planError && (
                        <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{planError}</p>
                      )}
                    </div>
                  )}

                  <div style={{ marginTop: '14px', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px' }}>
                    <p className={s.modalLabel}>Next payment link amount:</p>
                    <p className={s.modalValue} style={{ fontWeight: 'bold' }}>
                      ₹{resolvePaymentSendAmount(paymentModal.booking).toFixed(2)}
                    </p>
                  </div>

                  <div style={{ marginTop: '14px' }}>
                    <p className={s.modalLabel}>Payment request status:</p>
                    {(() => {
                      const instrument = String(paymentModal.booking.payment_instrument_type || '').toUpperCase();
                      const status = String(paymentModal.booking.payment_link_status || '').toUpperCase();
                      const isQr = instrument === 'QR_CODE';
                      const isManual = instrument === 'MANUAL';
                      const instrumentLabel = isQr ? 'QR code' : isManual ? 'manual payment' : 'payment link';
                      if (!paymentModal.booking.payment_link_sent) {
                        return (
                          <p className={s.modalValue} style={{ fontSize: '13px', color: '#6b7280' }}>
                            No payment link or QR has been sent yet.
                          </p>
                        );
                      }
                      return (
                        <div style={{ fontSize: '13px', color: '#374151' }}>
                          <div>
                            <i
                              className={`fa ${status === 'ACTIVE' ? 'fa-check-circle' : 'fa-info-circle'}`}
                              style={{ color: status === 'ACTIVE' ? '#2563eb' : '#6b7280', marginRight: '6px' }}
                            />
                            {status === 'ACTIVE'
                              ? `Active ${instrumentLabel}`
                              : `Last ${instrumentLabel}: ${paymentModal.booking.payment_link_status || 'unknown'}`}
                          </div>
                          {paymentModal.booking.payment_link_sent_at && (
                            <div style={{ marginTop: '4px' }}>
                              {status === 'ACTIVE' ? 'Sent at' : 'Last updated'}: {formatDateTime(paymentModal.booking.payment_link_sent_at)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {getAmountStatus(paymentModal.booking) === 'estimate' && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      background: '#fef3c7',
                      border: '1px solid #fcd34d',
                      borderRadius: '8px',
                      color: '#92400e',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      <i className="fa fa-info-circle" style={{ marginRight: '6px' }} />
                      This is an estimate. Payment cannot be collected until the final amount is available.
                    </div>
                  )}
                  
                  {getAmountStatus(paymentModal.booking) !== 'estimate' && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      border: '1px solid #ddd6fe',
                      background: '#f5f3ff',
                      borderRadius: '8px'
                    }}>
                      <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                        Send payment request to
                      </p>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <input
                            type="radio"
                            name="paymentRecipient"
                            checked={paymentRecipient === 'customer'}
                            onChange={() => {
                              setPaymentRecipient('customer');
                              setPaymentRecipientError('');
                              setPaymentRecipientNotice('');
                            }}
                          />
                          Customer ({formatPhoneForDisplay(paymentModal.booking.customer_phone)})
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <input
                            type="radio"
                            name="paymentRecipient"
                            checked={paymentRecipient === 'other_party'}
                            onChange={() => {
                              setPaymentRecipient('other_party');
                              setPaymentRecipientError('');
                              setPaymentRecipientNotice('');
                            }}
                          />
                          Other party
                        </label>
                      </div>

                      {paymentRecipient === 'other_party' && (
                        <div style={{ marginTop: '10px' }}>
                          <OtherPartySuggestions
                            booking={paymentModal.booking}
                            selectedPhone={paymentOtherPartyPhone}
                            onPick={(phone) => {
                              setPaymentOtherPartyPhone(phone);
                              setPaymentRecipientError('');
                              setPaymentRecipientNotice('');
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="tel"
                              value={paymentOtherPartyPhone}
                              onChange={(e) => {
                                setPaymentOtherPartyPhone(e.target.value);
                                setPaymentRecipientError('');
                                setPaymentRecipientNotice('');
                              }}
                              onBlur={() => setPaymentOtherPartyPhone((prev) => normalizeIndianPhone(prev))}
                              placeholder="Other party phone number"
                              style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '10px 12px',
                                borderRadius: '8px',
                                border: '1px solid #c4b5fd',
                                fontSize: '14px',
                                boxSizing: 'border-box'
                              }}
                            />
                            <button
                              type="button"
                              className={s.modalConfirmBtn}
                              onClick={() => handleSavePaymentOtherParty(paymentModal.booking)}
                              disabled={
                                paymentRecipientSavingId === paymentModal.booking.id ||
                                actionBookingId === paymentModal.booking.id
                              }
                              style={{ background: '#7c3aed', whiteSpace: 'nowrap' }}
                            >
                              {paymentRecipientSavingId === paymentModal.booking.id ? (
                                <>
                                  <i className="fa fa-spinner fa-spin" /> Saving...
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-save" /> Save for Later
                                </>
                              )}
                            </button>
                          </div>
                          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                            Save without sending now, or use the buttons below to save and send immediately.
                          </p>
                        </div>
                      )}

                      {paymentRecipientError && (
                        <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '8px' }}>
                          {paymentRecipientError}
                        </p>
                      )}
                      {paymentRecipientNotice && (
                        <p style={{ color: '#047857', fontSize: '12px', marginTop: '8px' }}>
                          <i className="fa fa-check-circle" /> {paymentRecipientNotice}
                        </p>
                      )}
                    </div>
                  )}

                  {getAmountStatus(paymentModal.booking) !== 'estimate' && (
                    <div style={{
                      marginTop: '14px',
                      padding: '12px',
                      border: '1px solid #bbf7d0',
                      background: '#f0fdf4',
                      borderRadius: '8px'
                    }}>
                      <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                        Record Manual Payment (Cash / Other Provider)
                      </p>

                      <div style={{ display: 'flex', gap: '14px', marginBottom: '8px', alignItems: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <input
                            type="radio"
                            name="manualBookingPaymentMode"
                            checked={manualPaymentMode === 'CASH'}
                            onChange={() => setManualPaymentMode('CASH')}
                          />
                          Cash
                        </label>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                          <input
                            type="radio"
                            name="manualBookingPaymentMode"
                            checked={manualPaymentMode === 'OTHER'}
                            onChange={() => setManualPaymentMode('OTHER')}
                          />
                          Other Provider
                        </label>
                      </div>

                      {manualPaymentMode === 'OTHER' && (
                        <input
                          type="text"
                          placeholder="Provider name (e.g. UPI_DIRECT, BANK_TRANSFER)"
                          value={manualOtherProvider}
                          onChange={(e) => setManualOtherProvider(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '9px 11px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px',
                            marginBottom: '8px',
                            boxSizing: 'border-box'
                          }}
                        />
                      )}

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <input
                          type="datetime-local"
                          value={manualTxnTime}
                          onChange={(e) => setManualTxnTime(e.target.value)}
                          style={{
                            padding: '9px 11px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px'
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Txn/Gateway ID (optional)"
                          value={manualGatewayTxnId}
                          onChange={(e) => setManualGatewayTxnId(e.target.value)}
                          style={{
                            padding: '9px 11px',
                            borderRadius: '8px',
                            border: '1px solid #d1d5db',
                            fontSize: '13px'
                          }}
                        />
                      </div>

                      <textarea
                        rows={2}
                        placeholder="Notes (optional)"
                        value={manualPayNotes}
                        onChange={(e) => setManualPayNotes(e.target.value)}
                        style={{
                          width: '100%',
                          marginTop: '8px',
                          padding: '9px 11px',
                          borderRadius: '8px',
                          border: '1px solid #d1d5db',
                          fontSize: '13px',
                          boxSizing: 'border-box'
                        }}
                      />

                      {manualPayError && (
                        <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px' }}>{manualPayError}</p>
                      )}
                    </div>
                  )}
                </div>
                <div className={s.modalFooter}>
                  <button className={s.modalCancelBtn} onClick={() => setPaymentModal(null)}>
                    Close
                  </button>
                  <button
                    className={s.modalConfirmBtn}
                    onClick={() => handleRecordManualPayment(paymentModal.booking)}
                    disabled={manualPaySavingId === paymentModal.booking.id || getAmountStatus(paymentModal.booking) === 'estimate'}
                    style={{
                      background: '#059669',
                      opacity: getAmountStatus(paymentModal.booking) === 'estimate' ? 0.5 : 1,
                      cursor: getAmountStatus(paymentModal.booking) === 'estimate' ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {manualPaySavingId === paymentModal.booking.id ? (
                      <>
                        <i className="fa fa-spinner fa-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-money" />
                        Mark Paid (Manual)
                      </>
                    )}
                  </button>
                  {normalizeState(paymentModal.booking.state) !== 'draft' &&
                    normalizeState(paymentModal.booking.state) !== 'deleted' && (
                    <>
                      <button
                        className={s.modalConfirmBtn}
                        onClick={() => handleGeneratePaymentLink(paymentModal.booking, paymentRecipient, 'link')}
                        disabled={
                          !!actionBookingId ||
                          !!paymentRecipientSavingId ||
                          getAmountStatus(paymentModal.booking) === 'estimate'
                        }
                        style={{
                          background: paymentRecipient === 'other_party' ? '#7c3aed' : undefined,
                          opacity: getAmountStatus(paymentModal.booking) === 'estimate' ? 0.5 : 1,
                          cursor: getAmountStatus(paymentModal.booking) === 'estimate' ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {actionBookingId === paymentModal.booking.id ? (
                          <>
                            <i className="fa fa-spinner fa-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-send" />
                            {paymentRecipient === 'other_party' ? 'Send Link to Other Party' : 'Send Payment Link'}
                          </>
                        )}
                      </button>
                      <button
                        className={s.modalConfirmBtn}
                        onClick={() => handleGeneratePaymentLink(paymentModal.booking, paymentRecipient, 'qr')}
                        disabled={
                          !!actionBookingId ||
                          !!paymentRecipientSavingId ||
                          getAmountStatus(paymentModal.booking) === 'estimate'
                        }
                        style={{
                          background: '#0f766e',
                          opacity: getAmountStatus(paymentModal.booking) === 'estimate' ? 0.5 : 1,
                          cursor: getAmountStatus(paymentModal.booking) === 'estimate' ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {actionBookingId === paymentModal.booking.id ? (
                          <>
                            <i className="fa fa-spinner fa-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-qrcode" />
                            {paymentRecipient === 'other_party' ? 'Send QR to Other Party' : 'Send QR'}
                          </>
                        )}
                      </button>
                      {String(paymentModal.booking.payment_link_status || '').toUpperCase() === 'ACTIVE' && (
                        <button
                          className={s.modalCancelBtn}
                          onClick={() => handleCancelActivePayment(paymentModal.booking)}
                          disabled={!!actionBookingId}
                          style={{ borderColor: '#dc2626', color: '#dc2626' }}
                          title="Cancel/close the active payment link or QR"
                        >
                          <i className="fa fa-ban" />
                          {String(paymentModal.booking.payment_instrument_type || '').toUpperCase() === 'QR_CODE'
                            ? 'Cancel Active QR'
                            : 'Cancel Active Link'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Other Party Modal */}
      {otherPartyModal && (
        <div className={s.modalOverlay} onClick={() => setOtherPartyModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-phone" style={{ color: '#6366f1', fontSize: '24px' }} />
              <h3>Other Party Details</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking ID:</p>
              <p className={s.modalValue}>{otherPartyModal.id}</p>

              <p className={s.modalLabel}>Other Party Phone Number:</p>
              <OtherPartySuggestions
                booking={otherPartyModal}
                selectedPhone={otherPartyPhoneInput}
                onPick={(phone) => {
                  setOtherPartyPhoneInput(phone);
                  setOtherPartyUpdateError('');
                }}
              />
              <input
                type="tel"
                value={otherPartyPhoneInput}
                onChange={(e) => setOtherPartyPhoneInput(e.target.value)}
                onBlur={() => setOtherPartyPhoneInput((prev) => normalizeIndianPhone(prev))}
                placeholder="Enter phone number"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  marginBottom: '12px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '-4px' }}>
                10-digit numbers will be prefixed with 91 automatically.
              </p>

              <p className={s.modalLabel}>Does other party pay?</p>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginTop: '6px' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="otherPartyPays"
                    checked={otherPartyPaysInput === true}
                    onChange={() => setOtherPartyPaysInput(true)}
                  />
                  Yes
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                  <input
                    type="radio"
                    name="otherPartyPays"
                    checked={otherPartyPaysInput === false}
                    onChange={() => setOtherPartyPaysInput(false)}
                  />
                  No
                </label>
              </div>

              {otherPartyUpdateError && (
                <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '10px' }}>
                  {otherPartyUpdateError}
                </p>
              )}
            </div>
            <div className={s.modalFooter}>
              <button className={s.modalCancelBtn} onClick={() => setOtherPartyModal(null)}>
                Cancel
              </button>
              <button
                className={s.modalConfirmBtn}
                onClick={handleUpdateOtherParty}
                disabled={otherPartyUpdatingId === otherPartyModal.id}
              >
                {otherPartyUpdatingId === otherPartyModal.id ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Vehicle ID assignment */}
      {vehicleAssignModal && (
        <div className={s.modalOverlay} onClick={closeVehicleAssignModal}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-truck" style={{ color: '#7c3aed', fontSize: '24px' }} />
              <h3>{isDraftBooking(vehicleAssignModal) ? 'Driver Suggestions' : 'Assign Vehicle'}</h3>
            </div>
            <div className={s.modalBody}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                  <div>
                    <p className={s.modalLabel}>
                      {isDraftBooking(vehicleAssignModal) ? 'Draft ID:' : 'Booking ID:'}
                    </p>
                    <p className={s.modalValue}>{vehicleAssignModal.id ?? vehicleAssignModal.booking_id}</p>
                  </div>
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}
                    onClick={() => setBookingCallsOpen(true)}
                  >
                    <i className="fa fa-microphone" style={{ marginRight: '6px' }} />
                    AI calls
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      background: showFreeNearby ? '#dcfce7' : undefined,
                      borderColor: showFreeNearby ? '#86efac' : undefined,
                    }}
                    onClick={handleToggleFreeNearby}
                    disabled={freeNearbyLoading}
                    title="Show all free drivers within 12 km of pickup (same or one-higher class, not on a trip)"
                  >
                    <i className="fa fa-location-arrow" style={{ marginRight: '6px' }} />
                    {showFreeNearby ? 'Hide free nearby' : 'Free drivers \u2264 12 km'}
                  </button>
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      background: showFreeNearDrop ? '#e0f2fe' : undefined,
                      borderColor: showFreeNearDrop ? '#7dd3fc' : undefined,
                    }}
                    onClick={handleToggleFreeNearDrop}
                    disabled={freeNearDropLoading}
                    title="Show free + loyalty drivers within 5 km of drop (use when pickup has no drivers)"
                  >
                    <i className="fa fa-map-marker" style={{ marginRight: '6px' }} />
                    {showFreeNearDrop ? 'Hide free in drop' : 'Free in drop \u2264 5 km'}
                  </button>
                  {/* Call Queue toggle */}
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      background: callQueueMode ? '#faf5ff' : undefined,
                      borderColor: callQueueMode ? '#c084fc' : undefined,
                      color: callQueueMode ? '#7c3aed' : undefined,
                    }}
                    onClick={toggleCallQueueMode}
                    title="Select multiple drivers to call one after another"
                  >
                    <i className="fa fa-list-ol" style={{ marginRight: '6px' }} />
                    Call Queue
                  </button>
                  {/* Assign Transporter — only for non-draft, non-hourly bookings */}
                  {!isDraftBooking(vehicleAssignModal) && !isHourlyBooking(vehicleAssignModal) && (
                    <button
                      type="button"
                      className={s.modalCancelBtn}
                      style={{
                        padding: '6px 12px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        background: '#fffbeb',
                        borderColor: '#fbbf24',
                        color: '#92400e',
                      }}
                      onClick={() => openTransporterAssignModal(vehicleAssignModal)}
                      title="Assign this booking to a transporter agency before the vehicle is known"
                    >
                      <i className="fa fa-building" style={{ marginRight: '6px' }} />
                      Assign Transporter
                    </button>
                  )}
                </div>
                {/* Queue bar — shown only when queue mode is active */}
                {callQueueMode && (() => {
                  const allDrivers = [
                    ...freeNearbySuggestions,
                    ...mlSuggestions,
                    ...freeNearDropSuggestions,
                    ...dropLoyaltySuggestions,
                  ].filter((d, i, arr) => d.driver_phone && arr.findIndex(x => x.vehicle_id === d.vehicle_id) === i);
                  const selectedDrivers = allDrivers.filter(d => callQueueSelected.has(`${d.vehicle_id}:${d.driver_phone}`));
                  return (
                    <div style={{
                      marginTop: 8,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: '#faf5ff',
                      border: '1px solid #c084fc',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                    }}>
                      <i className="fa fa-list-ol" style={{ color: '#7c3aed' }} />
                      <span style={{ fontSize: 13, color: '#6d28d9', flex: 1 }}>
                        {selectedDrivers.length === 0
                          ? 'Tick drivers below to add to queue'
                          : `${selectedDrivers.length} driver${selectedDrivers.length > 1 ? 's' : ''} selected`}
                      </span>
                      {selectedDrivers.length > 0 && (
                        <button
                          type="button"
                          className={s.modalConfirmBtn}
                          style={{ padding: '5px 14px', fontSize: '12px', whiteSpace: 'nowrap' }}
                          disabled={callQueueRunning}
                          onClick={() => handleCallQueue(selectedDrivers)}
                        >
                          {callQueueRunning
                            ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />Queuing…</>
                            : <><i className="fa fa-microphone" style={{ marginRight: 6 }} />Call {selectedDrivers.length} driver{selectedDrivers.length > 1 ? 's' : ''}</>}
                        </button>
                      )}
                      <button
                        type="button"
                        className={s.modalCancelBtn}
                        style={{ padding: '5px 10px', fontSize: '12px' }}
                        onClick={toggleCallQueueMode}
                      >
                        Cancel
                      </button>
                    </div>
                  );
                })()}
              </div>

              {voiceCallNotice && (() => {
                const palette = {
                  success: { bg: '#ecfdf5', border: '#6ee7b7', text: '#065f46', icon: 'fa-phone' },
                  info:    { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', icon: 'fa-clock-o' },
                  warn:    { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', icon: 'fa-exclamation-circle' },
                  error:   { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', icon: 'fa-times-circle' },
                }[voiceCallNotice.tone] || {
                  bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', icon: 'fa-info-circle',
                };
                return (
                  <div
                    ref={voiceCallNoticeRef}
                    style={{
                      marginTop: '12px',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      background: palette.bg,
                      border: `1px solid ${palette.border}`,
                      color: palette.text,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                    }}
                  >
                    <i className={`fa ${palette.icon}`} style={{ marginTop: '2px' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>{voiceCallNotice.title}</div>
                      {voiceCallNotice.detail && (
                        <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.9 }}>
                          {voiceCallNotice.detail}
                        </div>
                      )}
                      {voiceCallNotice.tone !== 'error' && (
                        <button
                          type="button"
                          onClick={goToVoiceBotLogs}
                          style={{
                            marginTop: '8px',
                            padding: '5px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            background: '#7c3aed',
                            color: '#fff',
                          }}
                        >
                          <i className="fa fa-microphone" style={{ marginRight: '6px' }} />
                          Open live call logs
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setVoiceCallNotice(null)}
                      aria-label="Dismiss"
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: palette.text,
                        cursor: 'pointer',
                        fontSize: '14px',
                        lineHeight: 1,
                      }}
                    >
                      <i className="fa fa-times" />
                    </button>
                  </div>
                );
              })()}

              {showFreeNearby && (
                <div style={{ marginBottom: '16px', marginTop: '12px' }}>
                  <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                    Free drivers within 12 km (same / one-higher class, not on a trip)
                  </p>
                  {freeNearbyLoading ? (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>
                      <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} />
                      Loading free drivers...
                    </p>
                  ) : freeNearbySuggestions.length === 0 ? (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>
                      No free drivers within 12 km of pickup.
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {freeNearbySuggestions.map((driver) => {
                        const vehicleCalls = outreachForVehicle(
                          mlOutreachHistory,
                          driver.vehicle_id
                        );
                        const outreachSummary = summarizeOutreach(vehicleCalls);
                        return (
                        <div
                          key={`free-${driver.vehicle_id}`}
                          style={{
                            border: outreachSummary
                              ? outreachSummary.badgeStyle.border
                              : '1px solid #bbf7d0',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            background: outreachSummary ? outreachSummary.badgeStyle.background : '#f0fdf4',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                              {(driver.vehicle_photo_url || driver.url) ? (
                                <img
                                  src={driver.vehicle_photo_url || driver.url}
                                  alt="Vehicle"
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                    border: '2px solid #10b981',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    flexShrink: 0,
                                    marginTop: '2px'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewPhotoUrl(driver.vehicle_photo_url || driver.url);
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '18px',
                                  flexShrink: 0,
                                  marginTop: '2px'
                                }} title="No photo">🚛</div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <strong>
                                  #{driver.suggestion_rank} V-{driver.vehicle_id}
                                  {driver.driver_name ? ` · ${driver.driver_name}` : ''}
                                  {formatSuggestionVehicleMeta(driver)}
                                  {driver.is_transporter ? (
                                    <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                      transporter
                                    </span>
                                  ) : null}
                                </strong>
                                {driver.driver_phone ? (
                                  <div style={{ marginTop: '2px' }}>
                                    {(() => {
                                      const clean = String(driver.driver_phone).replace(/\D/g, '');
                                      const isCalling = callingDrivers[clean];
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => handlePlaceCall(driver.driver_phone)}
                                          disabled={isCalling}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: isCalling ? '#f59e0b' : '#2563eb',
                                            cursor: isCalling ? 'wait' : 'pointer',
                                            textDecoration: 'underline',
                                            whiteSpace: 'nowrap',
                                            padding: 0,
                                            fontFamily: 'inherit',
                                            fontSize: '12px',
                                          }}
                                        >
                                          {isCalling ? '\ud83d\udcde Calling...' : `\ud83d\udcde ${driver.driver_phone}`}
                                        </button>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                    No phone number
                                  </div>
                                )}
                                <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                                  {driver.reason || `${driver.distance_to_pickup_km} km away`}
                                </div>
                                <OutreachCallStatus calls={vehicleCalls} compact />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                              <button
                                type="button"
                                className={s.modalCancelBtn}
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() =>
                                  openDriverOutreachModal(driver.vehicle_id, driver.driver_name)
                                }
                              >
                                <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                                {outreachSummary
                                  ? `Log again (${outreachSummary.callCount}\u00d7)`
                                  : 'Log call'}
                              </button>
                              {/* AI Voice Call button / Queue checkbox */}
                              {driver.driver_phone && (() => {
                                const clean = String(driver.driver_phone).replace(/\D/g, '');
                                const isVoiceCalling = voiceCallingDrivers[clean];
                                const qKey = `${driver.vehicle_id}:${driver.driver_phone}`;
                                const isQueued = callQueueSelected.has(qKey);
                                if (callQueueMode) {
                                  return (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 600, padding: '4px 6px', border: `2px solid ${isQueued ? '#7c3aed' : '#c084fc'}`, borderRadius: 6, background: isQueued ? '#ede9fe' : '#faf5ff', userSelect: 'none' }}>
                                      <input type="checkbox" checked={isQueued} onChange={() => toggleCallQueueDriver(qKey)} style={{ accentColor: '#7c3aed' }} />
                                      Add to queue
                                    </label>
                                  );
                                }
                                return (
                                  <button
                                    type="button"
                                    className={s.modalCancelBtn}
                                    style={{ padding: '4px 10px', fontSize: '12px', background: isVoiceCalling ? '#f3e8ff' : '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', whiteSpace: 'nowrap' }}
                                    disabled={isVoiceCalling}
                                    onClick={() => handleVoiceOfferBooking(driver.driver_phone, driver.driver_name, vehicleAssignModal, driver.vehicle_id)}
                                    title="Have Shreya (AI) call this driver about the booking"
                                  >
                                    <i className="fa fa-microphone" style={{ marginRight: '4px' }} />
                                    {isVoiceCalling ? 'Calling...' : 'Call Driver (AI)'}
                                  </button>
                                );
                              })()}
                              {!isDraftBooking(vehicleAssignModal) && (
                              <button
                                type="button"
                                className={s.modalConfirmBtn}
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                disabled={assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)}
                                onClick={() => handleAssignFromFreeNearby(driver)}
                              >
                                Assign
                              </button>
                              )}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {showFreeNearDrop && (
                <div style={{ marginBottom: '16px', marginTop: '12px' }}>
                  <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                    Free drivers within 5 km of drop (same / one-higher class, not on a trip)
                  </p>
                  {freeNearDropLoading ? (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>
                      <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} />
                      Loading drivers near drop...
                    </p>
                  ) : (
                    <>
                      {freeNearDropSuggestions.length === 0 ? (
                        <p style={{ fontSize: '13px', color: '#6b7280' }}>
                          No free drivers within 5 km of drop.
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                          {freeNearDropSuggestions.map((driver) => {
                            const vehicleCalls = outreachForVehicle(
                              mlOutreachHistory,
                              driver.vehicle_id
                            );
                            const outreachSummary = summarizeOutreach(vehicleCalls);
                            return (
                              <div
                                key={`free-drop-${driver.vehicle_id}`}
                                style={{
                                  border: outreachSummary
                                    ? outreachSummary.badgeStyle.border
                                    : '1px solid #bae6fd',
                                  borderRadius: '8px',
                                  padding: '10px 12px',
                                  background: outreachSummary ? outreachSummary.badgeStyle.background : '#f0f9ff',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    {(driver.vehicle_photo_url || driver.url) ? (
                                      <img
                                        src={driver.vehicle_photo_url || driver.url}
                                        alt="Vehicle"
                                        style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '50%',
                                          objectFit: 'cover',
                                          cursor: 'pointer',
                                          border: '2px solid #10b981',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          flexShrink: 0,
                                          marginTop: '2px'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPreviewPhotoUrl(driver.vehicle_photo_url || driver.url);
                                        }}
                                      />
                                    ) : (
                                      <div style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        backgroundColor: '#e5e7eb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        flexShrink: 0,
                                        marginTop: '2px'
                                      }} title="No photo">🚛</div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                    <strong>
                                      #{driver.suggestion_rank} V-{driver.vehicle_id}
                                      {driver.driver_name ? ` \u00b7 ${driver.driver_name}` : ''}
                                      {formatSuggestionVehicleMeta(driver)}
                                      {driver.is_transporter ? (
                                        <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                          transporter
                                        </span>
                                      ) : null}
                                    </strong>
                                    {driver.driver_phone ? (
                                      <div style={{ marginTop: '2px' }}>
                                        {(() => {
                                          const clean = String(driver.driver_phone).replace(/\D/g, '');
                                          const isCalling = callingDrivers[clean];
                                          return (
                                            <button
                                              type="button"
                                              onClick={() => handlePlaceCall(driver.driver_phone)}
                                              disabled={isCalling}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                color: isCalling ? '#f59e0b' : '#2563eb',
                                                cursor: isCalling ? 'wait' : 'pointer',
                                                textDecoration: 'underline',
                                                whiteSpace: 'nowrap',
                                                padding: 0,
                                                fontFamily: 'inherit',
                                                fontSize: '12px',
                                              }}
                                            >
                                              {isCalling ? '\ud83d\udcde Calling...' : `\ud83d\udcde ${driver.driver_phone}`}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                        No phone number
                                      </div>
                                    )}
                                    <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                                      {driver.reason || `${driver.distance_to_pickup_km} km from drop`}
                                    </div>
                                    <OutreachCallStatus calls={vehicleCalls} compact />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      className={s.modalCancelBtn}
                                      style={{ padding: '4px 10px', fontSize: '12px' }}
                                      onClick={() =>
                                        openDriverOutreachModal(driver.vehicle_id, driver.driver_name)
                                      }
                                    >
                                      <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                                      {outreachSummary
                                        ? `Log again (${outreachSummary.callCount}\u00d7)`
                                        : 'Log call'}
                                    </button>
                                    {/* AI Voice Call button / Queue checkbox */}
                                    {driver.driver_phone && (() => {
                                      const clean = String(driver.driver_phone).replace(/\D/g, '');
                                      const isVoiceCalling = voiceCallingDrivers[clean];
                                      const qKey = `${driver.vehicle_id}:${driver.driver_phone}`;
                                      const isQueued = callQueueSelected.has(qKey);
                                      if (callQueueMode) {
                                        return (
                                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 600, padding: '4px 6px', border: `2px solid ${isQueued ? '#7c3aed' : '#c084fc'}`, borderRadius: 6, background: isQueued ? '#ede9fe' : '#faf5ff', userSelect: 'none' }}>
                                            <input type="checkbox" checked={isQueued} onChange={() => toggleCallQueueDriver(qKey)} style={{ accentColor: '#7c3aed' }} />
                                            Add to queue
                                          </label>
                                        );
                                      }
                                      return (
                                        <button
                                          type="button"
                                          className={s.modalCancelBtn}
                                          style={{ padding: '4px 10px', fontSize: '12px', background: isVoiceCalling ? '#f3e8ff' : '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', whiteSpace: 'nowrap' }}
                                          disabled={isVoiceCalling}
                                          onClick={() => handleVoiceOfferBooking(driver.driver_phone, driver.driver_name, vehicleAssignModal, driver.vehicle_id)}
                                          title="Have Shreya (AI) call this driver about the booking"
                                        >
                                          <i className="fa fa-microphone" style={{ marginRight: '4px' }} />
                                          {isVoiceCalling ? 'Calling...' : 'Call Driver (AI)'}
                                        </button>
                                      );
                                    })()}
                                    {!isDraftBooking(vehicleAssignModal) && (
                                      <button
                                        type="button"
                                        className={s.modalConfirmBtn}
                                        style={{ padding: '4px 10px', fontSize: '12px' }}
                                        disabled={assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)}
                                        onClick={() => handleAssignFromFreeNearby(driver)}
                                      >
                                        Assign
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <p className={s.modalLabel} style={{ marginBottom: '8px', marginTop: '4px' }}>
                        Suggested drivers near drop (loyalty, top 3)
                      </p>
                      {dropLoyaltySuggestions.length === 0 ? (
                        <p style={{ fontSize: '13px', color: '#6b7280' }}>
                          No loyalty drivers within 5 km of drop.
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {dropLoyaltySuggestions.map((suggestion) => {
                            const vehicleCalls = outreachForVehicle(
                              mlOutreachHistory,
                              suggestion.vehicle_id
                            );
                            const outreachSummary = summarizeOutreach(vehicleCalls);
                            return (
                              <div
                                key={`drop-loyalty-${suggestion.vehicle_id}`}
                                style={{
                                  border: outreachSummary
                                    ? outreachSummary.badgeStyle.border
                                    : '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  padding: '10px 12px',
                                  background: outreachSummary ? outreachSummary.badgeStyle.background : '#f8fafc',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                                  <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    {(suggestion.vehicle_photo_url || suggestion.url) ? (
                                      <img
                                        src={suggestion.vehicle_photo_url || suggestion.url}
                                        alt="Vehicle"
                                        style={{
                                          width: '36px',
                                          height: '36px',
                                          borderRadius: '50%',
                                          objectFit: 'cover',
                                          cursor: 'pointer',
                                          border: '2px solid #10b981',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          flexShrink: 0,
                                          marginTop: '2px'
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPreviewPhotoUrl(suggestion.vehicle_photo_url || suggestion.url);
                                        }}
                                      />
                                    ) : (
                                      <div style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        backgroundColor: '#e5e7eb',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        flexShrink: 0,
                                        marginTop: '2px'
                                      }} title="No photo">🚛</div>
                                    )}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                    <strong>
                                      #{suggestion.suggestion_rank} V-{suggestion.vehicle_id}
                                      {suggestion.driver_name ? ` \u00b7 ${suggestion.driver_name}` : ''}
                                      {formatSuggestionVehicleMeta(suggestion)}
                                      {suggestion.is_transporter ? (
                                        <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                          transporter
                                        </span>
                                      ) : null}
                                    </strong>
                                    {suggestion.driver_phone ? (
                                      <div style={{ marginTop: '2px' }}>
                                        {(() => {
                                          const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                          const isCalling = callingDrivers[clean];
                                          return (
                                            <button
                                              type="button"
                                              onClick={() => handlePlaceCall(suggestion.driver_phone)}
                                              disabled={isCalling}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                color: isCalling ? '#f59e0b' : '#2563eb',
                                                cursor: isCalling ? 'wait' : 'pointer',
                                                textDecoration: 'underline',
                                                whiteSpace: 'nowrap',
                                                padding: 0,
                                                fontFamily: 'inherit',
                                                fontSize: '12px',
                                              }}
                                            >
                                              {isCalling ? '\ud83d\udcde Calling...' : `\ud83d\udcde ${suggestion.driver_phone}`}
                                            </button>
                                          );
                                        })()}
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                        No phone number
                                      </div>
                                    )}
                                    <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                                      {suggestion.reason || `Score ${suggestion.loyalty_score}`}
                                    </div>
                                    <OutreachCallStatus calls={vehicleCalls} compact />
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                                    <button
                                      type="button"
                                      className={s.modalCancelBtn}
                                      style={{ padding: '4px 10px', fontSize: '12px' }}
                                      onClick={() =>
                                        openDriverOutreachModal(suggestion.vehicle_id, suggestion.driver_name)
                                      }
                                    >
                                      <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                                      {outreachSummary
                                        ? `Log again (${outreachSummary.callCount}\u00d7)`
                                        : 'Log call'}
                                    </button>
                                    {/* AI Voice Call button / Queue checkbox */}
                                    {suggestion.driver_phone && (() => {
                                      const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                      const isVoiceCalling = voiceCallingDrivers[clean];
                                      const qKey = `${suggestion.vehicle_id}:${suggestion.driver_phone}`;
                                      const isQueued = callQueueSelected.has(qKey);
                                      if (callQueueMode) {
                                        return (
                                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 600, padding: '4px 6px', border: `2px solid ${isQueued ? '#7c3aed' : '#c084fc'}`, borderRadius: 6, background: isQueued ? '#ede9fe' : '#faf5ff', userSelect: 'none' }}>
                                            <input type="checkbox" checked={isQueued} onChange={() => toggleCallQueueDriver(qKey)} style={{ accentColor: '#7c3aed' }} />
                                            Add to queue
                                          </label>
                                        );
                                      }
                                      return (
                                        <button
                                          type="button"
                                          className={s.modalCancelBtn}
                                          style={{ padding: '4px 10px', fontSize: '12px', background: isVoiceCalling ? '#f3e8ff' : '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', whiteSpace: 'nowrap' }}
                                          disabled={isVoiceCalling}
                                          onClick={() => handleVoiceOfferBooking(suggestion.driver_phone, suggestion.driver_name, vehicleAssignModal, suggestion.vehicle_id)}
                                          title="Have Shreya (AI) call this driver about the booking"
                                        >
                                          <i className="fa fa-microphone" style={{ marginRight: '4px' }} />
                                          {isVoiceCalling ? 'Calling...' : 'Call Driver (AI)'}
                                        </button>
                                      );
                                    })()}
                                    {!isDraftBooking(vehicleAssignModal) && (
                                      <button
                                        type="button"
                                        className={s.modalConfirmBtn}
                                        style={{ padding: '4px 10px', fontSize: '12px' }}
                                        disabled={assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)}
                                        onClick={() => handleAssignFromMlSuggestion(suggestion)}
                                      >
                                        Assign
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                  Suggested drivers (loyalty)
                </p>
                {mlOutreachHistory.length > 0 && (
                  <div
                    style={{
                      marginBottom: '10px',
                      padding: '8px 10px',
                      borderRadius: '8px',
                      background: '#f8fafc',
                      border: '1px solid #e5e7eb',
                      fontSize: '12px',
                      color: '#374151',
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: '6px' }}>
                      Calls logged on this {isDraftBooking(vehicleAssignModal) ? 'draft' : 'booking'} ({mlOutreachHistory.length})
                    </strong>
                    {mlOutreachHistory.map((row) => {
                      const summary = summarizeOutreach([row]);
                      return (
                        <div key={row.id || `${row.vehicle_id}-${row.logged_at}`} style={{ marginBottom: '4px' }}>
                          V-{row.vehicle_id}: {summary?.summaryText || 'Logged'}
                        </div>
                      );
                    })}
                  </div>
                )}
                {mlSuggestionsLoading ? (
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} />
                    Loading suggestions...
                  </p>
                ) : mlSuggestions.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>No feasible drivers ranked yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {mlSuggestions.map((suggestion) => {
                      const vehicleCalls = outreachForVehicle(
                        mlOutreachHistory,
                        suggestion.vehicle_id
                      );
                      const outreachSummary = summarizeOutreach(vehicleCalls);
                      return (
                        <div
                          key={suggestion.vehicle_id}
                          style={{
                            border: outreachSummary
                              ? outreachSummary.badgeStyle.border
                              : '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            background: outreachSummary ? outreachSummary.badgeStyle.background : '#f9fafb',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1, display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                              {(suggestion.vehicle_photo_url || suggestion.url) ? (
                                <img
                                  src={suggestion.vehicle_photo_url || suggestion.url}
                                  alt="Vehicle"
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    cursor: 'pointer',
                                    border: '2px solid #10b981',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    flexShrink: 0,
                                    marginTop: '2px'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewPhotoUrl(suggestion.vehicle_photo_url || suggestion.url);
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  backgroundColor: '#e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '18px',
                                  flexShrink: 0,
                                  marginTop: '2px'
                                }} title="No photo">🚛</div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                              <strong>
                                #{suggestion.suggestion_rank} V-{suggestion.vehicle_id}
                                {suggestion.driver_name ? ` · ${suggestion.driver_name}` : ''}
                                {formatSuggestionVehicleMeta(suggestion)}
                                {suggestion.is_transporter ? (
                                  <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                    transporter
                                  </span>
                                ) : null}
                              </strong>
                              {suggestion.driver_phone ? (
                                <div style={{ marginTop: '2px' }}>
                                  {(() => {
                                    const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                    const isCalling = callingDrivers[clean];
                                    return (
                                      <button
                                        type="button"
                                        onClick={() => handlePlaceCall(suggestion.driver_phone)}
                                        disabled={isCalling}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: isCalling ? '#f59e0b' : '#2563eb',
                                          cursor: isCalling ? 'wait' : 'pointer',
                                          textDecoration: 'underline',
                                          whiteSpace: 'nowrap',
                                          padding: 0,
                                          fontFamily: 'inherit',
                                          fontSize: '12px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}
                                      >
                                        <span>{isCalling ? '📞 Calling...' : `📞 ${suggestion.driver_phone}`}</span>
                                      </button>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                  No phone number
                                </div>
                              )}
                              <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                                {suggestion.reason || `Score ${suggestion.loyalty_score}`}
                              </div>
                              {suggestion.is_feasible_at_suggest_time === false && (
                                <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                                  {Array.isArray(suggestion.conflicting_booking_ids)
                                  && suggestion.conflicting_booking_ids.length > 0
                                    ? `Already on booking ${suggestion.conflicting_booking_ids.join(', ')}`
                                    : 'Not feasible from current location'}
                                </div>
                              )}
                              <OutreachCallStatus calls={vehicleCalls} compact />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0 }}>
                              <button
                                type="button"
                                className={s.modalCancelBtn}
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() =>
                                  openDriverOutreachModal(suggestion.vehicle_id, suggestion.driver_name)
                                }
                              >
                                <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                                {outreachSummary
                                  ? `Log again (${outreachSummary.callCount}×)`
                                  : 'Log call'}
                              </button>
                              {/* AI Voice Call button / Queue checkbox */}
                              {suggestion.driver_phone && (() => {
                                const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                const isVoiceCalling = voiceCallingDrivers[clean];
                                const qKey = `${suggestion.vehicle_id}:${suggestion.driver_phone}`;
                                const isQueued = callQueueSelected.has(qKey);
                                if (callQueueMode) {
                                  return (
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 600, padding: '4px 6px', border: `2px solid ${isQueued ? '#7c3aed' : '#c084fc'}`, borderRadius: 6, background: isQueued ? '#ede9fe' : '#faf5ff', userSelect: 'none' }}>
                                      <input type="checkbox" checked={isQueued} onChange={() => toggleCallQueueDriver(qKey)} style={{ accentColor: '#7c3aed' }} />
                                      Add to queue
                                    </label>
                                  );
                                }
                                return (
                                  <button
                                    type="button"
                                    className={s.modalCancelBtn}
                                    style={{ padding: '4px 10px', fontSize: '12px', background: isVoiceCalling ? '#f3e8ff' : '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', whiteSpace: 'nowrap' }}
                                    disabled={isVoiceCalling}
                                    onClick={() => handleVoiceOfferBooking(suggestion.driver_phone, suggestion.driver_name, vehicleAssignModal, suggestion.vehicle_id)}
                                    title="Have Shreya (AI) call this driver about the booking"
                                  >
                                    <i className="fa fa-microphone" style={{ marginRight: '4px' }} />
                                    {isVoiceCalling ? 'Calling...' : 'Call Driver (AI)'}
                                  </button>
                                );
                              })()}
                              {!isDraftBooking(vehicleAssignModal) && (
                              <button
                                type="button"
                                className={s.modalConfirmBtn}
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                disabled={
                                  assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)
                                  || suggestion.is_feasible_at_suggest_time === false
                                }
                                onClick={() => handleAssignFromMlSuggestion(suggestion)}
                              >
                                Assign
                              </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {mlCityWildcards.length > 0 && (
                  <div style={{ marginTop: '14px' }}>
                    <p className={s.modalLabel} style={{ marginBottom: '8px' }}>
                      Known in city (not feasible from current location)
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {mlCityWildcards.map((suggestion) => {
                        const vehicleCalls = outreachForVehicle(
                          mlOutreachHistory,
                          suggestion.vehicle_id
                        );
                        const outreachSummary = summarizeOutreach(vehicleCalls);
                        return (
                          <div
                            key={`wildcard-${suggestion.vehicle_id}`}
                            style={{
                              border: outreachSummary
                                ? outreachSummary.badgeStyle.border
                                : '1px solid #fcd34d',
                              borderRadius: '8px',
                              padding: '10px 12px',
                              background: outreachSummary
                                ? outreachSummary.badgeStyle.background
                                : '#fffbeb',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'flex-start' }}>
                              <div style={{ flex: 1 }}>
                                <strong>
                                  #{suggestion.suggestion_rank} V-{suggestion.vehicle_id}
                                  {suggestion.driver_name ? ` · ${suggestion.driver_name}` : ''}
                                  {formatSuggestionVehicleMeta(suggestion)}
                                  {suggestion.is_transporter ? (
                                    <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800, fontSize: '0.78rem' }}>
                                      transporter
                                    </span>
                                  ) : null}
                                </strong>
                                <span
                                  style={{
                                    marginLeft: '8px',
                                    fontSize: '11px',
                                    color: '#92400e',
                                    background: '#fef3c7',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  Same city · different pincode
                                </span>
                                {suggestion.driver_phone ? (
                                  <div style={{ marginTop: '2px' }}>
                                    {(() => {
                                      const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                      const isCalling = callingDrivers[clean];
                                      return (
                                        <button
                                          type="button"
                                          onClick={() => handlePlaceCall(suggestion.driver_phone)}
                                          disabled={isCalling}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            color: isCalling ? '#f59e0b' : '#2563eb',
                                            cursor: isCalling ? 'wait' : 'pointer',
                                            textDecoration: 'underline',
                                            whiteSpace: 'nowrap',
                                            padding: 0,
                                            fontFamily: 'inherit',
                                            fontSize: '12px',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                          }}
                                        >
                                          <span>{isCalling ? '📞 Calling...' : `📞 ${suggestion.driver_phone}`}</span>
                                        </button>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                    No phone number
                                  </div>
                                )}
                                <div style={{ fontSize: '12px', color: '#4b5563', marginTop: '4px' }}>
                                  {suggestion.reason || `Score ${suggestion.loyalty_score}`}
                                </div>
                                <OutreachCallStatus calls={vehicleCalls} compact />
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                <button
                                  type="button"
                                  className={s.modalCancelBtn}
                                  style={{ padding: '4px 10px', fontSize: '12px' }}
                                  onClick={() =>
                                    openDriverOutreachModal(suggestion.vehicle_id, suggestion.driver_name)
                                  }
                                >
                                  <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                                  {outreachSummary
                                    ? `Log again (${outreachSummary.callCount}×)`
                                    : 'Log call'}
                                </button>
                                {/* AI Voice Call button / Queue checkbox */}
                                {suggestion.driver_phone && (() => {
                                  const clean = String(suggestion.driver_phone).replace(/\D/g, '');
                                  const isVoiceCalling = voiceCallingDrivers[clean];
                                  const qKey = `${suggestion.vehicle_id}:${suggestion.driver_phone}`;
                                  const isQueued = callQueueSelected.has(qKey);
                                  if (callQueueMode) {
                                    return (
                                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#7c3aed', fontWeight: 600, padding: '4px 6px', border: `2px solid ${isQueued ? '#7c3aed' : '#c084fc'}`, borderRadius: 6, background: isQueued ? '#ede9fe' : '#faf5ff', userSelect: 'none', marginTop: '4px' }}>
                                        <input type="checkbox" checked={isQueued} onChange={() => toggleCallQueueDriver(qKey)} style={{ accentColor: '#7c3aed' }} />
                                        Add to queue
                                      </label>
                                    );
                                  }
                                  return (
                                    <button
                                      type="button"
                                      className={s.modalCancelBtn}
                                      style={{ padding: '4px 10px', fontSize: '12px', background: isVoiceCalling ? '#f3e8ff' : '#faf5ff', borderColor: '#c084fc', color: '#7c3aed', whiteSpace: 'nowrap', marginTop: '4px' }}
                                      disabled={isVoiceCalling}
                                      onClick={() => handleVoiceOfferBooking(suggestion.driver_phone, suggestion.driver_name, vehicleAssignModal, suggestion.vehicle_id)}
                                      title="Have Shreya (AI) call this driver about the booking"
                                    >
                                      <i className="fa fa-microphone" style={{ marginRight: '4px' }} />
                                      {isVoiceCalling ? 'Calling...' : 'Call Driver (AI)'}
                                    </button>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {!isDraftBooking(vehicleAssignModal) && (
              <>
              {/* Driver Pay & Labour Pay Inputs */}
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  margin: '0 0 14px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                  <i className="fa fa-money" style={{ color: '#10b981', fontSize: '13px' }} />
                  <span>Driver & Labour Pay (applied on assign)</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <p className={s.modalLabel} style={{ marginBottom: '4px', fontSize: '11px' }}>Driver Pay (₹):</p>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="e.g. 500"
                      value={driverPayInput}
                      onChange={(e) => setDriverPayInput(e.target.value)}
                      className={s.modalInput}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                  <div>
                    <p className={s.modalLabel} style={{ marginBottom: '4px', fontSize: '11px' }}>Labour Pay (₹):</p>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="e.g. 200"
                      value={labourPayInput}
                      onChange={(e) => setLabourPayInput(e.target.value)}
                      className={s.modalInput}
                      style={{ width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', margin: '0 0 12px' }}>
                — manual assign —
              </p>

              <p className={s.modalLabel}>Vehicle ID:</p>
              <input
                type="number"
                min="1"
                value={vehicleIdInput}
                onChange={(e) => setVehicleIdInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmVehicleAssign();
                }}
                placeholder="Enter vehicle ID"
                autoFocus
                disabled={assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)}
                className={s.modalInput}
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: '12px' }}
              />
              {(() => {
                const vid = Number.parseInt((vehicleIdInput || '').trim(), 10);
                if (!Number.isInteger(vid) || vid <= 0) return null;
                const manualCalls = outreachForVehicle(mlOutreachHistory, vid);
                if (!manualCalls.length) return null;
                return <OutreachCallStatus calls={manualCalls} />;
              })()}

              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', margin: '0 0 12px' }}>— or —</p>

              <p className={s.modalLabel}>Driver name:</p>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={driverNameInput}
                  onChange={(e) => handleDriverNameInputChange(e.target.value)}
                  onFocus={() => filterDriverAssignSuggestions(driverNameInput)}
                  onBlur={() => {
                    window.setTimeout(() => setDriverAssignSuggestions([]), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (driverAssignSuggestions.length > 0) {
                        e.preventDefault();
                        selectDriverAssignSuggestion(driverAssignSuggestions[0]);
                        return;
                      }
                      handleConfirmVehicleAssign();
                    }
                    if (e.key === 'Escape') {
                      setDriverAssignSuggestions([]);
                    }
                  }}
                  placeholder={assignVehicleDriversLoading ? 'Loading drivers...' : 'Type to search driver name'}
                  disabled={
                    assignVehicleDriversLoading ||
                    assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)
                  }
                  className={s.modalInput}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                  autoComplete="off"
                />
                {driverAssignSuggestions.length > 0 && (
                  <div className={s.addressSuggestions} style={{ position: 'absolute', left: 0, right: 0, zIndex: 10, marginTop: '4px' }}>
                    <div className={s.addressSuggestionsHeader}>
                      <i className="fa fa-user" />
                      Matching drivers:
                    </div>
                    {driverAssignSuggestions.map((vehicle) => (
                      <button
                        key={vehicle.vehicle_id}
                        type="button"
                        className={s.addressSuggestionItem}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectDriverAssignSuggestion(vehicle)}
                      >
                        <strong>{vehicle.driver_name || 'Unnamed driver'}</strong>
                        <small>
                          V-{vehicle.vehicle_id}
                          {vehicle.registration_no ? ` • ${vehicle.registration_no}` : ''}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Search matches Vehicle Details drivers. Pick a suggestion to fill both fields.
              </p>

              <button
                type="button"
                className={s.modalCancelBtn}
                onClick={handleOpenNearbyVehicles}
                disabled={nearbyVehiclesLoading}
                style={{
                  width: '100%',
                  marginTop: '16px',
                  borderColor: '#7c3aed',
                  color: '#7c3aed',
                  fontWeight: 600,
                }}
              >
                <i className="fa fa-map-marker" style={{ marginRight: '6px' }} />
                Check nearby vehicles
              </button>

              {(() => {
                const vid = Number.parseInt((vehicleIdInput || '').trim(), 10);
                if (!Number.isInteger(vid) || vid <= 0) return null;
                const driverMatch = assignVehicleDrivers.find((v) => v.vehicle_id === vid);
                if (!driverMatch?.driver_phone) return null;

                const clean = String(driverMatch.driver_phone).replace(/\D/g, '');
                const isCalling = callingDrivers[clean];
                const isVoiceCalling = voiceCallingDrivers[clean];

                return (
                  <>
                    <button
                      type="button"
                      className={s.modalCancelBtn}
                      onClick={() => handlePlaceCall(driverMatch.driver_phone)}
                      disabled={isCalling}
                      style={{
                        width: '100%',
                        marginTop: '16px',
                        borderColor: '#10b981',
                        color: '#10b981',
                        fontWeight: 600,
                      }}
                    >
                      <i className="fa fa-phone" style={{ marginRight: '6px' }} />
                      {isCalling ? 'Calling...' : `Call Driver (${driverMatch.driver_phone})`}
                    </button>
                    <button
                      type="button"
                      className={s.modalCancelBtn}
                      onClick={() => handleVoiceOfferBooking(
                        driverMatch.driver_phone,
                        driverMatch.driver_name || driverNameInput,
                        vehicleAssignModal,
                        vid
                      )}
                      disabled={isVoiceCalling}
                      title="Have Shreya (AI) call this driver about the booking"
                      style={{
                        width: '100%',
                        marginTop: '8px',
                        background: isVoiceCalling ? '#f3e8ff' : '#faf5ff',
                        borderColor: '#c084fc',
                        color: '#7c3aed',
                        fontWeight: 600,
                      }}
                    >
                      <i className="fa fa-microphone" style={{ marginRight: '6px' }} />
                      {isVoiceCalling ? 'Calling...' : `Call Driver (AI) (${driverMatch.driver_phone})`}
                    </button>
                  </>
                );
              })()}

              {vehicleIdInput.trim() && (
                <button
                  type="button"
                  className={s.modalCancelBtn}
                  onClick={() => {
                    const vid = Number.parseInt(vehicleIdInput.trim(), 10);
                    if (!Number.isInteger(vid) || vid <= 0) {
                      toast.error('Enter a valid vehicle ID');
                      return;
                    }
                    const driverMatch = assignVehicleDrivers.find((v) => v.vehicle_id === vid);
                    openDriverOutreachModal(vid, driverMatch?.driver_name || driverNameInput);
                  }}
                  style={{
                    width: '100%',
                    marginTop: '8px',
                    borderColor: '#2563eb',
                    color: '#2563eb',
                    fontWeight: 600,
                  }}
                >
                  <i className="fa fa-phone" style={{ marginRight: '6px' }} />
                  {(() => {
                    const vid = Number.parseInt(vehicleIdInput.trim(), 10);
                    const summary = summarizeOutreach(
                      Number.isInteger(vid) ? outreachForVehicle(mlOutreachHistory, vid) : []
                    );
                    return summary
                      ? `Log again for V-${vid} (${summary.callCount}×)`
                      : 'Log call for this vehicle';
                  })()}
                </button>
              )}
              </>
              )}
            </div>
            <div className={s.modalFooter}>
              <button
                type="button"
                className={s.modalCancelBtn}
                onClick={closeVehicleAssignModal}
              >
                {isDraftBooking(vehicleAssignModal) ? 'Close' : 'Cancel'}
              </button>
              {!isDraftBooking(vehicleAssignModal) && (
              <button
                type="button"
                className={s.modalConfirmBtn}
                onClick={handleConfirmVehicleAssign}
                disabled={
                  (!vehicleIdInput.trim() && !driverNameInput.trim()) ||
                  assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)
                }
              >
                {assigningBookingId === (vehicleAssignModal.id ?? vehicleAssignModal.booking_id) ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" />
                    Assign
                  </>
                )}
              </button>
              )}
            </div>
          </div>
          {bookingCallsOpen && (
            <div
              className={s.modalOverlay}
              style={{ zIndex: 10002 }}
              onClick={(e) => {
                e.stopPropagation();
                setBookingCallsOpen(false);
              }}
            >
              <div
                className={s.modalContent}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '640px', width: '95%' }}
              >
                <div className={s.modalHeader}>
                  <i className="fa fa-microphone" style={{ color: '#7c3aed', fontSize: '24px' }} />
                  <h3>AI calls</h3>
                </div>
                <div className={s.modalBody}>
                  <BookingBotCalls
                    bookingId={isDraftBooking(vehicleAssignModal) ? null : (vehicleAssignModal.id ?? vehicleAssignModal.booking_id)}
                    draftId={isDraftBooking(vehicleAssignModal) ? (vehicleAssignModal.id ?? vehicleAssignModal.booking_id) : null}
                  />
                </div>
                <div className={s.modalFooter}>
                  <button
                    type="button"
                    className={s.modalCancelBtn}
                    onClick={() => setBookingCallsOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nearby vehicles (base_lat/base_lng vs booking pickup) */}
      {nearbyVehiclesModal && (
        <div
          className={s.modalOverlay}
          style={{ zIndex: 10001 }}
          onClick={closeNearbyVehiclesModal}
        >
          <div
            className={s.modalContent}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '640px', width: '95%' }}
          >
            <div className={s.modalHeader}>
              <i className="fa fa-map-marker" style={{ color: '#059669', fontSize: '24px' }} />
              <h3>Nearby Vehicles</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking pickup</p>
              <p className={s.modalValue} style={{ fontSize: '13px' }}>
                {nearbyVehiclesModal.pickup_address || 'Pickup location'}
              </p>

              <div style={{ marginBottom: '12px' }}>
                <p className={s.modalLabel} style={{ marginBottom: '6px' }}>Vehicle filter</p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="nearbyClassFilter"
                      checked={nearbyClassFilter === 'all'}
                      onChange={() => handleNearbyClassFilterChange('all')}
                      disabled={nearbyVehiclesLoading}
                    />
                    All vehicles
                  </label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="nearbyClassFilter"
                      checked={nearbyClassFilter === 'class'}
                      onChange={() => handleNearbyClassFilterChange('class')}
                      disabled={nearbyVehiclesLoading}
                    />
                    Class based filter
                  </label>
                </div>
                {nearbyClassFilter === 'class' && getBookingVehicleClassNumber(nearbyVehiclesModal) && (
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px' }}>
                    Showing Class {getBookingVehicleClassNumber(nearbyVehiclesModal)} and Class{' '}
                    {getBookingVehicleClassNumber(nearbyVehiclesModal) + 1} only
                  </p>
                )}
                {nearbyClassFilter === 'class' && !getBookingVehicleClassNumber(nearbyVehiclesModal) && (
                  <p style={{ fontSize: '12px', color: '#b45309', marginTop: '6px' }}>
                    Booking has no vehicle class — showing all nearby vehicles.
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label className={s.modalLabel} style={{ margin: 0 }}>Radius:</label>
                <select
                  value={nearbyRadiusKm}
                  onChange={(e) => handleNearbyRadiusChange(e.target.value)}
                  disabled={nearbyVehiclesLoading}
                  className={s.modalInput}
                  style={{ width: 'auto', minWidth: '100px' }}
                >
                  {[5, 10, 15, 20, 25, 30, 50].map((km) => (
                    <option key={km} value={km}>{km} km</option>
                  ))}
                </select>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>or</span>
                <input
                  type="number"
                  min="1"
                  max="200"
                  step="1"
                  placeholder="Custom km"
                  value={nearbyCustomRadiusKm}
                  onChange={(e) => setNearbyCustomRadiusKm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const custom = Number.parseFloat(nearbyCustomRadiusKm);
                      if (Number.isFinite(custom) && custom > 0) {
                        handleNearbyRadiusChange(custom);
                      }
                    }
                  }}
                  disabled={nearbyVehiclesLoading}
                  className={s.modalInput}
                  style={{ width: '100px' }}
                />
                <button
                  type="button"
                  className={s.modalCancelBtn}
                  disabled={nearbyVehiclesLoading}
                  onClick={() => {
                    const custom = Number.parseFloat(nearbyCustomRadiusKm);
                    if (!Number.isFinite(custom) || custom <= 0) {
                      toast.error('Enter a valid radius in km');
                      return;
                    }
                    handleNearbyRadiusChange(custom);
                  }}
                  style={{ padding: '8px 12px' }}
                >
                  Apply
                </button>
              </div>

              {nearbyVehiclesLoading ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>
                  <i className="fa fa-spinner fa-spin" /> Loading vehicles...
                </p>
              ) : nearbyVehiclesList.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>
                  {nearbyClassFilter === 'class' && getBookingVehicleClassNumber(nearbyVehiclesModal) ? (
                    <>
                      No Class {getBookingVehicleClassNumber(nearbyVehiclesModal)} or Class{' '}
                      {getBookingVehicleClassNumber(nearbyVehiclesModal) + 1} vehicles within {nearbyRadiusKm} km.
                    </>
                  ) : (
                    <>No vehicles with base location within {nearbyRadiusKm} km of pickup.</>
                  )}
                </p>
              ) : (
                <div style={{ maxHeight: '320px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Vehicle</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Class</th>
                        <th style={{ padding: '8px 10px', textAlign: 'left' }}>Driver</th>
                        <th style={{ padding: '8px 10px', textAlign: 'right' }}>Distance</th>
                        <th style={{ padding: '8px 10px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {nearbyVehiclesList.map((vehicle) => (
                        <tr key={vehicle.vehicle_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '8px 10px', fontWeight: 600 }}>V-{vehicle.vehicle_id}</td>
                          <td style={{ padding: '8px 10px', color: '#4f46e5', fontWeight: 600 }}>
                            {formatNearbyVehicleClass(vehicle)}
                            {formatVehicleBodyType(vehicle) ? (
                              <span style={bodyTypeBadgeStyle(formatVehicleBodyType(vehicle))}>
                                {formatVehicleBodyType(vehicle)}
                              </span>
                            ) : null}
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            {vehicle.driver_name || '—'}
                            {vehicle.registration_no ? (
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>{vehicle.registration_no}</div>
                            ) : null}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', color: '#059669', fontWeight: 600 }}>
                            {vehicle.distance_km.toFixed(1)} km
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                            <button
                              type="button"
                              className={s.modalConfirmBtn}
                              onClick={() => selectNearbyVehicle(vehicle)}
                              style={{ padding: '6px 10px', fontSize: '12px' }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '10px' }}>
                Distance is straight-line from vehicle base (base_lat/base_lng) to booking pickup.
              </p>
            </div>
            <div className={s.modalFooter}>
              <button type="button" className={s.modalCancelBtn} onClick={closeNearbyVehiclesModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm before directly assigning a suggested driver (no separate owner) */}
      {confirmAssignModal && (
        <div className={s.modalOverlay} onClick={() => setConfirmAssignModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-truck" style={{ color: '#7c3aed', fontSize: '24px' }} />
              <h3>Confirm assignment</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking</p>
              <p className={s.modalValue}>#{confirmAssignModal.booking?.id ?? confirmAssignModal.booking?.booking_id}</p>
              <p className={s.modalLabel}>Vehicle</p>
              <p className={s.modalValue}>V-{confirmAssignModal.vehicleId}</p>
              <p className={s.modalLabel}>Driver</p>
              <p className={s.modalValue}>
                {confirmAssignModal.driver?.name || confirmAssignModal.suggestion?.driver_name || 'Unknown'}
                {confirmAssignModal.driver?.phone ? ` • ${confirmAssignModal.driver.phone}` : ''}
              </p>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '12px 0 0' }}>
                Assign this vehicle to the booking?
              </p>
            </div>
            <div className={s.modalFooter}>
              <button type="button" className={s.modalCancelBtn} onClick={() => setConfirmAssignModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={s.modalConfirmBtn}
                onClick={handleConfirmDirectAssign}
                disabled={assigningBookingId === (confirmAssignModal.booking?.id ?? confirmAssignModal.booking?.booking_id)}
              >
                {assigningBookingId === (confirmAssignModal.booking?.id ?? confirmAssignModal.booking?.booking_id) ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="fa fa-truck" />
                    Confirm assign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Owner vs driver assignment choice (non-owner drivers only) */}
      {ownerAssignChoiceModal && (
        <div className={s.modalOverlay} onClick={() => setOwnerAssignChoiceModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-users" style={{ color: '#7c3aed', fontSize: '24px' }} />
              <h3>Vehicle has a separate owner</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking</p>
              <p className={s.modalValue}>#{ownerAssignChoiceModal.booking?.id ?? ownerAssignChoiceModal.booking?.booking_id}</p>
              <p className={s.modalLabel}>Vehicle</p>
              <p className={s.modalValue}>V-{ownerAssignChoiceModal.vehicleId}</p>
              <p className={s.modalLabel}>Assigned driver</p>
              <p className={s.modalValue}>
                {ownerAssignChoiceModal.driver?.name || 'Unknown'}
                {ownerAssignChoiceModal.driver?.phone ? ` • ${ownerAssignChoiceModal.driver.phone}` : ''}
              </p>
              <p className={s.modalLabel}>Registered owner</p>
              <p className={s.modalValue}>
                {ownerAssignChoiceModal.owner?.name || 'Unknown'}
                {ownerAssignChoiceModal.owner?.phone ? ` • ${ownerAssignChoiceModal.owner.phone}` : ''}
                {ownerAssignChoiceModal.owner?.user_id ? ` (user #${ownerAssignChoiceModal.owner.user_id})` : ''}
              </p>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: '12px 0 0' }}>
                Choose how to notify for this assignment. Direct assignment uses the existing flow (driver).
              </p>
            </div>
            <div className={`${s.modalFooter} ${s.ownerChoiceFooter}`}>
              <button type="button" className={s.modalCancelBtn} onClick={() => setOwnerAssignChoiceModal(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={s.modalOwnerBtn}
                onClick={handleAssignThroughOwner}
                title="Coming soon"
              >
                <i className="fa fa-user-circle" />
                Assign through owner
              </button>
              <button
                type="button"
                className={s.modalConfirmBtn}
                onClick={handleAssignDirectToDriver}
                disabled={assigningBookingId === (ownerAssignChoiceModal.booking?.id ?? ownerAssignChoiceModal.booking?.booking_id)}
              >
                {assigningBookingId === (ownerAssignChoiceModal.booking?.id ?? ownerAssignChoiceModal.booking?.booking_id) ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="fa fa-truck" />
                    Assign directly to driver
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Part Booking Vehicle Picker Modal */}
      {partAssignModal && (
        <div className={s.modalOverlay} onClick={() => setPartAssignModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-car" style={{ color: '#7c3aed', fontSize: '24px' }} />
              <h3>Assign Part Booking Vehicle</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking ID:</p>
              <p className={s.modalValue}>{partAssignModal.id}</p>

              <p className={s.modalLabel}>Select Vehicle (Part Booking Enabled):</p>
              <select
                value={selectedPartVehicleId}
                onChange={(e) => setSelectedPartVehicleId(e.target.value)}
                disabled={partVehiclesLoading || assigningBookingId === partAssignModal.id}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                {partVehicles.map((vehicle) => (
                  <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                    #{vehicle.vehicle_id} • {vehicle.registration_no || 'No Reg'}
                    {vehicle.driver_name ? ` • ${vehicle.driver_name}` : ''}
                    {!vehicle.is_active ? ' • Inactive' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className={s.modalFooter}>
              <button className={s.modalCancelBtn} onClick={() => setPartAssignModal(null)}>
                Cancel
              </button>
              <button
                className={s.modalConfirmBtn}
                onClick={handleConfirmPartVehicleAssign}
                disabled={partVehiclesLoading || !selectedPartVehicleId || assigningBookingId === partAssignModal.id}
              >
                {assigningBookingId === partAssignModal.id ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Assigning...
                  </>
                ) : (
                  <>
                    <i className="fa fa-check" />
                    Assign Vehicle
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal */}
      {cancelModal && (
        <div className={s.modalOverlay} onClick={() => setCancelModal(null)}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-exclamation-triangle" style={{ color: '#ef4444', fontSize: '24px' }} />
              <h3>Cancel Booking</h3>
            </div>
            <div className={s.modalBody}>
              <p className={s.modalLabel}>Booking ID:</p>
              <p className={s.modalValue}>{cancelModal.id}</p>

              <p className={s.modalLabel}>Customer:</p>
              <p className={s.modalValue}>{cancelModal.customer_name}</p>
              {cancelModal.customer_phone && (
                <p className={s.modalValue} style={{ fontSize: '13px', color: '#6b7280' }}>
                  <i className="fa fa-phone" /> {cancelModal.customer_phone}
                </p>
              )}

              <p className={s.modalLabel}>Reason (optional):</p>
              <textarea
                className={s.cancelReasonTextarea}
                placeholder="e.g. No driver available, Customer request, etc."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />

              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '13px',
                fontWeight: '500'
              }}>
                <i className="fa fa-info-circle" style={{ marginRight: '6px' }} />
                A cancellation message will be sent to the customer via WhatsApp.
              </div>
            </div>
            <div className={s.modalFooter}>
              <button className={s.modalCancelBtn} onClick={() => setCancelModal(null)}>
                Keep Booking
              </button>
              <button
                className={s.modalConfirmDangerBtn}
                onClick={handleCancelBooking}
                disabled={!!cancellingId}
              >
                {cancellingId === cancelModal.id ? (
                  <>
                    <i className="fa fa-spinner fa-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <i className="fa fa-times" />
                    Confirm Cancel
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Draft Confirmation Popup */}
      {draftConfirmModal && (() => {
        const b = draftConfirmModal;
        const bookingId = b?.id ?? b?.booking_id;
        const pickupTime = b?.pickup_time ? new Date(b.pickup_time).toLocaleString() : '—';
        const pickupAddr = b?.pickup_address || b?.pickup_location_name || '—';
        const dropAddrs = Array.isArray(b?.drop_addresses) ? b.drop_addresses.filter(Boolean)
          : Array.isArray(b?.drop_stops) ? b.drop_stops.map(s => s?.location_name).filter(Boolean)
          : b?.drop_address ? [b.drop_address] : [];
        const vehicle = getVehicleDisplayName(b);
        const distance = b?.distance != null && Number(b.distance) > 0 ? `${Number(b.distance).toFixed(1)} km` : null;
        const duration = b?.duration ? formatDurationHours(b.duration) : null;
        const estimate = b?.payment_estimate ? `₹${Number(b.payment_estimate).toLocaleString('en-IN')}`
          : b?.actual_bill ? `₹${Number(b.actual_bill).toLocaleString('en-IN')}` : '—';
        const toll = Number(b?.toll_charge) > 0 ? `₹${Number(b.toll_charge).toFixed(2)}` : null;
        const material = b?.material_description || null;
        const remarks = b?.remarks || null;

        return (
          <div className={s.modalOverlay} onClick={() => setDraftConfirmModal(null)}>
            <div className={s.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
              <div className={s.modalHeader} style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', borderBottomColor: '#a7f3d0' }}>
                <i className="fa fa-check-circle" style={{ color: '#059669', fontSize: '24px' }} />
                <h3>Confirm Draft Booking</h3>
              </div>
              <div className={s.modalBody}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', fontWeight: 500 }}>
                  Please verify all details before confirming
                </div>

                {/* Customer */}
                <p className={s.modalLabel}>Customer</p>
                <p className={s.modalValue}>{b?.customer_name || '—'}{b?.customer_phone ? ` (${b.customer_phone})` : ''}</p>

                {/* Booking Details */}
                <p className={s.modalLabel}>Booking ID</p>
                <p className={s.modalValue}>#{bookingId}</p>

                <p className={s.modalLabel}>Pickup Time</p>
                <p className={s.modalValue}>{pickupTime}</p>

                <p className={s.modalLabel}>Vehicle</p>
                <p className={s.modalValue}>{vehicle}</p>

                {/* Locations */}
                <p className={s.modalLabel}>Pickup</p>
                <p className={s.modalValue}>{pickupAddr}</p>

                {dropAddrs.length > 0 && dropAddrs.map((addr, i) => (
                  <React.Fragment key={i}>
                    <p className={s.modalLabel}>Drop {i + 1}</p>
                    <p className={s.modalValue}>{addr}</p>
                  </React.Fragment>
                ))}

                {/* Pricing */}
                {distance && (
                  <>
                    <p className={s.modalLabel}>Distance</p>
                    <p className={s.modalValue}>{distance}</p>
                  </>
                )}
                {duration && (
                  <>
                    <p className={s.modalLabel}>Duration</p>
                    <p className={s.modalValue}>{duration}</p>
                  </>
                )}
                {toll && (
                  <>
                    <p className={s.modalLabel}>Toll</p>
                    <p className={s.modalValue}>{toll}</p>
                  </>
                )}
                <p className={s.modalLabel}>Estimated Price</p>
                <p className={s.modalValue} style={{ color: '#059669', fontWeight: 700, fontSize: '18px' }}>{estimate}</p>

                {/* Additional */}
                {material && (
                  <>
                    <p className={s.modalLabel}>Material</p>
                    <p className={s.modalValue}>{material}</p>
                  </>
                )}
                {remarks && (
                  <>
                    <p className={s.modalLabel}>Remarks</p>
                    <p className={s.modalValue}>{remarks}</p>
                  </>
                )}

                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '8px',
                  color: '#065f46',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  <i className="fa fa-info-circle" style={{ marginRight: '6px' }} />
                  This will convert the draft to a Pending booking.
                </div>
              </div>
              <div className={s.modalFooter}>
                <button className={s.modalCancelBtn} onClick={() => setDraftConfirmModal(null)}>
                  <i className="fa fa-arrow-left" /> Go Back
                </button>
                <button
                  className={s.modalConfirmBtn}
                  onClick={executeConfirmDraft}
                  disabled={draftActionLoading === bookingId}
                  style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                >
                  {draftActionLoading === bookingId ? (
                    <><i className="fa fa-spinner fa-spin" /> Confirming...</>
                  ) : (
                    <><i className="fa fa-check-circle" /> Confirm Booking</>
                  )}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Invoice Preview Modal */}
      {invoiceModal && (
        <div className={s.modalOverlay} onClick={closeInvoiceModal}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '680px', width: '90%', borderRadius: '16px', overflow: 'hidden', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '2px solid #f1f3f5', flexShrink: 0 }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <i className="fa fa-file-text-o" style={{ color: '#667eea' }} /> Invoice Preview
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {invoiceData && !invoiceData?.cached && !invoiceEditAllUnlocked && (
                  <button
                    onClick={handleInvoiceToggleEdit}
                    disabled={invoiceLoading}
                    style={{
                      padding: '6px 12px', border: '1px solid #667eea', background: invoiceEditMode ? '#667eea' : 'white',
                      color: invoiceEditMode ? 'white' : '#667eea', borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem',
                      cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                      opacity: invoiceLoading ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    <i className={`fa ${invoiceEditMode ? 'fa-eye' : 'fa-pencil'}`} /> {invoiceEditMode ? 'Preview' : 'Edit Details'}
                  </button>
                )}
                {invoiceData && (
                  <button
                    onClick={handleInvoiceEditAllClick}
                    disabled={invoiceLoading}
                    style={{
                      padding: '6px 12px', border: '1px solid #2b579a', background: invoiceEditAllUnlocked ? '#2b579a' : 'white',
                      color: invoiceEditAllUnlocked ? 'white' : '#2b579a', borderRadius: '8px', fontWeight: 600, fontSize: '0.78rem',
                      cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                      opacity: invoiceLoading ? 0.5 : 1,
                      display: 'flex', alignItems: 'center', gap: '6px',
                    }}
                  >
                    <i className={`fa ${invoiceEditAllUnlocked ? 'fa-unlock' : 'fa-lock'}`} /> {invoiceEditAllUnlocked ? 'Exit Edit All' : 'Edit All'}
                  </button>
                )}
                <button
                  onClick={closeInvoiceModal}
                  style={{ width: '32px', height: '32px', border: 'none', background: '#f1f3f5', color: '#495057', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >&times;</button>
              </div>
            </div>

            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {invoiceLoading && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#868e96' }}>
                  <i className="fa fa-spinner fa-spin" /> Loading invoice...
                </div>
              )}

              {invoiceError && (
                <div style={{ padding: '0.75rem', background: '#fff5f5', borderRadius: '8px', color: '#c62828', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{invoiceError}</div>
              )}

              {invoiceSent && (
                <div style={{ padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px', color: '#2e7d32', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                  <i className="fa fa-check-circle" /> Invoice sent to customer via WhatsApp!
                </div>
              )}

              {invoiceEditAllPrompt && !invoiceEditAllUnlocked && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginBottom: '12px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', marginBottom: '12px' }}>
                    <i className="fa fa-lock" style={{ color: '#2b579a', marginRight: '8px' }} /> Enter password to edit all fields
                  </div>
                  <input
                    type="password"
                    value={invoiceEditAllPasswordInput}
                    onChange={(e) => setInvoiceEditAllPasswordInput(e.target.value)}
                    placeholder="Password"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleInvoiceEditAllUnlock(e); }}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box', marginBottom: '12px' }}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleInvoiceEditAllUnlock}
                      disabled={invoiceLoading || !invoiceEditAllPasswordInput}
                      style={{
                        padding: '0.55rem 1.1rem', border: 'none', background: '#2b579a', color: 'white', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading || !invoiceEditAllPasswordInput ? 'not-allowed' : 'pointer',
                        opacity: invoiceLoading || !invoiceEditAllPasswordInput ? 0.5 : 1,
                      }}
                    >
                      {invoiceLoading ? (<><i className="fa fa-spinner fa-spin" /> Checking...</>) : 'Unlock'}
                    </button>
                    <button
                      onClick={() => { setInvoiceEditAllPrompt(false); setInvoiceEditAllPasswordInput(''); }}
                      disabled={invoiceLoading}
                      style={{
                        padding: '0.55rem 1.1rem', border: '1px solid #ced4da', background: 'white', color: '#495057', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {invoiceData?.cached && invoiceData?.invoice_url && !invoiceEditAllUnlocked && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', overflow: 'hidden' }}>
                  <iframe
                    src={invoiceData.invoice_url}
                    title="Invoice Preview"
                    style={{ width: '100%', height: '420px', border: 'none', background: 'white' }}
                  />
                </div>
              )}

              {invoiceData && !invoiceEditMode && invoiceData?.invoice_html && (!invoiceData?.cached || invoiceEditAllUnlocked) && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', overflow: 'hidden' }}>
                  <iframe
                    srcDoc={invoiceData.invoice_html}
                    title="Invoice Preview"
                    style={{ width: '100%', height: invoiceEditAllUnlocked ? '260px' : '420px', border: 'none', background: 'white' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              )}

              {invoiceEditAllUnlocked && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px', marginTop: '12px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <span><i className="fa fa-unlock" style={{ color: '#2b579a', marginRight: '8px' }} /> Edit All Invoice Fields</span>
                    {invoiceEditAllPreviewUpdating && <span style={{ fontWeight: 500, fontSize: '0.75rem', color: '#868e96' }}><i className="fa fa-spinner fa-spin" /> Updating preview...</span>}
                  </div>
                  {invoiceEditAllFieldDefs((invoiceModal?.booking_type || '').toLowerCase() === 'hourly').map(field => (
                    <div key={field.key} style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#495057', marginBottom: '4px' }}>{field.label}</label>
                      <input
                        type="text"
                        value={invoiceEditAllFields[field.key] || ''}
                        onChange={(e) => handleInvoiceEditAllField(field.key, e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '4px' }}>
                    <button
                      onClick={handleInvoiceEditAllApplyPreview}
                      disabled={invoiceLoading}
                      style={{
                        padding: '0.55rem 1.1rem', border: 'none', background: '#2b579a', color: 'white', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                        opacity: invoiceLoading ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem',
                      }}
                    >
                      {invoiceLoading ? (<><i className="fa fa-spinner fa-spin" /> Updating...</>) : (<><i className="fa fa-eye" /> Update Preview</>)}
                    </button>
                    <button
                      onClick={handleInvoiceEditAllClick}
                      disabled={invoiceLoading}
                      style={{
                        padding: '0.55rem 1.1rem', border: '1px solid #ced4da', background: 'white', color: '#495057', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Close Edit All
                    </button>
                  </div>
                </div>
              )}

              {invoiceData && !invoiceData?.cached && invoiceEditMode && !invoiceEditAllUnlocked && (
                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa fa-pencil" style={{ color: '#667eea' }} /> Edit Invoice Details
                  </div>
                  {[
                    { key: 'gstin', label: 'GSTIN', placeholder: 'e.g., 29AADCU9202C1ZH', hint: 'Applied only to this invoice' },
                    { key: 'consigner_name', label: 'Consigner Name', placeholder: 'Customer / business name' },
                    { key: 'company_name', label: 'Company Name', placeholder: 'Company name on invoice' },
                    { key: 'company_address', label: 'Company Address', placeholder: 'Address on invoice' },
                  ].map(field => (
                    <div key={field.key} style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#495057', marginBottom: '4px' }}>{field.label}</label>
                      <input
                        type="text"
                        value={invoiceEditFields[field.key] || ''}
                        onChange={(e) => handleInvoiceEditField(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                      {field.hint && <div style={{ fontSize: '0.72rem', color: '#868e96', marginTop: '3px' }}>{field.hint}</div>}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '4px' }}>
                    <button
                      onClick={handleInvoiceApplyPreview}
                      disabled={invoiceLoading}
                      style={{
                        padding: '0.55rem 1.1rem', border: 'none', background: '#667eea', color: 'white', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                        opacity: invoiceLoading ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '0.4rem',
                      }}
                    >
                      {invoiceLoading ? (<><i className="fa fa-spinner fa-spin" /> Updating...</>) : (<><i className="fa fa-eye" /> Update Preview</>)}
                    </button>
                    <button
                      onClick={handleInvoiceToggleEdit}
                      disabled={invoiceLoading}
                      style={{
                        padding: '0.55rem 1.1rem', border: '1px solid #ced4da', background: 'white', color: '#495057', borderRadius: '10px',
                        fontWeight: 600, fontSize: '0.85rem', cursor: invoiceLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

            </div>
            <div style={{ display: 'flex', gap: '0.75rem', padding: '16px 24px 20px', flexWrap: 'wrap', borderTop: '1px solid #f1f3f5', flexShrink: 0 }}>
                <button
                  onClick={handleInvoiceDownload}
                  disabled={invoiceLoading || invoiceFinalizing || !invoiceData}
                  style={{
                    flex: 1, padding: '0.7rem 1.25rem', border: '2px solid #667eea', background: 'white',
                    color: '#667eea', borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem',
                    cursor: invoiceLoading || invoiceFinalizing || !invoiceData ? 'not-allowed' : 'pointer',
                    opacity: invoiceLoading || invoiceFinalizing || !invoiceData ? 0.5 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.background = '#667eea'; e.currentTarget.style.color = 'white'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(102,126,234,0.3)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#667eea'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {invoiceFinalizing ? (
                    <><i className="fa fa-spinner fa-spin" /> Generating...</>
                  ) : (
                    <><i className="fa fa-download" /> {invoiceData?.cached ? 'Download PDF' : 'Generate & Download PDF'}</>
                  )}
                </button>
                <button
                  onClick={invoiceEditAllUnlocked ? handleInvoiceEditAllDownload : handleInvoiceEditAllClick}
                  disabled={invoiceLoading || invoiceEditAllDownloading || !invoiceData}
                  style={{
                    flex: 1, padding: '0.7rem 1.25rem', border: '2px solid #2b579a', background: 'white',
                    color: '#2b579a', borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem',
                    cursor: invoiceLoading || invoiceEditAllDownloading || !invoiceData ? 'not-allowed' : 'pointer',
                    opacity: invoiceLoading || invoiceEditAllDownloading || !invoiceData ? 0.5 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.background = '#2b579a'; e.currentTarget.style.color = 'white'; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(43,87,154,0.3)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#2b579a'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {invoiceEditAllDownloading ? (
                    <><i className="fa fa-spinner fa-spin" /> Downloading...</>
                  ) : invoiceEditAllUnlocked ? (
                    <><i className="fa fa-download" /> Download Local PDF</>
                  ) : (
                    <><i className="fa fa-lock" /> Edit All</>
                  )}
                </button>
                <button
                  onClick={handleInvoiceSend}
                  disabled={invoiceLoading || invoiceSending || !invoiceData}
                  style={{
                    flex: 1, padding: '0.7rem 1.25rem', border: 'none',
                    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                    color: 'white', borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem',
                    cursor: invoiceLoading || invoiceSending || !invoiceData ? 'not-allowed' : 'pointer',
                    opacity: invoiceLoading || invoiceSending || !invoiceData ? 0.5 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => { if (!e.currentTarget.disabled) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(37,211,102,0.35)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  {invoiceSending ? (
                    <><i className="fa fa-spinner fa-spin" /> Sending...</>
                  ) : (
                    <><i className="fa fa-whatsapp" /> Send via WhatsApp</>
                  )}
                </button>
              </div>
          </div>
        </div>
      )}

      {customerPreviewModal && (
        <div className={s.modalOverlay} onClick={handleCloseCustomerPreview}>
          <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={s.modalHeader}>
              <i className="fa fa-whatsapp" style={{ color: '#25d366', fontSize: '24px' }} />
              <h3>{customerPreviewModal.title}</h3>
            </div>
            <div className={s.modalBody}>
              {customerPreviewModal.loading ? (
                <p className={s.modalValue}><i className="fa fa-spinner fa-spin" /> Loading preview...</p>
              ) : (
                <>
                  {((customerPreviewModal.type === 'booking_summary' && notifStatusByBooking[customerPreviewModal.bookingId]?.confirmationSent) ||
                    (customerPreviewModal.type === 'driver_details' && notifStatusByBooking[customerPreviewModal.bookingId]?.driverDetailsSent)) && (
                    <p
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412',
                        fontSize: '0.8rem', fontWeight: 600, padding: '8px 12px', borderRadius: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      <i className="fa fa-exclamation-triangle" style={{ color: '#ea580c' }} />
                      This message has already been sent to the customer.
                    </p>
                  )}
                  <p className={s.waPreviewNote}>
                    This is what the customer will receive. Nothing is sent until you confirm.
                  </p>
                  <div className={s.waCanvas}>
                    <div className={s.waBubble}>
                      {customerPreviewModal.channel === 'template' && customerPreviewModal.photoUrl && (
                        <img
                          src={customerPreviewModal.photoUrl}
                          alt={customerPreviewModal.imageKind === 'vehicle' ? 'Vehicle photo' : 'Message header'}
                          className={s.waHeaderImage}
                        />
                      )}
                      <div className={s.waBody}>
                        {renderWhatsAppText(customerPreviewModal.message)}
                        <span className={s.waTime}>
                          {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    {customerPreviewModal.channel !== 'template' && customerPreviewModal.photoUrl && (
                      <div className={s.waBubble}>
                        <img
                          src={customerPreviewModal.photoUrl}
                          alt="Vehicle photo"
                          className={s.waHeaderImage}
                        />
                      </div>
                    )}
                  </div>
                  {customerPreviewModal.imageKind === 'logo' && (
                    <p className={s.waPreviewNote}>
                      No vehicle photo on file, so the UNLOADIN logo is sent in the header.
                    </p>
                  )}
                </>
              )}
            </div>
            <div className={s.modalFooter}>
              <button
                className={s.modalCloseBtn}
                onClick={handleCloseCustomerPreview}
                disabled={customerPreviewModal.sending}
              >
                Cancel
              </button>
              <button
                className={s.modalConfirmBtn}
                onClick={handleConfirmCustomerSend}
                disabled={customerPreviewModal.loading || customerPreviewModal.sending}
              >
                {customerPreviewModal.sending ? (
                  <><i className="fa fa-spinner fa-spin" /> Sending...</>
                ) : (
                  <><i className="fa fa-whatsapp" /> Send to customer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {bookingOutreachModal && (
        <BookingOutreachModal
          bookingId={bookingOutreachModal.bookingId}
          refreshToken={outreachRefreshToken}
          onClose={() => setBookingOutreachModal(null)}
          onLogCall={(vehicleId, driverName) => {
            setDriverOutreachModal({
              bookingId: bookingOutreachModal.bookingId,
              vehicleId,
              driverName,
            });
          }}
        />
      )}

      {driverOutreachModal && (
        <DriverOutreachModal
          bookingId={driverOutreachModal.bookingId}
          vehicleId={driverOutreachModal.vehicleId}
          driverName={driverOutreachModal.driverName}
          isDraft={Boolean(driverOutreachModal.isDraft)}
          onClose={() => setDriverOutreachModal(null)}
          onSuccess={handleOutreachLogged}
        />
      )}
      {previewPhotoUrl && (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'zoom-out'
            }}
            onClick={() => setPreviewPhotoUrl(null)}
        >
            <div 
                style={{ 
                    position: 'relative', 
                    maxWidth: '90%', 
                    maxHeight: '90%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <img 
                    src={previewPhotoUrl} 
                    alt="Vehicle Preview" 
                    style={{ 
                        maxWidth: '100%', 
                        maxHeight: '80vh', 
                        borderRadius: '8px', 
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
                        border: '4px solid #fff'
                    }} 
                />
                <button
                    style={{
                        position: 'absolute',
                        top: '-45px',
                        right: '-10px',
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: '#fff',
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s'
                    }}
                    onClick={() => setPreviewPhotoUrl(null)}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.4)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                >
                    &times;
                </button>
            </div>
        </div>
      )}
      {/* ─── Assign Transporter Modal ─────────────────────────────────────── */}
      {transporterAssignModal && (
        <div
          className={s.modalOverlay}
          style={{ zIndex: 10100 }}
          onClick={() => { setTransporterAssignModal(null); setSelectedTransporter(null); setTransporterSearch(''); }}
        >
          <div
            className={s.modalContent}
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={s.modalHeader}>
              <i className="fa fa-building" style={{ color: '#d97706', fontSize: '22px' }} />
              <h3 style={{ color: '#92400e' }}>Assign Transporter</h3>
            </div>

            <div className={s.modalBody}>
              {/* Booking info */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                <div>
                  <p className={s.modalLabel}>Booking ID</p>
                  <p className={s.modalValue}>
                    {transporterAssignModal?.id ?? transporterAssignModal?.booking_id}
                  </p>
                </div>
                {transporterAssignModal?.pickup_time && (
                  <div>
                    <p className={s.modalLabel}>Pickup</p>
                    <p className={s.modalValue}>
                      {new Date(transporterAssignModal.pickup_time).toLocaleString('en-IN', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>

              <p style={{ fontSize: '13px', color: '#78716c', marginBottom: '10px' }}>
                Select the transporter agency. The vehicle and driver can be assigned later once onboarded.
              </p>

              {/* Search */}
              <input
                type="text"
                placeholder="Search by agency name or contact..."
                value={transporterSearch}
                onChange={(e) => setTransporterSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '13px',
                  marginBottom: '8px',
                  boxSizing: 'border-box',
                  outline: 'none',
                }}
              />

              {/* Transporter list */}
              <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {transporterListLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af' }}>
                    <i className="fa fa-spinner fa-spin" /> Loading transporters...
                  </div>
                ) : transporterList
                    .filter((t) => {
                      const q = transporterSearch.toLowerCase();
                      return (
                        !q ||
                        (t.agency_name || '').toLowerCase().includes(q) ||
                        (t.contact_name || '').toLowerCase().includes(q) ||
                        (t.phone_number || '').includes(q)
                      );
                    })
                    .map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTransporter(t)}
                        style={{
                          padding: '10px 12px',
                          borderRadius: '8px',
                          border: `2px solid ${
                            selectedTransporter?.id === t.id ? '#f59e0b' : '#e5e7eb'
                          }`,
                          background: selectedTransporter?.id === t.id ? '#fffbeb' : '#fafafa',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background 0.15s',
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#1c1917' }}>
                          {t.agency_name || t.contact_name || `Transporter #${t.id}`}
                        </div>
                        {t.contact_name && t.agency_name && (
                          <div style={{ fontSize: '12px', color: '#78716c', marginTop: '2px' }}>
                            Contact: {t.contact_name}
                          </div>
                        )}
                        {t.phone_number && (
                          <div style={{ fontSize: '12px', color: '#78716c' }}>
                            <i className="fa fa-phone" style={{ marginRight: '4px' }} />
                            {t.phone_number}
                          </div>
                        )}
                      </div>
                    ))
                }
                {!transporterListLoading && transporterList.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '13px' }}>
                    No transporters found. Onboard one via the Dispatch → Transporters page.
                  </div>
                )}
              </div>

              {selectedTransporter && (
                <div style={{
                  marginTop: '10px',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  fontSize: '13px',
                  color: '#92400e',
                }}>
                  <i className="fa fa-check-circle" style={{ marginRight: '6px', color: '#d97706' }} />
                  Selected: <strong>{selectedTransporter.agency_name || selectedTransporter.contact_name}</strong>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={s.modalFooter}>
              <button
                type="button"
                className={s.modalCancelBtn}
                onClick={() => { setTransporterAssignModal(null); setSelectedTransporter(null); setTransporterSearch(''); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={s.modalConfirmBtn}
                style={{ background: '#d97706', borderColor: '#d97706' }}
                disabled={!selectedTransporter || assigningTransporter}
                onClick={handleAssignTransporter}
              >
                {assigningTransporter ? (
                  <><i className="fa fa-spinner fa-spin" /> Assigning...</>
                ) : (
                  <><i className="fa fa-building" /> Assign to {selectedTransporter?.agency_name || 'Transporter'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerDetailsModal
        isOpen={customerDetailsModalOpen}
        toggle={() => setCustomerDetailsModalOpen(false)}
        customerId={customerDetailsModalId}
        onViewAllCalls={(phone) => {
          setCustomerDetailsModalOpen(false);
          setCustomerDetailsModalId(null);
          const qs = phone ? `?view=call-logs&phone=${encodeURIComponent(phone)}` : '?view=call-logs';
          history.push(`/app/call-manager${qs}`);
        }}
      />
    </div>
  );
};

export default Bookings;
